import 'strophe.js'
import 'strophejs-plugin-ping'
import './adapter'
import CryptoJS from './hmac-sha256'

/*
========================================================================================

(c) Copyright 2017 Mercer Road Corp. All rights reserved.

Mercer Road Corp. hereby grants you ("User"), under its copyright rights, the right to
distribute, reproduce and/or prepare derivative works of this software source
(or binary) code  ("Software") solely to the extent expressly authorized in writing by Mercer Road Corp.
If you do not have a written license from Mercer Road Corp., you have no right to use the Software
except for internal testing and review.

No other rights are granted and no other use is authorized. The availability of the Software
does not provide any license by implication, estoppel, or otherwise under any patent rights
or other rights owned or controlled by Mercer Road or others covering any use of the Software herein.

USER AGREES THAT MERCER ROAD ASSUMES NO LIABILITY FOR ANY DAMAGES,
WHETHER DIRECT OR OTHERWISE, WHICH THE USER MAY SUFFER DUE TO
USER'S USE OF THE SOFTWARE. MERCER ROAD PROVIDES THE SOFTWARE “AS IS,”
MAKES NO EXPRESS OR IMPLIED REPRESENTATIONS OR WARRANTIES OF ANY TYPE,
AND EXPRESSLY DISCLAIMS THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR
A PARTICULAR PURPOSE, TITLE AND NON-INFRINGEMENT. USER ACKNOWLEDGES THAT
IT ASSUMES TOTAL RESPONSIBILITY AND RISK FOR USER'S USE OF THE SOFTWARE.

Except for any written authorization, license or agreement from or with Mercer Road Corp. the
foregoing terms and conditions shall constitute the entire agreement between User and
Mercer Road Corp. with respect to the subject matter hereof and shall not be modified or superseded
without the express written authorization of Mercer Road Corp.

Any copies or derivative works must include this and all other proprietary notices contained
on or in the Software as received by the User.


========================================================================================
*/

/**
 * @file Vivox Web SDK
 * @name Vivox Web SDK
 * @copyright (c) Copyright 2017 Mercer Road Corp. All rights reserved.
 */

'use strict';

// *****************************************************************************************************************************

/**
 * All Vivox Web SDK objects are inside this namespace.
 *
 * @namespace
 */
var Vivox5 = {
  // ===========================================================================================
  // Vivox5.Tools -- useful functions used everywhere in the library

  Tools: {
    // -------------------------------------------------------------------------

    getRandomInt: function(rangeMin = 0, rangeMax = Number.MAX_SAFE_INTEGER) {
      return Math.floor(Math.random() * (rangeMax - rangeMin + 1)) + rangeMin;
    },

    // -------------------------------------------------------------------------

    makeUuid: function () {
      function s4() { return Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1); }
      return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
    },

    // -------------------------------------------------------------------------

    createUniqueId: function () {
      // Creates 8 bytes hex string
      var result = Vivox5.Tools.getRandomInt(0, 0xFFFFFFFF).toString(16);
      while (result.length < 8) {
        result = '0' + result;
      }
      return result;
    },

    // -------------------------------------------------------------------------

    nextInternalId: 1,

    generateInternalId: function () {
      return this.nextInternalId++;
    },

    // -------------------------------------------------------------------------

    isValidRealmName: function (name) {
      if (!name || typeof name !== 'string') return false;
      return !!name.match(/^(([a-z0-9\-\_]+)\.)+([a-z]+)$/g);
    },

    // -------------------------------------------------------------------------

    validateRealmName: function (name, fromWhere, subsystemId) {
      if (typeof name !== 'string') {
        throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'realm', 'string', fromWhere, subsystemId);
      }

      if (!Vivox5.Tools.isValidRealmName(name)) {
        throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'realm', fromWhere, subsystemId);
      }
    },

    // -------------------------------------------------------------------------

    isValidIssuerName: function (name) {
      if (!name || typeof name !== 'string') return false;
      return !!name.match(/^[a-z0-9\-\_]+$/g);
    },

    // -------------------------------------------------------------------------

    validateIssuerName: function (name, fromWhere, subsystemId) {
      if (typeof name !== 'string') {
        throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'issuer', 'string', fromWhere, subsystemId);
      }

      if (!Vivox5.Tools.isValidIssuerName(name)) {
        throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'issuer', fromWhere, subsystemId);
      }
    },

    // -------------------------------------------------------------------------

    isValidUserName: function (name) {
      if (!name || typeof name !== 'string') return false;
      return !!name.match(/^[a-z0-9\-\_]+$/g);
    },

    // -------------------------------------------------------------------------

    validateUserName: function (name, fromWhere, subsystemId) {
      if (typeof name !== 'string') {
        throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'userName', 'string', fromWhere, subsystemId);
      }

      if (!Vivox5.Tools.isValidUserName(name)) {
        throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'userName', fromWhere, subsystemId);
      }
    },

    // -------------------------------------------------------------------------

    isValidChannelName: function (name) {
      if (!name || typeof name !== 'string') return false;
      return !!name.match(/^[a-z0-9\-]+$/g); // FIXME be more strict
    },

    // -------------------------------------------------------------------------

    validateChannelName: function (name, fromWhere, subsystemId) {
      if (typeof name !== 'string') {
        throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'channelName', 'string', fromWhere, subsystemId);
      }

      if (!Vivox5.Tools.isValidChannelName(name)) {
        throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'channelName', fromWhere, subsystemId);
      }
    },

    // -------------------------------------------------------------------------

    isValidChannelType: function (channelType) {
      return (channelType === Vivox5.ChannelSession.CHANTYPE.ECHO || channelType === Vivox5.ChannelSession.CHANTYPE.CONF);
    },

    // -------------------------------------------------------------------------

    validateChannelType: function (channelType, fromWhere, subsystemId) {
      if (typeof channelType !== typeof Vivox5.ChannelSession.CHANTYPE.CONF) {
        throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'channelType', typeof Vivox5.ChannelSession.CHANTYPE.CONF, fromWhere, subsystemId);
      }

      if (!Vivox5.Tools.isValidChannelType(channelType)) {
        throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'channelType', fromWhere, subsystemId);
      }
    },

    // -------------------------------------------------------------------------

    isInteger: function (n) {
      if (typeof (n) == 'number') {
        return n >= 0 && Math.trunc(n) === n;
      }
      else { // string or something convertable to string
        return /^\d+$/.test(n);
      }
    },

    // -------------------------------------------------------------------------

    isIntegerInRange: function (n, low, high) {
      return Vivox5.Tools.isInteger(n) && n >= low && n <= high;
    },

    // -------------------------------------------------------------------------

    isValidIpAddress: function (ip) {
      return /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(ip);
    },

    // -------------------------------------------------------------------------

    isValidWsUri: function (wsUri) {
      // NB: URI parameters (after '?') are not supported here
      var re = new RegExp(
        '^'                                // start of line
        + '(wss?)\:\/\/'                   // protocol
        + '('                              // host name or IP address
        + '((([a-z0-9\-\_]+)\.)+([a-z]+))' //   host name -- see isValidRealmName()
        + '|((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))' //   or IP address -- see isValidIpAddress()
        + ')'                              // end of host name or IP address
        + '(\:([0-9]+))?'                  // optional port
        + '(\/[a-zA-Z0-9\-\.\_\~\%\!\$\&\(\)\*\+\,\;\=]+)*/?' // path
        + '$' // end of line
      );

      return re.test(wsUri);
    },

    // -------------------------------------------------------------------------

    base64UrlEscape: function (s) {
      return s.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    },

    // -------------------------------------------------------------------------

    base64UrlEncode: function (buf) {
      var encoded = btoa(buf);
      encoded = Vivox5.Tools.base64UrlEscape(encoded);
      return encoded;
    },

    // -------------------------------------------------------------------------

    propCount: function (obj) {
      var count = 0;
      for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
          ++count;
        }
      }
      return count;
    },

    // -------------------------------------------------------------------------

    clearObject: function (obj) {
      for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
          delete obj[key];
        }
      }
    },

    findKeyByValue: function (obj, value) {
      for (let key in obj) {
        if (
          obj.hasOwnProperty(key) &&
          obj[key] == value
        ) {
          return key;
        }
      }
      return null;
    },

    // -------------------------------------------------------------------------

    callFunctionForArrayOrString: function (obj, func) {
      // obj can be string or array of strings or object with string values; return value is boolean
      if (Array.isArray(obj)) {
        var result = false;
        for (let n = 0; n < obj.length; ++n) {
          result = func(obj[n]) || result;
        }
        return result;
      }

      else if (typeof (obj) == 'object' && !(obj instanceof String)) {
        var result = false;
        for (let key in obj) {
          result = func(obj[key]) || result;
        }
        return result;
      }

      else if (typeof (obj) == 'string' || (obj instanceof String)) {
        return func(obj);
      }

      else {
        return false;
      }
    },

    // -------------------------------------------------------------------------

    callFunctionForSetOrObject: function (obj, type, func, errorReportParamName = undefined, errorReportParamType = undefined, errorReportFunctionName = undefined, errorReportSubsystemId = undefined) {
      // If obj is Set or Array or Map then call func for each value;
      // else call func once for obj as is;
      // return value is boolean: logical OR of each result.

      // Optionally, obj (or value of Set/Array/Map) is checked to be an instanceof type before calling func(), and returns false otherwise.

      if (Array.isArray(obj)) {
        var result = false;
        for (let n = 0; n < obj.length; ++n) {
          let value = obj[n];
          if (!type || (value instanceof type)) {
            result = !!func(value) || result;
          }
          else if (arguments.length >= 5) {
            throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, errorReportParamName, errorReportParamType, errorReportFunctionName, errorReportSubsystemId);
          }
        }
        return result;
      }

      else if (obj instanceof Set) {
        var result = false;
        for (let value of obj) {
          if (!type || (value instanceof type)) {
            result = !!func(value) || result;
          }
          else if (arguments.length >= 5) {
            throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, errorReportParamName, errorReportParamType, errorReportFunctionName, errorReportSubsystemId);
          }
        }
        return result;
      }

      else if (obj instanceof Map) {
        var result = false;
        for (let value of obj.values()) {
          if (!type || (value instanceof type)) {
            result = !!func(value) || result;
          }
          else if (arguments.length >= 5) {
            throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, errorReportParamName, errorReportParamType, errorReportFunctionName, errorReportSubsystemId);
          }
        }
        return result;
      }

      else {
        if (!type || (obj instanceof type)) {
          return func(obj);
        }
        else if (arguments.length >= 5) {
          throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, errorReportParamName, errorReportParamType, errorReportFunctionName, errorReportSubsystemId);
        }
        else {
          return false;
        }
      }
    },

    // -------------------------------------------------------------------------
  }, // end of Vivox5.Tools

  // ===========================================================================================
  // Vivox5.Xml -- XML helpers and wrappers

  Xml: {
    xmlParserLoggingDebug: false,
    xmlParserLoggingErrors: true,
    xmlParser: undefined,
    xmlSerializer: undefined,

    // -------------------------------------------------------------------------

    parseXml: function (s) {
      if (!Vivox5.Xml.xmlParser) {
        Vivox5.Xml.xmlParser = new DOMParser();
      }

      try {
        var xml = Vivox5.Xml.xmlParser.parseFromString(s, 'application/xml');
        if (xml.getElementsByTagName('parsererror').length > 0) {
          if (Vivox5.Xml.xmlParserLoggingErrors) {
            console.log('[ERRR] Error in XML parsing: ' + Vivox5.Xml.xmlToString(xml));
          }
          return null;
        }
        return xml;
      }

      catch (e) { // the catch block is only for IE (other browsers return null instead of throw)
        if (Vivox5.Xml.xmlParserLoggingErrors) {
          console.log('[ERRR] Error in XML parsing: ' + e);
        }
        return null;
      }
    },

    // -------------------------------------------------------------------------

    xmlToObj: function (xml) {
      var obj = undefined;

      if (xml.nodeType == 9) { // DOCUMENT_NODE
        if (Vivox5.Xml.xmlParserLoggingDebug) {
          console.log('ROOT node is normal XML Document', xml.nodeName);
        }
        obj = Vivox5.Xml.xmlNodeToObj(xml);
      }

      else if (xml.nodeType == 1) { // ELEMENT_NODE
        // create nested object with element name
        if (Vivox5.Xml.xmlParserLoggingDebug) {
          console.log('Processing ROOT node "%s"', xml.nodeName);
        }
        obj[xml.nodeName] = Vivox5.Xml.xmlNodeToObj(xml);
      }

      else { // may be text (other node types skipped inside)
        if (Vivox5.Xml.xmlParserLoggingDebug) {
          console.log('ROOT node is not XML element');
        }
        obj = Vivox5.Xml.xmlNodeToObj(xml);
      }

      return obj;
    },

    // -------------------------------------------------------------------------

    xmlNodeToObj: function (xml) {
      var obj = {};

      if (xml.nodeType == 1) { // ELEMENT_NODE
        if (Vivox5.Xml.xmlParserLoggingDebug) {
          console.log('This is element node "%s"', xml.nodeName);
        }
        // attributes
        if (xml.attributes.length > 0) {
          obj['_attrs'] = {};
          for (let n = 0; n < xml.attributes.length; n++) {
            var attr = xml.attributes.item(n);
            obj['_attrs'][attr.nodeName] = attr.nodeValue;
          }
        }
      }

      else if (xml.nodeType == 3) { // TEXT_NODE
        if (Vivox5.Xml.xmlParserLoggingDebug) {
          console.log('This is text node "%s"', xml.nodeValue);
        }
        obj = xml.nodeValue;
      }

      // No special processing for all other node types (comments, processing instructions, nested documents, etc. -- https://developer.mozilla.org/en/docs/Web/API/Node/nodeType)

      // child nodes (of DOCUMENT_NODE, ELEMENT_NODE, ...)
      if (xml.hasChildNodes()) {
        for (let n = 0; n < xml.childNodes.length; n++) {
          var nodeChild = xml.childNodes.item(n);
          var sNodeName = nodeChild.nodeName;
          if (Vivox5.Xml.xmlParserLoggingDebug) {
            console.log('Processing child node "%s"', sNodeName);
          }

          // special handling of child text nodes: take only text of first child, assign it here (instead of nested object), skip other childs
          if (n == 0 && nodeChild.nodeType == 3 && xml.childNodes.length == 1) {
            var attrs = obj['_attrs'];
            obj = new String(nodeChild.nodeValue);
            obj['_attrs'] = attrs;
            break;
          }

          if (typeof (obj[sNodeName]) == 'undefined') { // first child element with this name
            obj[sNodeName] = Vivox5.Xml.xmlNodeToObj(nodeChild);
          }

          else { // child element with this name already exists
            if (typeof (obj[sNodeName].push) == 'undefined') {
              // convert to array
              var nodeFirstChild = obj[sNodeName];
              obj[sNodeName] = [];
              obj[sNodeName].push(nodeFirstChild);
            }

            obj[sNodeName].push(Vivox5.Xml.xmlNodeToObj(nodeChild));
          }
        }
      }
      return obj;
    },

    // -------------------------------------------------------------------------

    objToXml: function (obj) {
      var xml = new Document();
      Vivox5.Xml.objToXmlNode(xml, xml, obj);
      return xml;
    },

    // -------------------------------------------------------------------------

    objToXmlNode: function (doc, parent, obj) {
      for (let sNodeName in obj) {
        if (sNodeName == '_attrs') {
          // attributes of parent node, skip this
          continue;
        }

        var node = doc.createElement(sNodeName);
        if (Vivox5.Xml.xmlParserLoggingDebug) {
          console.log('Created node ' + sNodeName);
        }
        parent.appendChild(node);

        if (obj[sNodeName]['_attrs'] !== undefined) {
          for (let sAttrName in obj[sNodeName]['_attrs']) {
            node.setAttribute(sAttrName, obj[sNodeName]['_attrs'][sAttrName]);
          }
        }

        if (
          (typeof (obj[sNodeName]) == 'string') ||
          (obj[sNodeName] instanceof String) ||
          (typeof (obj[sNodeName]) == 'number') ||
          (typeof (obj[sNodeName]) == 'boolean')
        ) {
          if (obj[sNodeName] !== '') { // FIXME: distinguish between empty string <element></element> and element w/o text node <element/>
            var nodeText = doc.createTextNode(obj[sNodeName]);
            node.appendChild(nodeText);
          }
        }

        else if (typeof (obj[sNodeName]) == 'object') {
          Vivox5.Xml.objToXmlNode(doc, node, obj[sNodeName]);
        }
      }
    },

    // -------------------------------------------------------------------------

    xmlToString: function (node) {
      if (Vivox5.Xml.xmlSerializer === undefined) {
        if (typeof (XMLSerializer) !== 'undefined') {
          Vivox5.Xml.xmlSerializer = new XMLSerializer();
        }
        else {
          Vivox5.Xml.xmlSerializer = null;
        }
      }

      if (Vivox5.Xml.xmlSerializer) {
        return Vivox5.Xml.xmlSerializer.serializeToString(node);
      }
      else if (node.xml) {
        return node.xml;
      }
      else {
        return null; // FIXME: may be throw an Error?
      }
    },

    // -------------------------------------------------------------------------
  }, // end of Vivox5.Xml

  // ===========================================================================================
  // Vivox5.Xmpp -- XMPP helper functions and constants

  Xmpp: {
    // Namespace constants
    NS: {
      URN_XMPP_BLOCKING: 'urn:xmpp:blocking',
      URN_VIVOX_PRESENCE: 'urn:vivox:presence',
      URN_VIVOX_MUC: 'urn:vivox:muc',
    },

    DEFAULT_TRANSACTION_TIMEOUT: 10000, // in ms

    // -------------------------------------------------------------------------

    isXmppError: function isXmppError(xmlElement) {
      var hasErrorAttr = ((xmlElement.hasAttribute('type')) && (xmlElement.getAttribute('type') == 'error'));
      if (hasErrorAttr) {
        return true;
      }

      var elsError = xmlElement.getElementsByTagName('error');
      var hasErrorChild = (elsError && elsError.length > 0);
      if (hasErrorChild) {
        return true;
      }

      return false;
    },
  }, // end of Vivox5.Xmpp

  // ===========================================================================================
  // Vivox5.Token -- functions for generating debug access tokens

  Token: {
    accessTokenSerial: 1,

    // -------------------------------------------------------------------------

    debugGenerateAccessToken: function (issuer, duration, cmd, subject, from, to, secret) {
      // and add the 'sip:' prefix if it is absent in from/to/subject (of course, it is absent!)
      if (from && from.substr(0, 4) != 'sip:') from = 'sip:' + from;
      if (to && to.substr(0, 4) != 'sip:') to = 'sip:' + to;
      if (subject && subject.substr(0, 4) != 'sip:') subject = 'sip:' + subject;

      var vx_var_rtp_encryption = 0; // TODO clarify this
      var expiration = Math.floor(Date.now() / 1000) + duration;
      var serial = Vivox5.Token.accessTokenSerial++;

      // The header is always empty.
      var header = '{}';
      //var header = '{"alg":"HS256"}'; //use this to validate with http://jwt.io/

      // Create payload from arguments.
      var payload = '';
      if (!subject && !to) {
        payload = '{"iss":"' + issuer + '","exp":' + expiration + ',"vxa":"' + cmd + '","vxi":' + serial + ',"rtpe":"' + vx_var_rtp_encryption + '","f":"' + from + '"}';
      }
      else if (!subject) {
        payload = '{"iss":"' + issuer + '","exp":' + expiration + ',"vxa":"' + cmd + '","vxi":' + serial + ',"rtpe":"' + vx_var_rtp_encryption + '","f":"' + from + '","t":"' + to + '"}';
      }
      else {
        payload = '{"iss":"' + issuer + '","exp":' + expiration + ',"vxa":"' + cmd + '","vxi":' + serial + ',"rtpe":"' + vx_var_rtp_encryption + '","sub":"' + subject + '","f":"' + from + '","t":"' + to + '"}';
      }

      // Base64 URL-safe encode the header and payload.
      var headerB64 = Vivox5.Tools.base64UrlEncode(header);
      var payloadB64 = Vivox5.Tools.base64UrlEncode(payload);

      // Use HMAC SHA-256 to hash header.payload with the secret as the key.
      // Uses open source library with New BSD license: https://code.google.com/archive/p/crypto-js/
      // And Base64 URL-safe encode the signature.
      var signMe = headerB64 + '.' + payloadB64
      var signature = CryptoJS.HmacSHA256(signMe, secret);
      // console.log(typeof signature);
      var signatureB64 = Vivox5.Tools.base64UrlEncode(signature.toString(CryptoJS.enc.Latin1));

      // Construct token and free provisional resources.
      var token =
        headerB64 +
        '.' +
        payloadB64 +
        '.' +
        signatureB64
        ;

      //console.log('header: ' + header + '\n');
      //console.log('payload: ' + payload + '\n');
      //console.log('headerB64: ' + headerB64 + '\n');
      //console.log('payloadB64: ' + payloadB64 + '\n');
      //console.log('signMe: ' + signMe + '\n');
      //console.log('signature: ' + signature + '\n');
      //console.log('signatureB64: ' + signatureB64 + '\n');
      //console.log('token: ' + token + '\n');

      return token;
    },

    // -------------------------------------------------------------------------
  }, // end of Vivox5.Token

  // ===========================================================================================
  // Misc

  loggerInfo: undefined,
  loggerTraf: undefined,

  log: function log(subsystem, msg) {
    if (Vivox5.loggerInfo) {
      var dt = new Date();
      //var dateString = dt.toISOString();
      //Vivox5.loggerInfo(dateString + ' ' + msg);
      Vivox5.loggerInfo(dt, subsystem, msg);
    }
  },

  // -------------------------------------------------------------------------

  logTraffic: function logTraffic(msg) {
    if (Vivox5.loggerTraf) {
      var dt = new Date();
      var dateString = dt.toISOString();
      Vivox5.loggerTraf(dateString + ' ' + msg);
      //Vivox5.loggerInfo(dt, 'TRAF', msg);
    }
  },

  // -------------------------------------------------------------------------

  logXmppError: function logXmppError(subsystem, xmlElement, prefix) {
    var logString = prefix ? prefix : '';
    var elsError = xmlElement.getElementsByTagName('error');
    if (elsError && elsError.length > 0) {
      var errorCode = elsError[0].getAttribute('code');
      var errorData = elsError[0].innerHTML;
      if (errorCode) {
        if (logString.length > 0) logString += ', ';
        logString += 'error code = ' + errorCode;
      }
      if (errorData) {
        if (logString.length > 0) logString += ', ';
        logString += 'error data = ' + errorData;
      }
    }
    Vivox5.log(subsystem, logString);
  },

  // -------------------------------------------------------------------------

  trace: function trace() {
    //var callerName = arguments.callee.caller.name; // does not work in strict mode

    /*
    FireFox stack example:
trace@file:///P:/Vivox/WSDemo/htdocs/vivox5webrtc.js:265:17
_Vivox5_Client_getRandomUserId@file:///P:/Vivox/WSDemo/htdocs/vivox5webrtc.js:325:5
@file:///P:/Vivox/WSDemo/htdocs/vivox5test.js:20:31

    Chrome/Opera stack example:
Error
    at Object.trace (vivox5webrtc.js:265)
    at Object._Vivox5_Client_getRandomUserId [as getRandomUserId] (vivox5webrtc.js:325)
    at vivox5test.js:20
    */

    var stack = undefined;

    if (Error.captureStackTrace) {
      // Chrome-specific
      let tmpObject = {};
      Error.captureStackTrace(tmpObject);
      if (tmpObject.stack) stack = tmpObject.stack;
    }

    if (!stack) {
      let err = new Error();
      if (err.stack) stack = err.stack;
    }

    if (stack) {
      let arrStack = stack.split('\n');
      let callerName = ((arrStack[0] == 'Error' || (arrStack[0].indexOf('@') === -1 && arrStack[0].indexOf('at') === -1)) ? arrStack[2] : arrStack[1]).trim();
      console.log('TRACE: ' + callerName);
    }
  },

  // -------------------------------------------------------------------------

  // ===========================================================================================
};

Object.defineProperties(Vivox5, {
  Tools: { writable: false, configurable: false, enumerable: true },
  Xml: { writable: false, configurable: false, enumerable: false },
  Xmpp: { writable: false, configurable: false, enumerable: false },
  Token: { writable: false, configurable: false, enumerable: false },
  log: { writable: false, configurable: false, enumerable: true },
  logTraffic: { writable: false, configurable: false, enumerable: false },
  logXmppError: { writable: false, configurable: false, enumerable: false },
  trace: { writable: false, configurable: false, enumerable: true },
});

// *****************************************************************************************************************************

/**
 * @class
 * @extends Error
 * @classdesc Used for reporting error conditions from the Vivox Web SDK to a web application. The application should handle the Vivox5.Error objects using the standard try/catch blocks.
 * @desc The constructor should not be called by the application.
 *
 * @param {Vivox5.Error.CODE} code - error code
 *
 * @property {Vivox5.Error.CODE} code - error code
 * @property {string} name - object name (always 'Vivox5.Error')
 * @property {string} message - a brief error message corresponding to the code
 * @property {string} extendedMessage - detailed error messsage in a human-readable form explaining all error properties listed below
 * @property {string} stack - call stack where the error has occured (format depends on user's web browser)
 * @property {string} [parameter] - name of an incorrect parameter (this property is added only for the following error codes: EPARAMTYPE, EPARAMVALUE, EINCONSISTPARAMS)
 * @property {string} [property] - name of an incorrect or missing property (this property is added only for the following error codes: EMISSINGPROP, EPROPTYPE, EPROPVALUE)
 * @property {string} [typeExpected] - expected type (this property is added only for the following error codes: EPARAMTYPE, EPROPTYPE)
 * @property {string} [stateDescription] - state description (only for ESTATE errors)
 * @property {string} [point] - object/function name which generated this error (in free form)
 * @property {string} [subsystemId] - subsystem ID where this error was thrown (used for logging)
 * @property {string} [notes] - optional notes related to this error (in free form)
 */
Vivox5.Error = function _Vivox5_Error(code) {
  Error.apply(this, arguments); // base class constructor

  // Check parameters
  if (typeof (code) !== 'number' || code < Vivox5.Error.CODE._MIN || code > Vivox5.Error.CODE._MAX) code = Vivox5.Error.CODE.EUNKNOWN;

  // Basic error properties
  this.name = 'Vivox5.Error';
  this.code = code;
  this.message = Vivox5.Error.codeToMessage(code);

  // Capture stack trace if possible
  if (Error.captureStackTrace) {
    // Chrome-specific
    Error.captureStackTrace(this, this.constructor);
  }
  else {
    // Firefox and standard browsers
    let tmp = new Error();
    if (tmp.stack) {
      this.stack = tmp.stack;
    }
  }

  // Extended error properties for specific error codes (parameter errors, property errors, type errors, etc.)
  var idxAfterDynamicArgs = 1;

  // parameter name
  if (
    code == Vivox5.Error.CODE.EPARAMTYPE ||
    code == Vivox5.Error.CODE.EPARAMVALUE ||
    code == Vivox5.Error.CODE.EINCONSISTPARAMS
  ) {
    ++idxAfterDynamicArgs;
    if (arguments.length > 1 && typeof (arguments[1] === 'string')) {
      this.parameter = arguments[1];
    }
  }

  // property name
  else if (
    code == Vivox5.Error.CODE.EMISSINGPROP ||
    code == Vivox5.Error.CODE.EPROPTYPE ||
    code == Vivox5.Error.CODE.EPROPVALUE
  ) {
    ++idxAfterDynamicArgs;
    if (arguments.length > 1 && typeof (arguments[1] === 'string')) {
      this.property = arguments[1];
    }
  }

  // expected parameter or property type
  if (
    code == Vivox5.Error.CODE.EPARAMTYPE ||
    code == Vivox5.Error.CODE.EPROPTYPE
  ) {
    ++idxAfterDynamicArgs;
    if (arguments.length > 2 && typeof (arguments[2] === 'string')) {
      this.typeExpected = arguments[2];
    }
  }

  // current state description for unexpected state errors
  if (code == Vivox5.Error.CODE.ESTATE) {
    ++idxAfterDynamicArgs;
    if (arguments.length > 1 && typeof (arguments[1] === 'string')) {
      this.stateDescription = arguments[1];
    }
  }

  // Three optional arguments after previous 'dynamic' arguments are:
  // - object/function name which generated this error (in free form)
  // - subsystem ID (used for logging)
  // - optional notes
  if (arguments.length > idxAfterDynamicArgs && typeof (arguments[idxAfterDynamicArgs] === 'string')) {
    this.point = arguments[idxAfterDynamicArgs];
  }

  ++idxAfterDynamicArgs;
  if (arguments.length > idxAfterDynamicArgs && typeof (arguments[idxAfterDynamicArgs] === 'string')) {
    this.subsystemId = arguments[idxAfterDynamicArgs];
  }

  ++idxAfterDynamicArgs;
  if (arguments.length > idxAfterDynamicArgs && typeof (arguments[idxAfterDynamicArgs] === 'string')) {
    this.notes = arguments[idxAfterDynamicArgs];
  }

  if ('subsystemId' in this && this.subsystemId && Vivox5.loggerInfo) {
    Vivox5.log(this.subsystemId, this.extendedMessage);
  }

  if (console && console.log) {
    console.log('Vivox5.Error dump', this);
  }

  return this;
};

Vivox5.Error.prototype = Object.create(Error.prototype);
Vivox5.Error.prototype.constructor = Vivox5.Error;

// -------------------------------------------------------------------------

Vivox5.Error.codeToMessage = function _Vivox5_Error_codeToMessage(code) {
  if (
    code < Vivox5.Error.CODE._MIN ||
    code > Vivox5.Error.CODE._MAX ||
    !Vivox5.Error.MESSAGE.hasOwnProperty(code)
  ) {
    return Vivox5.Error.MESSAGE[Vivox5.Error.CODE.EUNKNOWN];
  }

  return Vivox5.Error.MESSAGE[code];
};

// -------------------------------------------------------------------------

Object.defineProperty(Vivox5.Error.prototype, 'extendedMessage', {
  get: function _Vivox5_Error_extendedMessage_get() {
    // Compose extendedMessage property on-demand, cache in _extendedMessage
    if (!this.hasOwnProperty('_extendedMessage')) {
      this._extendedMessage = (this.hasOwnProperty('message') && this.message) ? this.message : '';

      if ('stateDescription' in this && this.stateDescription) {
        this._extendedMessage += (' (' + this.stateDescription + ')');
      }

      if ('point' in this && this.point) {
        this._extendedMessage += (' at ' + this.point);
      }

      if ('parameter' in this && this.parameter) {
        this._extendedMessage += (', parameter is ' + this.parameter);
      }

      if ('property' in this && this.property) {
        this._extendedMessage += (', property is ' + this.property);
      }

      if ('typeExpected' in this && this.typeExpected) {
        this._extendedMessage += (', ' + this.typeExpected + ' expected');
      }

      if ('notes' in this && this.notes) {
        this._extendedMessage += (' (' + this.notes + ')');
      }
    } // if !has _extendedMessage

    return this._extendedMessage;
  }
});

// -------------------------------------------------------------------------

/**
 * Converts a Vivox5.Error object to a human-readable string
 */
Vivox5.Error.prototype.toString = function _Vivox5_Error_toString() {
  var codeName = Vivox5.Tools.findKeyByValue(Vivox5.Error.CODE, this.code);
  return (
    this.name
    + (codeName ? (' [' + codeName + ']: ') : ': ')
    + this.extendedMessage
  );
};

// -------------------------------------------------------------------------

// ===========================================================================================

/**
 * Enum of error codes used in the Vivox5.Error#code property
 * @typedef
 * @property EUNKNOWN - unknown error (should not happen)
 * @property ENUMPARAMS - incorrect number of parameters
 * @property EPARAMTYPE - unexpected parameter type
 * @property EPARAMVALUE - invalid parameter value
 * @property EINCONSISTPARAMS - inconsistent parameters
 * @property EMISSINGPROP - required property not set
 * @property EPROPTYPE - unexpected property type
 * @property EPROPVALUE - invalid property value
 * @property ESTATE - call in unexpected state
 * @property ECONSTRAINS - internal constrains violation
 */
Vivox5.Error.CODE = Object.create(Object.prototype, {
  _MIN: { value: 0, writable: false, configurable: false, enumerable: false },
  EUNKNOWN: { value: 0, writable: false, configurable: false, enumerable: true },
  ENUMPARAMS: { value: 1, writable: false, configurable: false, enumerable: true },
  EPARAMTYPE: { value: 2, writable: false, configurable: false, enumerable: true },
  EPARAMVALUE: { value: 3, writable: false, configurable: false, enumerable: true },
  EINCONSISTPARAMS: { value: 4, writable: false, configurable: false, enumerable: true },
  EMISSINGPROP: { value: 5, writable: false, configurable: false, enumerable: true },
  EPROPTYPE: { value: 6, writable: false, configurable: false, enumerable: true },
  EPROPVALUE: { value: 7, writable: false, configurable: false, enumerable: true },
  ESTATE: { value: 8, writable: false, configurable: false, enumerable: true },
  ECONSTRAINS: { value: 9, writable: false, configurable: false, enumerable: true },
  _MAX: { value: 9, writable: false, configurable: false, enumerable: false },
});

Object.defineProperty(Vivox5.Error, 'CODE', { writable: false, configurable: false, enumerable: true });

// ===========================================================================================

Vivox5.Error.MESSAGE = Object.create(Object.prototype, {
  [Vivox5.Error.CODE.EUNKNOWN]: { value: 'unknown error', writable: false, configurable: false, enumerable: true },
  [Vivox5.Error.CODE.ENUMPARAMS]: { value: 'incorrect number of parameters', writable: false, configurable: false, enumerable: true },
  [Vivox5.Error.CODE.EPARAMTYPE]: { value: 'unexpected parameter type', writable: false, configurable: false, enumerable: true },
  [Vivox5.Error.CODE.EPARAMVALUE]: { value: 'invalid parameter value', writable: false, configurable: false, enumerable: true },
  [Vivox5.Error.CODE.EINCONSISTPARAMS]: { value: 'inconsistent parameters', writable: false, configurable: false, enumerable: true },
  [Vivox5.Error.CODE.EMISSINGPROP]: { value: 'required property not set', writable: false, configurable: false, enumerable: true },
  [Vivox5.Error.CODE.EPROPTYPE]: { value: 'unexpected property type', writable: false, configurable: false, enumerable: true },
  [Vivox5.Error.CODE.EPROPVALUE]: { value: 'invalid property value', writable: false, configurable: false, enumerable: true },
  [Vivox5.Error.CODE.ESTATE]: { value: 'call in unexpected state', writable: false, configurable: false, enumerable: true },
  [Vivox5.Error.CODE.ECONSTRAINS]: { value: 'internal constrains violation', writable: false, configurable: false, enumerable: true },
});

Object.defineProperty(Vivox5.Error, 'MESSAGE', { writable: false, configurable: false, enumerable: true });

// ===========================================================================================

Object.defineProperty(Vivox5, 'Error', { writable: false, configurable: false, enumerable: true });

// *****************************************************************************************************************************

/**
 * The unique identitier for a player that accesses a Vivox instance
 *
 * @class
 * @param {string} issuer The issuer that is responsible for authenticating this account
 * @param {string} name The Player's Name
 * @param {string} domain The Vivox realm that provides service for this account
 *
 * @property {string} issuer The issuer that is responsible for authenticating this account
 * @property {string} name The Player's Name
 * @property {string} domain The Vivox realm that provides service for this account
 */
Vivox5.AccountId = function _Vivox5_AccountId() {
  Vivox5.trace();

  if (arguments.length == 3) {
    // 3 arguments: string issuer, string name, string domain
    Vivox5.Tools.validateIssuerName(arguments[0], 'Vivox5.AccountId()', 'CORE'); // throws Vivox5.Error(EPARAMTYPE or EPARAMVALUE) on failure, does nothing on success
    Vivox5.Tools.validateUserName(arguments[1], 'Vivox5.AccountId()', 'CORE');
    Vivox5.Tools.validateRealmName(arguments[2], 'Vivox5.AccountId()', 'CORE');

    this._isComplete = true;
    this.issuer = arguments[0];
    this.name = arguments[1];
    this.domain = arguments[2];
    this.resource = null;
  }

  else if (arguments.length == 1) {
    if (typeof (arguments[0]) == 'string' || (arguments[0] instanceof String)) {
      // parse from string: '.issuer.name.@doma.in'
      var mtch = arguments[0].match(/^\.([a-z0-9\-\_]+)\.([a-z0-9\-\_]+)\.@(([a-z0-9\-\_]+\.)+([a-z]+))(\/([a-zA-Z0-9\-\_@]+))?$/);
      if (mtch && mtch.length >= 4) {
        this._isComplete = true;
        this.issuer = mtch[1];
        this.name = mtch[2];
        this.domain = mtch[3];
        this.resource = mtch[7] ? mtch[7] : null;
      }
      else {
        // try to parse as a valid JID, but without '.issuer.name.' syntax
        mtch = arguments[0].match(/^([a-zA-Z0-9\-\_\.]+)@(([a-z0-9\-\_]+\.)+([a-z]+))(\/([a-zA-Z0-9\-\_@]+))?$/);
        if (mtch && mtch.length >= 3) {
          this._isComplete = false;
          this._originalUri = arguments[0];
          this.issuer = null;
          this.name = mtch[1];
          this.domain = mtch[2];
          this.resource = mtch[6] ? mtch[6] : null;
        }
        else {
          // Invalid JID
          throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'jid', 'Vivox5.AccountId()', 'CORE');
        }
      }
    }

    else if (arguments[0] instanceof Vivox5.AccountId) {
      // copy existing AccountId
      this._isComplete = arguments[0]._isComplete;
      this.issuer = arguments[0].issuer;
      this.name = arguments[0].name;
      this.domain = arguments[0].domain;
      if ('resource' in arguments[0]) this.resource = arguments[0].resource;
      if ('_originalUri' in arguments[0]) this._originalUri = arguments[0]._originalUri;
    }

    else {
      // unexpected argument type
      throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'accountId', 'string or Vivox5.AccountId', 'Vivox5.AccountId()', 'CORE');
    }
  }

  else {
    // incorrect number of arguments passed to the constructor
    throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, 'Vivox5.AccountId()', 'CORE');
  }

  // -------------------------------------------------------------------------

  Object.defineProperties(this, {
    'issuer': { writable: false, configurable: false, enumerable: true },
    'name': { writable: false, configurable: false, enumerable: true },
    'domain': { writable: false, configurable: false, enumerable: true },
    'resource': { writable: false, configurable: false, enumerable: true },
    '_isComplete': { writable: false, configurable: false, enumerable: false }, // internal
    '_originalUri': { writable: false, configurable: false, enumerable: false }, // internal
  });
};

// ===========================================================================================

/**
 * The string representation of the account id
 *
 * @returns {string} A URI for this account
 */
Vivox5.AccountId.prototype.toString = function _Vivox5_AccountId_toString() {
  return (
    this._isComplete
      ? ('.' + this.issuer + '.' + this.name + '.@' + this.domain + (('resource' in this && this.resource) ? '/' + this.resource : ''))
      : this._originalUri
  );
};

// -------------------------------------------------------------------------

/**
 * A primitive value (string) representation of the account id
 *
 * @returns {string} A URI for this account
 * @see The same as {@link Vivox5.AccountId#toString}
 */
Vivox5.AccountId.prototype.valueOf = function _Vivox5_AccountId_valueOf() {
  // Same as toString()
  return (
    this._isComplete
      ? ('.' + this.issuer + '.' + this.name + '.@' + this.domain + (('resource' in this && this.resource) ? '/' + this.resource : ''))
      : this._originalUri
  );
};

// -------------------------------------------------------------------------

Object.defineProperty(Vivox5.AccountId.prototype, 'accountName', {
  get: function _Vivox5_AccountId_accountName_get() {
    if (this._isComplete) {
      if (this.issuer && this.name) {
        return '.' + this.issuer + '.' + this.name + '.';
      }
      else {
        return '';
      }
    }
    else {
      if (this.name) {
        return this.name;
      }
      else {
        return '';
      }
    }
  },
});

// -------------------------------------------------------------------------

/**
 * Determines if two objects are equal
 *
 * @param {Vivox5.AccountId} other the other object
 * @returns {boolean} true of the objects are of equal value
 */
Vivox5.AccountId.prototype.equals = function _Vivox5_AccountId_equals(other) {
  return (
    (other instanceof Vivox5.AccountId) &&
    (
      (
        this._isComplete === true &&
        this._isComplete === other._isComplete &&
        this.issuer === other.issuer &&
        this.name === other.name &&
        this.domain === other.domain
      )
      ||
      (
        this._isComplete === false &&
        this._isComplete === other._isComplete &&
        this._originalUri === other._originalUri
      )
    )
  );
};

// -------------------------------------------------------------------------

/**
 * Test for an empty AccountId
 *
 * @returns {boolean} true if the name, domain, and issuer are empty
 */
Vivox5.AccountId.prototype.isEmpty = function _Vivox5_AccountId_isEmpty() {
  return (
    (this._isComplete && (!this.issuer || !this.name || !this.domain)) ||
    (!this._isComplete && !this._originalUri)
  );
};

// ===========================================================================================

Object.defineProperty(Vivox5, 'AccountId', { writable: false, configurable: false, enumerable: true });

// *****************************************************************************************************************************

/**
 * The unique identifier for a channel. Channels are created and destroyed automatically on demand.
 *
 * @class
 * @param {string} issuer The issuer that is responsible for authorizing access to this channel
 * @param {string} name The name of the channel
 * @param {string} domain The Vivox domain that hosts this channel
 * @param {Vivox5.ChannelSession.CHANTYPE} type The channel type
 *
 * @property {string} issuer The issuer that is responsible for authorizing access to this channel
 * @property {string} name The name of the channel
 * @property {string} domain The Vivox domain that hosts this channel
 * @property {Vivox5.ChannelSession.CHANTYPE} type The channel type
 */
Vivox5.ChannelId = function _Vivox5_ChannelId() {
  Vivox5.trace();

  if (arguments.length == 4 || arguments.length == 3) {
    // 4 arguments: string issuer, string name, string domain, Vivox5.ChannelSession.CHANTYPE type (default CONF)
    Vivox5.Tools.validateIssuerName(arguments[0], 'Vivox5.ChannelId()', 'CORE'); // throws Vivox5.Error(EPARAMTYPE or EPARAMVALUE) on failure, does nothing on success
    Vivox5.Tools.validateChannelName(arguments[1], 'Vivox5.ChannelId()', 'CORE');
    Vivox5.Tools.validateRealmName(arguments[2], 'Vivox5.ChannelId()', 'CORE');
    if (arguments.length > 3) {
      Vivox5.Tools.validateChannelType(arguments[3], 'Vivox5.ChannelId()', 'CORE');
    }

    this.issuer = arguments[0];
    this.name = arguments[1];
    this.domain = arguments[2];
    this.type = (arguments.length > 3) ? arguments[3] : Vivox5.ChannelSession.CHANTYPE.CONF;
  }

  else if (arguments.length == 1) {
    if (typeof (arguments[0]) == 'string' || (arguments[0] instanceof String)) {
      // parse from string: 'confctl-type-issuer.name@doma.in'
      var mtch = arguments[0].match(/^confctl-(e|g)-([a-z0-9\-\_]+)\.([a-z0-9\-\_]+)@(([a-z0-9\-\_]+\.)+([a-z]+))$/);
      if (mtch && mtch.length >= 5) {
        this.type = mtch[1] == 'g' ? Vivox5.ChannelSession.CHANTYPE.CONF : Vivox5.ChannelSession.CHANTYPE.ECHO;
        this.issuer = mtch[2];
        this.name = mtch[3];
        this.domain = mtch[4];
      }
      else {
        // Invalid JID or unexpected channel URI format
        throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'jid', 'Vivox5.ChannelId()', 'CORE');
      }
    }

    else if (arguments[0] instanceof Vivox5.ChannelId) {
      // copy existing ChannelId
      this.issuer = arguments[0].issuer;
      this.name = arguments[0].name;
      this.domain = arguments[0].domain;
      this.type = arguments[0].type;
    }

    else {
      // unexpected argument type
      throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'channelId', 'string or Vivox5.ChannelId', 'Vivox5.ChannelId()', 'CORE');
    }
  }

  else {
    // incorrect number of arguments passed to the constructor
    throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, 'Vivox5.ChannelId()', 'CORE');
  }

  // -------------------------------------------------------------------------

  Object.defineProperties(this, {
    'issuer': { writable: false, configurable: false, enumerable: true },
    'name': { writable: false, configurable: false, enumerable: true },
    'domain': { writable: false, configurable: false, enumerable: true },
    'type': { writable: false, configurable: false, enumerable: true },
  });
};

// ===========================================================================================

/**
 * The string representation of the channel id
 *
 * @returns {string} A URI for this channel
 */
Vivox5.ChannelId.prototype.toString = function _Vivox5_ChannelId_toString() {
  return 'confctl-' + this.type + '-' + this.issuer + '.' + this.name + '@' + this.domain;
};

// -------------------------------------------------------------------------

/**
 * A primitive value (string) representation of the channel id
 *
 * @returns {string} A URI for this account
 * @see The same as {@link Vivox5.ChannelId#toString}
 */
Vivox5.ChannelId.prototype.valueOf = function _Vivox5_ChannelId_valueOf() {
  return 'confctl-' + this.type + '-' + this.issuer + '.' + this.name + '@' + this.domain;
};

// -------------------------------------------------------------------------

Object.defineProperty(Vivox5.ChannelId.prototype, 'channelName', {
  get: function _Vivox5_ChannelId_channelName_get() {
    if (this.issuer || this.name) {
      return 'confctl-' + this.type + '-' + this.issuer + '.' + this.name;
    }
    else {
      return '';
    }
  },
});

// -------------------------------------------------------------------------

/**
 * Determines if two objects are equal
 *
 * @param {Vivox5.ChannelId} other the other object
 * @returns {boolean} true of the objects are of equal value
 */
Vivox5.ChannelId.prototype.equals = function _Vivox5_ChannelId_equals(other) {
  return (
    (other instanceof Vivox5.ChannelId) &&
    this.issuer === other.issuer &&
    this.name === other.name &&
    this.domain === other.domain &&
    this.type === other.type
  );
};

// -------------------------------------------------------------------------

/**
 * Test for an empty ChannelId
 *
 * @returns {boolean} true if the name, domain, and issuer are empty
 */
Vivox5.ChannelId.prototype.isEmpty = function _Vivox5_ChannelId_isEmpty() {
  return !this.issuer && !this.name && !this.domain;
};

// ===========================================================================================

Object.defineProperty(Vivox5, 'ChannelId', { writable: false, configurable: false, enumerable: true });

// *****************************************************************************************************************************

/**
 * @class
 * @classdesc A participant in a [channel]{@link Vivox5.ChannelSession} and its properties. Note that the key for this interface is not the account id. It's a unique identifier of that participant's session in that channel.
 * @desc The constructor must not be called directly by the application. The application can get objects of this type from a {@link Vivox5.ChannelSession} ({@link Vivox5.ChannelSession#onParticipantAdded}, {@link Vivox5.ChannelSession#onParticipantUpdated}, {@link Vivox5.ChannelSession#onParticipantRemoved} callbacks).
 *
 * @property {Vivox5.ChannelSession} parentChannelSession - The ChannelSession that owns this Participant
 * @property {string} participantId - The unique identifier for this participant (this is not the same as account!)
 * @property {Vivox5.AccountId} account - The account id of this particiapnt
 * @property {boolean} isSelf - true if this participant corresponds to the currently connected user
 * @property {boolean} inAudio - if true, the user is in audio
 * @property {boolean} inText - if true, the user is in text
 * @property {Vivox5.ChannelSession.OPERSTATE} localMute - indicates whether this participant is muted for the current user (ON) or not (OFF), or the operation is in progress (ENABLING, DISABLING)
 */
Vivox5.Participant = function _Vivox5_Participant(parentChannelSession, participantId, account, isSelf) {
  Vivox5.trace();

  // Check parameters
  if (arguments.length != 4) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, 'Vivox5.Participant()', 'CORE');
  if (!(parentChannelSession instanceof Vivox5.ChannelSession)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'parentChannelSession', 'Vivox5.ChannelSession', 'Vivox5.Participant()', 'CORE');
  if (typeof (participantId) !== 'string') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'participantId', 'string', 'Vivox5.Participant()', 'CORE');
  if (!(account instanceof Vivox5.AccountId)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'account', 'Vivox5.AccountId', 'Vivox5.Participant()', 'CORE');
  if (typeof (isSelf) !== 'boolean') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'isSelf', 'boolean', 'Vivox5.Participant()', 'CORE');

  // Mandatory properties
  this.parentChannelSession = parentChannelSession;
  this.participantId = participantId;
  this.account = account;
  this.isSelf = isSelf;

  // Optional properties
  this.roomNickname = undefined; // internal
  this.affiliation = undefined; // internal
  this.role = undefined; // internal
  this.inAudio = undefined;
  this.inText = undefined;
  this.localMute = undefined;

  // Protect mandatory object properties from modification and removal
  Object.defineProperties(this, {
    parentChannelSession: { writable: false, configurable: false, enumerable: true },
    participantId: { writable: false, configurable: false, enumerable: true },
    account: { writable: false, configurable: false, enumerable: true },
    isSelf: { writable: false, configurable: false, enumerable: true },
  });
};

// ===========================================================================================

Vivox5.Participant.prototype.toString = function _Vivox5_Participant_toString() {
  if (this.hasOwnProperty('account') && this.account instanceof Vivox5.AccountId) {
    return this.account.toString();
  }
  else {
    return '[object Vivox5.Participant(unidentified)]';
  }
};

// ===========================================================================================

Object.defineProperty(Vivox5, 'Participant', { writable: false, configurable: false, enumerable: true });

// *****************************************************************************************************************************

/**
 * @class
 * @classdesc Provides access to the Vivox System. This object is static: the application must not create its instances.
 * @hideconstructor
 */
Vivox5.Client = {
  // !!! Important: when adding, modifying, removing properties of Vivox5.Client,
  // don't forget to update the Object.defineProperties table located after this object declaration.

  /**
   * The internal version of the Vivox Web SDK.
   * @type {string}
   * @readonly
   */
  internalVersion: '0.0.0',

  isInitialized: false,

  /**
   * All the Login Sessions associated with this Client instance.
   * @type {Object<string,Vivox5.LoginSession>}
   * @readonly
   * @see The application should not work directly with this object, use the {@link Vivox5.Client.getLoginSession} method instead.
   */
  loginSessions: {}, // map AccountId.toString() -> Vivox5.LoginSession

  logSubsystemId: 'CLIENT',

  // -------------------------------------------------------------------------

  /**
   * Initializes the Vivox Web Client. If the Client is already initialized, it will do nothing.
   * @returns {boolean} true on success, false otherwise (e.g. if already initialized)
   */
  initialize: function _Vivox5_Client_initialize() {
    Vivox5.trace();

    // Check parameters
    if (arguments.length != 0) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, 'Vivox5.Client.initialize()', this.logSubsystemId);

    if (this.isInitialized) {
      // Already initialized
      return false;
    }

    // nothing to do now (may be changed in the future)
    this.isInitialized = true;

    return true;
  },

  // -------------------------------------------------------------------------

  /**
   * Uninitialize the Vivox Web Client. If the Client is not initialized, it will do nothing.
   * @returns {boolean} true on success, false otherwise (e.g. if not initialized)
   */
  uninitialize: function _Vivox5_Client_uninitialize() {
    Vivox5.trace();

    // Check parameters
    if (arguments.length != 0) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, 'Vivox5.Client.uninitialize()', this.logSubsystemId);

    if (!this.isInitialized) {
      // Not initialized or already uninitialized
      return false;
    }

    // Uninitialize all loginSessions (and loginSessions must uninitialize all channelSessions)
    for (let idx in this.loginSessions) {
      var loginSession = this.loginSessions[idx];
      delete this.loginSessions[idx];
      loginSession.logout();
      loginSession = undefined;
      // FIXME: maybe we need to wait asynchronously here?
    }

    this.isInitialized = false;

    return true;
  },

  // -------------------------------------------------------------------------

  /**
   * Generates a random user name with an optional prefix
   *
   * @param {string} [prefix='wrtc_'] - the prefix that will be prepended to the generated user name
   *
   * @returns {string} random user name
   *
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} prefix is not a string: Vivox5.Error.CODE.EPARAMTYPE
   *
   * @see {@link Vivox5.Client.getRandomAccountId}
   */
  getRandomUserName: function _Vivox5_Client_getRandomUserName(prefix = 'wrtc_') {
    Vivox5.trace();

    // Check parameters
    if (arguments.length > 1) {
      throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, 'Vivox5.Client.getRandomUserName()', this.logSubsystemId);
    }

    if (typeof prefix !== 'string' && prefix !== undefined) {
      throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'prefix', 'string', 'Vivox5.Client.getRandomUserName()', this.logSubsystemId);
    }

    if (!Vivox5.Tools.isValidUserName(prefix)) {
      prefix = '';
    }

    var result = prefix + Vivox5.Tools.createUniqueId();
    return result;
  },

  // -------------------------------------------------------------------------

  /**
   * Generates a random account id object with given issuer and realm values and a random user name
   *
   * @param {string} issuer - the issuer that is responsible for authenticating this account
   * @param {string} realm - the Vivox realm that provides service for this account
   * @param {string} [prefix='wrtc_'] - the prefix that will be prepended to the generated user name
   *
   * @returns {Vivox5.AccountId} random account id
   *
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} issuer or realm or prefix is not a string: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} issuer contains invalid characters: Vivox5.Error.CODE.EPARAMVALUE
   * @throws {Vivox5.Error} realm contains invalid characters: Vivox5.Error.CODE.EPARAMVALUE
   *
   * @see {@link Vivox5.Client.getRandomUserName}
   */
  getRandomAccountId: function _Vivox5_Client_getRandomAccountId(issuer, realm, prefix = 'wrtc_') {
    Vivox5.trace();

    if (arguments.length < 2 || arguments.length > 3) {
      throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, 'Vivox5.Client.getRandomAccountId()', this.logSubsystemId);
    }

    Vivox5.Tools.validateIssuerName(issuer, 'Vivox5.Client.getRandomAccountId()', this.logSubsystemId); // throws Vivox5.Error(EPARAMTYPE or EPARAMVALUE) on failure, does nothing on success
    Vivox5.Tools.validateRealmName(realm, 'Vivox5.Client.getRandomAccountId()', this.logSubsystemId);

    var userName = this.getRandomUserName(prefix);
    return new Vivox5.AccountId(issuer, userName, realm);
  },

  // -------------------------------------------------------------------------

  /**
   * Generates a random channel name
   *
   * @returns {string} random channel name
   *
   * @throws {Vivox5.Error} called with unexpected parameters: Vivox5.Error.CODE.ENUMPARAMS
   *
   * @see {@link Vivox5.Client.getRandomChannelId}
   */
  getRandomChannelName: function _Vivox5_Client_getRandomChannelName() {
    // Generate just a name without prefix and type (type moved to Vivox5.ChannelId)
    Vivox5.trace();

    // Check parameters
    if (arguments.length != 0) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, 'Vivox5.Client.getRandomChannelName()', this.logSubsystemId);

    return Vivox5.Tools.createUniqueId();
  },

  // -------------------------------------------------------------------------

  /**
   * Generates a random channel id object of the type specified with given issuer and realm values and a random channel name
   *
   * @param {string} issuer The issuer that is responsible for authorizing access to this channel
   * @param {string} realm The Vivox domain that hosts this channel
   * @param {Vivox5.ChannelSession.CHANTYPE} channelType - type of the channel (conference or echo)
   *
   * @returns {Vivox5.AccountId} random account id
   *
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} issuer or realm is not a string: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} issuer or realm contain invalid characters: Vivox5.Error.CODE.EPARAMVALUE
   * @throws {Vivox5.Error} channelType is not a type described in Vivox5.ChannelSession.CHANTYPE: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} channelType is not one of values described in Vivox5.ChannelSession.CHANTYPE: Vivox5.Error.CODE.EPARAMVALUE
   *
   * @see {@link Vivox5.Client.getRandomChannelName}
   */
  getRandomChannelId: function _Vivox5_Client_getRandomChannelId(issuer, realm, channelType) {
    Vivox5.trace();

    if (arguments.length != 3) {
      throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, 'Vivox5.Client.getRandomChannelId()', this.logSubsystemId);
    }

    Vivox5.Tools.validateIssuerName(issuer, 'Vivox5.Client.getRandomChannelId()', this.logSubsystemId); // throws Vivox5.Error(EPARAMTYPE or EPARAMVALUE) on failure, does nothing on success
    Vivox5.Tools.validateRealmName(realm, 'Vivox5.Client.getRandomChannelId()', this.logSubsystemId);
    Vivox5.Tools.validateChannelType(channelType, 'Vivox5.Client.getRandomChannelId()', this.logSubsystemId);

    var chanName = this.getRandomChannelName();
    return new Vivox5.ChannelId(issuer, chanName, realm, channelType);
  },

  // -------------------------------------------------------------------------

  /**
   * Gets the LoginSession object for the provided account id, creating one if necessary.
   *
   * @param {Vivox5.AccountId} loginSessionId - the account id
   * @param {boolean} [forceSecurePreloginURI=true] - select insecure (false) or secure (true) protocol for the prelogin procedure (it's NOT RECOMMENDED to use this parameter in the web applications!)
   * @param {string} [uriWsXmpp] - use the specified URI of the Vivox signalling server instead of the one configured in the prelogin procedure (it's NOT RECOMMENDED to use this parameter in the web applications!)
   *
   * @returns {Vivox5.LoginSession} the Login Session for that account id
   *
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} loginSessionId is not a Vivox5.AccountId: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} loginSessionId is empty: Vivox5.Error.CODE.EPARAMVALUE
   * @throws {Vivox5.Error} forceSecurePreloginURI is not boolean: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} uriWsXmpp is not a string: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} uriWsXmpp is not a valid server URI: Vivox5.Error.CODE.EPARAMVALUE
   *
   * @see {@link Vivox5.Client.deleteLoginSession}
   */
  getLoginSession: function _Vivox5_Client_getLoginSession(loginSessionId, forceSecurePreloginURI = true, uriWsXmpp = undefined) {
    Vivox5.trace();

    // Check state
    if (!this.isInitialized) {
      throw new Vivox5.Error(Vivox5.Error.CODE.ESTATE, 'not initialized', 'Vivox5.Client.getLoginSession()', this.logSubsystemId);
    }

    // Check parameters
    if (arguments.length < 1 || arguments.length > 3) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, 'Vivox5.Client.getLoginSession()', this.logSubsystemId);
    if (!(loginSessionId instanceof Vivox5.AccountId)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'loginSessionId', 'Vivox5.AccountId', 'Vivox5.Client.getLoginSession()', this.logSubsystemId);
    if (loginSessionId.isEmpty()) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'loginSessionId', 'Vivox5.Client.getLoginSession()', this.logSubsystemId);
    if (typeof forceSecurePreloginURI !== 'boolean') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'forceSecurePreloginURI', 'boolean', 'Vivox5.Client.getLoginSession()', this.logSubsystemId);
    if (uriWsXmpp !== undefined && typeof uriWsXmpp !== 'string') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'uriWsXmpp', 'string or undefined', 'Vivox5.Client.getLoginSession()', this.logSubsystemId);
    if (uriWsXmpp !== undefined && !Vivox5.Tools.isValidWsUri(uriWsXmpp)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'uriWsXmpp', 'Vivox5.Client.getLoginSession()', this.logSubsystemId);

    var accountJid = loginSessionId.toString(); // user@realm

    if (accountJid in this.loginSessions) {
      return this.loginSessions[accountJid];
    }

    var loginSession = new Vivox5.LoginSession(loginSessionId, forceSecurePreloginURI, uriWsXmpp);
    this.loginSessions[accountJid] = loginSession;

    return loginSession;
  },

  // -------------------------------------------------------------------------

  /**
   * Deletes the Login Session for this account id, logging out the session if necessary.
   * Note: this function completes synchronously and does not call any callbacks even if anynchronous logout procedure is started.
   * If you want to get the notification when the Login Session is actually logged out, you must call the {@link Vivox5.LoginSession#logout} method and wait for the {@link Vivox5.LoginSession#onDisconnected} callback.
   *
   * @param {(Vivox5.AccountId|Vivox5.LoginSession)} loginSessionOrId - the account id of the Login Session (or the whole Vivox5.LoginSession object may be passed here)
   *
   * @returns {boolean} true on success, false if the Login Session does not exist
   *
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} loginSessionOrId is neither Vivox5.AccountId nor Vivox5.LoginSession: Vivox5.Error.CODE.EPARAMTYPE
   *
   * @see {@link Vivox5.Client.getLoginSession}
   * @see {@link Vivox5.LoginSession#logout}
   */
  deleteLoginSession: function _Vivox5_Client_deleteLoginSession(loginSessionOrId) {
    Vivox5.trace();

    // Check parameters
    if (arguments.length != 1) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, 'Vivox5.Client.deleteLoginSession()', this.logSubsystemId);

    if (
      !(loginSessionOrId instanceof Vivox5.LoginSession) &&
      !(loginSessionOrId instanceof Vivox5.AccountId)
    ) {
      // invalid parameter
      throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'loginSessionOrId', 'Vivox5.LoginSession or Vivox5.AccountId', 'Vivox5.Client.deleteLoginSession()', this.logSubsystemId);
    }

    var accountJid = (loginSessionOrId instanceof Vivox5.AccountId) ? loginSessionOrId.toString() : ('loginSessionId' in loginSessionOrId ? loginSessionOrId.loginSessionId.toString() : '');
    if (!accountJid || !(accountJid in this.loginSessions)) {
      // unknown session
      return false;
    }

    var loginSession = this.loginSessions[accountJid];
    delete this.loginSessions[accountJid];

    if (
      loginSession.state == Vivox5.LoginSession.LOGINSTATE.PRELOGIN ||
      loginSession.state == Vivox5.LoginSession.LOGINSTATE.LOGGING_IN ||
      loginSession.state == Vivox5.LoginSession.LOGINSTATE.LOGGED_IN
    ) {
      loginSession.logout(); // will get into onLoginSessionDisconnectedInternal() after disconnect, close() will be called there
    }
    else if (
      loginSession.state == Vivox5.LoginSession.LOGINSTATE.LOGGED_OUT ||
      loginSession.state == Vivox5.LoginSession.LOGINSTATE.FINISHED
    ) {
      loginSession.close();
    }
    // else if LOGGING_OUT then do nothing: will get into onLoginSessionDisconnectedInternal() after completion, close() will be called there

    return true;
  },

  // -------------------------------------------------------------------------

  toString: function _Vivox5_Client_toString() {
    // just for debugging
    return '[object Vivox5.Client]';
  },

  // -------------------------------------------------------------------------

  onLoginSessionDisconnectedInternal: function _Vivox5_Client_onLoginSessionDisconnectedInternal(loginSession) {
    Vivox5.trace();

    var accountJid = loginSession.loginSessionId.toString();

    loginSession.close();

    if (accountJid in this.loginSessions) {
      delete this.loginSessions[accountJid];
    }
  },

  // -------------------------------------------------------------------------
};

// ===========================================================================================

// Protect essential Vivox5.Client object properties and methods from replacement and removal
Object.defineProperties(Vivox5.Client, {
  // properties
  internalVersion: { writable: false, configurable: false, enumerable: true },
  isInitialized: { writable: true, configurable: false, enumerable: true },
  loginSessions: { writable: false, configurable: false, enumerable: true },
  logSubsystemId: { writable: false, configurable: false, enumerable: true },

  // methods
  initialize: { writable: false, configurable: false, enumerable: true },
  uninitialize: { writable: false, configurable: false, enumerable: true },
  getRandomUserName: { writable: false, configurable: false, enumerable: true },
  getRandomAccountId: { writable: false, configurable: false, enumerable: true },
  getRandomChannelName: { writable: false, configurable: false, enumerable: true },
  getRandomChannelId: { writable: false, configurable: false, enumerable: true },
  getLoginSession: { writable: false, configurable: false, enumerable: true },
  deleteLoginSession: { writable: false, configurable: false, enumerable: true },
  toString: { writable: false, configurable: false, enumerable: true },

  // internal methods
  onLoginSessionDisconnectedInternal: { writable: false, configurable: false, enumerable: false },
});

Object.defineProperty(Vivox5, 'Client', { writable: false, configurable: false, enumerable: true });

// *****************************************************************************************************************************

/**
 * @class
 * @classdesc An instance of an account logging into the Vivox Network. The constructor must not be called directly by the application, use {@link Vivox5.Client.getLoginSession} instead.
 * @hideconstructor
 */
Vivox5.LoginSession = function _Vivox5_LoginSession(loginSessionId, forceSecurePreloginURI = true, uriWsXmpp = undefined) {
  Vivox5.trace();

  // Internal identification
  var self = this; // store 'this' in the closure for private functions
  this.internalId = Vivox5.Tools.generateInternalId();
  Object.defineProperty(this, 'internalId', { writable: false, configurable: false, enumerable: false });
  var logSubsystemId = 'LS-' + this.internalId; // used primarily for logging
  var className = 'Vivox5.LoginSession';        // used primarily for error reporting

  // Check parameters
  if (arguments.length < 1 || arguments.length > 3) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, className, logSubsystemId);
  if (!(loginSessionId instanceof Vivox5.AccountId)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'loginSessionId', 'Vivox5.AccountId', className, logSubsystemId);
  if (loginSessionId.isEmpty()) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'loginSessionId', className, logSubsystemId);
  if (typeof forceSecurePreloginURI !== 'boolean') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'forceSecurePreloginURI', 'boolean', className, logSubsystemId);
  if (uriWsXmpp !== undefined && typeof uriWsXmpp !== 'string') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'uriWsXmpp', 'string or undefined', className, logSubsystemId);
  if (uriWsXmpp !== undefined && !Vivox5.Tools.isValidWsUri(uriWsXmpp)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'uriWsXmpp', className, logSubsystemId);

  // ===========================================================================================
  // Vivox5.LoginSession public properties

  /**
   * The unique identifier for this LoginSession
   * @member {Vivox5.AccountId} Vivox5.LoginSession#loginSessionId
   * @readonly
   */
  this.loginSessionId = loginSessionId;
  Object.defineProperty(this, 'loginSessionId', { writable: false, configurable: false, enumerable: true });

  /**
   * The current state of this login session
   * @member {Vivox5.LoginSession.LOGINSTATE} Vivox5.LoginSession#state
   * @readonly
   */
  var state = Vivox5.LoginSession.LOGINSTATE.LOGGED_OUT;
  Object.defineProperties(this, {
    state: { configurable: false, enumerable: true, get: () => state },
    isConnected: { configurable: false, enumerable: true, get: () => (state == Vivox5.LoginSession.LOGINSTATE.LOGGED_IN) }, // just a shortcut
  });

  /**
   * Just a shortcut to (Vivox5.LoginSession.state == Vivox5.LoginSession.LOGINSTATE.LOGGED_IN)
   * @member {boolean} Vivox5.LoginSession#isConnected
   * @readonly
   */

  // ChannelSessions created by this LoginSession
  /**
   * The list of channel sessions associated with this login session (object keys are {@link Vivox5.ChannelId} in toString() format).
   * @member {{Object<string,Vivox5.ChannelSession>}} Vivox5.LoginSession#channelSessions
   * @readonly
   * @see The application should not work directly with this object, use the {@link Vivox5.LoginSession#getChannelSession} method instead.
   */
  var channelSessions = {}; // map ChannelId.toString() -> channelSession
  Object.defineProperties(this, {
    channelSessions: { configurable: false, enumerable: true, get: () => channelSessions },
    channelSessionsCount: { configurable: false, enumerable: true, get: () => Vivox5.Tools.propCount(channelSessions) }, // just a shortcut
  });

  /**
   * Count of Channel Sessions in the {@link Vivox5.LoginSession#channelSessions} collection
   * @member {number} Vivox5.LoginSession#channelSessionsCount
   * @readonly
   */

  // Own presence status
  /**
   * The online status that is sent to those accounts subscribing to the presence of this account.
   * This property is read-write. The application can assign new {@link Vivox5.Presence} objects to this property to send presence updates to the server.
   * @member {Vivox5.Presence} Vivox5.LoginSession#presence
   */
  var presence = new Vivox5.Presence();
  Object.defineProperty(this, 'presence', {
    configurable: false, enumerable: true,
    get: () => presence,
    set: newPresence => {
      if (!(newPresence instanceof Vivox5.Presence)) {
        throw new Vivox5.Error(Vivox5.Error.CODE.EPROPTYPE, 'presence', 'Vivox5.Presence', 'Vivox5.LoginSession.presence.set()', logSubsystemId);
      }
      presence = newPresence;
      sendOwnPresence();
    },
  });

  /**
   * The list of accounts allowed to see this accounts online status.
   * @member {Set<Vivox5.AccountId#toString>} Vivox5.LoginSession#allowedSubscriptions
   * @readonly
   * @see The application should not work directly with this object; use the {@link Vivox5.LoginSession#addAllowedSubscription} and {@link Vivox5.LoginSession#removeAllowedSubscription} methods to add and remove accounts to/from this list.
   */
  this.allowedSubscriptions = new Set(); // set of AccountId.toString()

  /**
   * The list of accounts blocked from seeing this accounts online status.
   * @member {Set<Vivox5.AccountId#toString>} Vivox5.LoginSession#blockedSubscriptions
   * @readonly
   * @see The application should not work directly with this object; use the {@link Vivox5.LoginSession#addBlockedSubscription} and {@link Vivox5.LoginSession#removeBlockedSubscription} methods to add and remove accounts to/from this list.
   */
  this.blockedSubscriptions = new Set(); // set of AccountId.toString()

  /**
   * The list of incoming subscription requests.
   * @member {Set<Vivox5.AccountId#toString>} Vivox5.LoginSession#incomingSubscriptionRequests
   * @readonly
   * @see The application should not work directly with this object; use the {@link Vivox5.LoginSession#addAllowedSubscription} and {@link Vivox5.LoginSession#addBlockedSubscription} methods to accept or reject incoming subscription requests.
   * @see See also {@link Vivox5.LoginSession#onIncomingSubscriptionRequest} callback.
   */
  this.incomingSubscriptionRequests = new Set(); // set of AccountId.toString()

  /**
   * The list of presence subscriptions associated with this login session. This typically corresponds to a list of "friends" or "buddies".
   * @member {Map<Vivox5.AccountId#toString, Vivox5.PresenceSubscription>} Vivox5.LoginSession#presenceSubscriptions
   * @readonly
   * @see The application should not work directly with this object; use the {@link Vivox5.LoginSession#addPresenceSubscription} and {@link Vivox5.LoginSession#removePresenceSubscription} methods to create and remove presence subscriptions stored in this list.
   */
  this.presenceSubscriptions = new Map(); // map AccountId.toString() -> PresenceSubscription

  // this.presenceSubscriptionsScheduled must not be accessible from outside, except for tests
  this.presenceSubscriptionsScheduled = new Set(); // scheduled presence subscriptions (added before login()), set of AccountId.toString()

  Object.defineProperties(this, {
    allowedSubscriptions: { writable: false, configurable: false, enumerable: true },
    blockedSubscriptions: { writable: false, configurable: false, enumerable: true },
    incomingSubscriptionRequests: { writable: false, configurable: false, enumerable: true },
    presenceSubscriptions: { writable: false, configurable: false, enumerable: true },
    presenceSubscriptionsScheduled: { writable: false, configurable: false, enumerable: false },
  });

  // ===========================================================================================
  // Callback functions (to be set by the application)

  // XMPP connection callbacks
  /**
   * Callback function provided by the application, invoked when a LoginSession is connected and logged in to a Vivox server
   * @member {Vivox5.LoginSession#callbackOnConnected} Vivox5.LoginSession#onConnected
   */
  /**
   * The signature of a callback function which is invoked when a LoginSession is connected and logged in to a Vivox server
   * @callback Vivox5.LoginSession#callbackOnConnected
   * @param {Vivox5.LoginSession} loginSession - the Login Session which invoked this callback function
   */
  var cbOnConnected = undefined; // function(loginSession)

  /**
   * Callback function provided by the application, invoked when a LoginSession is disconnected from a Vivox server
   * @member {Vivox5.LoginSession#callbackOnDisconnected} Vivox5.LoginSession#onDisconnected
   */
  /**
   * The signature of a callback function which is invoked when a LoginSession is disconnected from a Vivox server
   * @callback Vivox5.LoginSession#callbackOnDisconnected
   * @param {Vivox5.LoginSession} loginSession - the Login Session which invoked this callback function
   */
  var cbOnDisconnected = undefined; // function(loginSession)

  // Callbacks for channelSession creation/removal

  /**
   * Callback function provided by the application, invoked when a new {@link Vivox5.ChannelSession} is created within this Login Session
   * @member {Vivox5.LoginSession#callbackOnChannelSessionAdded} Vivox5.LoginSession#onChannelSessionAdded
   */
  /**
   * The signature of a callback function which is invoked when a new {@link Vivox5.ChannelSession} is created within this LoginSession
   * @callback Vivox5.LoginSession#callbackOnChannelSessionAdded
   * @param {Vivox5.LoginSession} loginSession - the LoginSession which invoked this callback function
   * @param {Vivox5.ChannelSession} channelSession - the ChannelSession just created
   * @see channelSessions property
   */
  var cbOnChannelSessionAdded = undefined; // function(loginSession, channelSession)

  /**
   * Callback function provided by the application, invoked when a terminated {@link Vivox5.ChannelSession} is removed from this Login Session
   * @member {Vivox5.LoginSession#callbackOnChannelSessionRemoved} Vivox5.LoginSession#onChannelSessionRemoved
   */
  /**
   * The signature of a callback function which is invoked when a terminated {@link Vivox5.ChannelSession} is removed from this LoginSession
   * @callback Vivox5.LoginSession#callbackOnChannelSessionRemoved
   * @param {Vivox5.LoginSession} loginSession - the Login Session which invoked this callback function
   * @param {Vivox5.ChannelSession} channelSession - the ChannelSession being removed (the application must not use the reference to that ChannelSession after this callback returns)
   * @see channelSessions property
   */
  var cbOnChannelSessionRemoved = undefined; // function(loginSession, channelSession), channelSession will become invalid *after* this callback returns

  // Callback for directed text message

  /**
   * Callback function provided by the application, invoked when a directed (peer-to-peer) text message is received for the account corresponding to this Login Session
   * @member {Vivox5.LoginSession#callbackOnDirectedTextMessage} Vivox5.LoginSession#onDirectedTextMessage
   */
  /**
   * The signature of a callback function which is invoked when a directed (peer-to-peer) text message is received for the account corresponding to this LoginSession
   * @callback Vivox5.LoginSession#callbackOnDirectedTextMessage
   * @param {Vivox5.LoginSession} loginSession - the Login Session which invoked this callback function
   * @param {Vivox5.TextMessage} textMessage - the newly received text message
   * @see {@link Vivox5.LoginSession#sendDirectedMessage}
   */
  var cbOnDirectedTextMessage = undefined; // function(loginSession, textMessage)

  // Presence subscription callbacks

  /**
   * Callback function provided by the application, invoked when another account inteds to subscribe to this account's presence status
   * @member {Vivox5.LoginSession#callbackOnIncomingSubscriptionRequest} Vivox5.LoginSession#onIncomingSubscriptionRequest
   */
  /**
   * The signature of a callback function which is invoked when another account inteds to subscribe to this account's presence status
   * @callback Vivox5.LoginSession#callbackOnIncomingSubscriptionRequest
   * @param {Vivox5.LoginSession} loginSession - the Login Session which invoked this callback function
   * @param {Vivox5.AccountId} subscriber - the account id of the subscriber
   * @param {boolean} autoAccepted - true means that the subscription request was automatically accepted by the LoginSession due to previous call to {@link Vivox5.LoginSession#addAllowedSubscription}; if false then the application must accept or reject the subscription request by calling {@link Vivox5.LoginSession#addAllowedSubscription} or {@link Vivox5.LoginSession#addBlockedSubscription} respectively
   */
  var cbOnIncomingSubscriptionRequest = undefined; // function(loginSession, accountId, autoAccepted)

  /**
   * Callback function provided by the application, invoked when an outgoing subscription is created to monitor the presence status of another account
   * @member {Vivox5.LoginSession#callbackOnBuddyAdded} Vivox5.LoginSession#onBuddyAdded
   */
  /**
   * The signature of a callback function which is invoked when an outgoing subscription is created to monitor the presence status of another account (this typically corresponds to a list of "friends" or "buddies")
   * @callback Vivox5.LoginSession#callbackOnBuddyAdded
   * @param {Vivox5.LoginSession} loginSession - the Login Session which invoked this callback function
   * @param {Vivox5.AccountId} buddy - the account id added to the presenceSubscriptions list
   * @see presenceSubscriptions property
   * @see Outgoing presence subscriptions are created using the {@link Vivox5.LoginSession#addPresenceSubscription} method.
   */
  var cbOnBuddyAdded = undefined; // function(loginSession, accountId)

  /**
   * Callback function provided by the application, invoked when an outgoing presence subscription is removed
   * @member {Vivox5.LoginSession#callbackOnBuddyRemoved} Vivox5.LoginSession#onBuddyRemoved
   */
  /**
   * The signature of a callback function which is invoked when an outgoing presence subscription is removed. It can happen not only after calling the {@link Vivox5.LoginSession#removePresenceSubscription} method, but also when the remote account disallows to monitor its presence status.
   * @callback Vivox5.LoginSession#callbackOnBuddyRemoved
   * @param {Vivox5.LoginSession} loginSession - the Login Session which invoked this callback function
   * @param {Vivox5.AccountId} buddy - the account id removed from the presenceSubscriptions list
   * @see Outgoing presence subscriptions can be removed using the {@link Vivox5.LoginSession#removePresenceSubscription} method.
   */
  var cbOnBuddyRemoved = undefined; // function(loginSession, accountId)

  /**
   * Callback function provided by the application, invoked when a friend's presence status is changed
   * @member {Vivox5.LoginSession#callbackOnBuddyPresence} Vivox5.LoginSession#onBuddyPresence
   */
  /**
   * The signature of a callback function which is invoked when a friend's presence status is changed
   * @callback Vivox5.LoginSession#callbackOnBuddyPresence
   * @param {Vivox5.LoginSession} loginSession - the Login Session which invoked this callback function
   * @param {Vivox5.AccountId} buddy - the account id of the friend, whose presence status is changed
   * @param {Vivox5.PresenceLocations} presenceLocations - presence status of all locations associated with this friend
   */
  var cbOnBuddyPresence = undefined; // function(loginSession, accountId, presenceLocations)

  function validateCB(cb, name) { if (typeof cb !== 'function' && cb !== undefined) throw new Vivox5.Error(Vivox5.Error.CODE.EPROPTYPE, name, 'function', className, logSubsystemId); }

  Object.defineProperties(this, {
    onConnected: { configurable: false, enumerable: true, get: () => cbOnConnected, set: (cb) => { validateCB(cb, 'onConnected'); cbOnConnected = cb; } },
    onDisconnected: { configurable: false, enumerable: true, get: () => cbOnDisconnected, set: (cb) => { validateCB(cb, 'onDisconnected'); cbOnDisconnected = cb; } },
    onChannelSessionAdded: { configurable: false, enumerable: true, get: () => cbOnChannelSessionAdded, set: (cb) => { validateCB(cb, 'onChannelSessionAdded'); cbOnChannelSessionAdded = cb; } },
    onChannelSessionRemoved: { configurable: false, enumerable: true, get: () => cbOnChannelSessionRemoved, set: (cb) => { validateCB(cb, 'onChannelSessionRemoved'); cbOnChannelSessionRemoved = cb; } },
    onDirectedTextMessage: { configurable: false, enumerable: true, get: () => cbOnDirectedTextMessage, set: (cb) => { validateCB(cb, 'onDirectedTextMessage'); cbOnDirectedTextMessage = cb; } },
    onIncomingSubscriptionRequest: { configurable: false, enumerable: true, get: () => cbOnIncomingSubscriptionRequest, set: (cb) => { validateCB(cb, 'onIncomingSubscriptionRequest'); cbOnIncomingSubscriptionRequest = cb; } },
    onBuddyAdded: { configurable: false, enumerable: true, get: () => cbOnBuddyAdded, set: (cb) => { validateCB(cb, 'onBuddyAdded'); cbOnBuddyAdded = cb; } },
    onBuddyRemoved: { configurable: false, enumerable: true, get: () => cbOnBuddyRemoved, set: (cb) => { validateCB(cb, 'onBuddyRemoved'); cbOnBuddyRemoved = cb; } },
    onBuddyPresence: { configurable: false, enumerable: true, get: () => cbOnBuddyPresence, set: (cb) => { validateCB(cb, 'onBuddyPresence'); cbOnBuddyPresence = cb; } },
  });

  // ===========================================================================================
  // Private data
  var accountJid = loginSessionId.toString(); // Expand to 'user@realm' string just once, because it is used everywhere as a string, not an object
  var xhrPrelogin = null;
  var stropheConnection = null; // Strophe.JS Connection object
  var subscrMode = Vivox5.LoginSession.SUBSCRIPTIONMODE.DEFER; // can be changed in this.login()
  var loginAccessToken = ''; // will be set in this.login()
  var stropheConnection = null; // Strophe.JS Connection object
  Object.defineProperty(this, '_stropheConnection', { configurable: false, enumerable: false, get: () => stropheConnection }); // exposed as a property only for other Vivox5 objects; it must not be used from outside

  // ===========================================================================================
  // Vivox5.LoginSession public methods
  // !!! Important: when adding or removing public methods, don't forget to update the Object.defineProperties table located below all public methods

  /**
   * Begin logging in this session, with presence.
   * @async
   *
   * @param {string} accessToken - an access token provided by your game server that enables this login
   * @param {Vivox5.LoginSession.SUBSCRIPTIONMODE} [subscriptionMode] - how to handle incoming subscriptions
   * @param {(Array<Vivox5.AccountId>|Set<Vivox5.AccountId>)} [presenceSubscriptions] - A list of accounts for which this user wishes to monitor online status
   * @param {(Array<Vivox5.AccountId>|Set<Vivox5.AccountId>)} [blockedSubscriptions] - A list of accounts that are not allwed to see this user's online status
   * @param {(Array<Vivox5.AccountId>|Set<Vivox5.AccountId>)} [allowedSubscriptions] - A list of accounts that are allowed to see this user's online status
   *
   * @returns {boolean} true on success
   *
   * @throws {Vivox5.Error} not in the LOGGED_OUT state: Vivox5.Error.CODE.ESTATE
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} accessToken is not a string: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} subscriptionMode is not a type described in Vivox5.LoginSession.SUBSCRIPTIONMODE: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} subscriptionMode is not one of values described in Vivox5.LoginSession.SUBSCRIPTIONMODE: Vivox5.Error.CODE.EPARAMVALUE
   * @throws {Vivox5.Error} presenceSubscriptions or blockedSubscriptions or allowedSubscriptions is not an Array or Set of Vivox5.AccountId: Vivox5.Error.CODE.EPARAMTYPE
   *
   * @see Developer of games that do not have secure communications requirements can use {@link Vivox5.LoginSession.getLoginToken} to generate the required access token.
   */
  this.login = function _Vivox5_LoginSession_login(
    accessToken,
    subscriptionMode = Vivox5.LoginSession.SUBSCRIPTIONMODE.DEFER,
    presenceSubscriptions = undefined, // string or array or set
    blockedSubscriptions = undefined,  // string or array or set
    allowedSubscriptions = undefined   // string or array or set
  ) {
    var funcName = 'Vivox5.LoginSession.login()';
    Vivox5.trace();

    // Check state
    if (state != Vivox5.LoginSession.LOGINSTATE.LOGGED_OUT) {
      throw new Vivox5.Error(Vivox5.Error.CODE.ESTATE, Vivox5.Tools.findKeyByValue(Vivox5.LoginSession.LOGINSTATE, state), funcName, logSubsystemId);
    }

    // Check parameters
    if (arguments.length < 1 || arguments.length > 5) {
      throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);
    }

    if (typeof accessToken !== 'string') {
      throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'accessToken', 'string', funcName, logSubsystemId);
    }

    if (typeof subscriptionMode !== typeof Vivox5.LoginSession.SUBSCRIPTIONMODE.DEFER) {
      throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'subscriptionMode', typeof Vivox5.LoginSession.SUBSCRIPTIONMODE.DEFER, funcName, logSubsystemId);
    }

    if (
      subscriptionMode !== Vivox5.LoginSession.SUBSCRIPTIONMODE.ACCEPT &&
      subscriptionMode !== Vivox5.LoginSession.SUBSCRIPTIONMODE.BLOCK &&
      subscriptionMode !== Vivox5.LoginSession.SUBSCRIPTIONMODE.DEFER
    ) {
      throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'subscriptionMode', funcName, logSubsystemId);
    }

    if (
      !(presenceSubscriptions instanceof Vivox5.AccountId)
      && !Array.isArray(presenceSubscriptions)
      && !(presenceSubscriptions instanceof Set)
      && !(presenceSubscriptions instanceof Map)
      && presenceSubscriptions !== undefined
    ) {
      throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'presenceSubscriptions', 'Array or Set or Map of Vivox5.AccountId, or plain Vivox5.AccountId, or undefined', funcName, logSubsystemId);
    }

    if (
      !(blockedSubscriptions instanceof Vivox5.AccountId)
      && !Array.isArray(blockedSubscriptions)
      && !(blockedSubscriptions instanceof Set)
      && !(blockedSubscriptions instanceof Map)
      && blockedSubscriptions !== undefined
    ) {
      throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'blockedSubscriptions', 'Array or Set or Map of Vivox5.AccountId, or plain Vivox5.AccountId, or undefined', funcName, logSubsystemId);
    }

    if (
      !(allowedSubscriptions instanceof Vivox5.AccountId)
      && !Array.isArray(allowedSubscriptions)
      && !(allowedSubscriptions instanceof Set)
      && !(allowedSubscriptions instanceof Map)
      && allowedSubscriptions !== undefined
    ) {
      throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'allowedSubscriptions', 'Array or Set or Map of Vivox5.AccountId, or plain Vivox5.AccountId, or undefined', funcName, logSubsystemId);
    }

    subscrMode = subscriptionMode;
    loginAccessToken = accessToken; // store for performStropheConnection()

    if (allowedSubscriptions !== undefined) {
      addAllowedSubscription(allowedSubscriptions);   // may throw Vivox5.Error(EPARAMTYPE)
    }

    if (blockedSubscriptions !== undefined) {
      addBlockedSubscription(blockedSubscriptions);   // may throw Vivox5.Error(EPARAMTYPE)
    }

    if (presenceSubscriptions !== undefined) {
      addPresenceSubscription(presenceSubscriptions); // may throw Vivox5.Error(EPARAMTYPE)
    }

    if (uriWsXmpp) {
      return performStropheConnection();
    }
    else {
      return performPrelogin();
    }
  };

  // -------------------------------------------------------------------------

  /**
   * Gets the channel session for this channelId, creating one if necessary.
   *
   * @param {Vivox5.ChannelId} channel - the id of the channel (important: the realm and issuer of the channel must be the same as in the Login Session)
   *
   * @returns {Vivox5.ChannelSession} the channel session
   *
   * @throws {Vivox5.Error} not in the LOGGED_IN state: Vivox5.Error.CODE.ESTATE
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} channel is not a Vivox5.ChannelId: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} channel is empty: Vivox5.Error.CODE.EPARAMVALUE
   * @throws {Vivox5.Error} domain or/and issuer in channel don't match those of the session: Vivox5.Error.CODE.EPARAMVALUE
   */
  this.getChannelSession = function _Vivox5_LoginSession_getChannelSession(channel) {
    var funcName = 'Vivox5.LoginSession.getChannelSession()';
    Vivox5.trace();

    // Check state
    if (state != Vivox5.LoginSession.LOGINSTATE.LOGGED_IN) {
      throw new Vivox5.Error(Vivox5.Error.CODE.ESTATE, Vivox5.Tools.findKeyByValue(Vivox5.LoginSession.LOGINSTATE, state), funcName, logSubsystemId);
    }

    // Check parameters
    if (arguments.length != 1) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);
    if (!(channel instanceof Vivox5.ChannelId)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'channel', 'Vivox5.ChannelId', funcName, logSubsystemId);
    if (channel.isEmpty()) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'channel', funcName, logSubsystemId);

    // Check that realm and issuer in channel are equal with loginSessionId
    if (
      channel.domain != loginSessionId.domain ||
      channel.issuer != loginSessionId.issuer
    ) {
      throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'channel', funcName, logSubsystemId, 'domain and issuer in channel must match with domain and issuer of the LoginSession');
    }

    var channelJid = channel.toString();

    if (channelJid in channelSessions) {
      // Existing channelSession
      return channelSessions[channelJid];
    }

    // Only one ChannelSession is allowed at a time, multichannel is not supported (WRT-25, WRT-85)
    if (Vivox5.Tools.propCount(channelSessions) > 0) {
      throw new Vivox5.Error(Vivox5.Error.CODE.ECONSTRAINS, funcName, logSubsystemId, 'multiple simultaneous ChannelSessions are not supported');
    }

    var channelSession = new Vivox5.ChannelSession(channel, self);
    channelSessions[channelJid] = channelSession;

    // Callback for channelSession creation
    if (cbOnChannelSessionAdded && typeof cbOnChannelSessionAdded === 'function') {
      cbOnChannelSessionAdded(self, channelSession);
    }

    return channelSession;
  };

  // -------------------------------------------------------------------------

  /**
   * Deletes the Channel Session for this channel id, disconnecting the session if necessary.
   *
   * @param {(Vivox5.ChannelId|Vivox5.ChannelSession)} channelSessionOrId - the id of the channel (or the whole Vivox5.ChannelSession object may be passed here)
   *
   * @returns {boolean} true on success, false if the ChannelSession specified does not exist
   *
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} channelSessionOrId is neither Vivox5.ChannelId nor Vivox5.ChannelSession: Vivox5.Error.CODE.EPARAMTYPE
   */
  this.deleteChannelSession = function _Vivox5_LoginSession_deleteChannelSession(channelSessionOrId) {
    var funcName = 'Vivox5.LoginSession.deleteChannelSession()';
    Vivox5.trace();

    // Check parameters
    if (arguments.length != 1) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);

    if (
      !(channelSessionOrId instanceof Vivox5.ChannelSession) &&
      !(channelSessionOrId instanceof Vivox5.ChannelId)
    ) {
      throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'channelSessionOrId', 'Vivox5.ChannelSession or Vivox5.ChannelId', funcName, logSubsystemId);
    }

    // Check whether the session exists in this LoginSession's collection
    var channelJid = (channelSessionOrId instanceof Vivox5.ChannelId) ? channelSessionOrId.toString() : ('channel' in channelSessionOrId ? channelSessionOrId.channel.toString() : '');
    if (!(channelJid in channelSessions)) {
      return false;
    }

    // Disconnect or remove ChannelSession
    var channelSession = channelSessions[channelJid];

    if (channelSession.isEstablished) {
      channelSession.disconnect(); // will get into onChannelSessionTerminatedInternal() after disconnect, close() will be called there
    }
    else {
      channelSession.close();
      delete channelSessions[channelJid];
    }

    return true;
  };

  // -------------------------------------------------------------------------

  // NB: this function is called internally be Vivox5.ChannelSession, it MUST NOT be called by the SDK users. Must be declared as {enumerable: false} below
  this.onChannelSessionTerminatedInternal = function _Vivox5_LoginSession_onSessionTerminatedInternal(channelSession) {
    Vivox5.trace();

    if (!(channelSession.channel.toString() in channelSessions)) {
      // Internal error, callback from not registered channelSession
      return false;
    }

    delete channelSessions[channelSession.channel.toString()];

    // Callback for channelSession removal
    if (cbOnChannelSessionRemoved && typeof cbOnChannelSessionRemoved === 'function') {
      cbOnChannelSessionRemoved(self, channelSession);
    }

    channelSession.close();

    if (state == Vivox5.LoginSession.LOGINSTATE.LOGGING_OUT) {
      var havePendingSessions = false;
      for (let channelJid in channelSessions) {
        havePendingSessions = true;
        break;
      }
      if (!havePendingSessions) {
        removeLoginSessionHandlers();
        stropheConnection.disconnect();
      }
    }

    return true;
  };

  // -------------------------------------------------------------------------

  /**
   * Log the account out of the Vivox system.
   * @async
   *
   * @returns {boolean} true on success
   *
   * @throws {Vivox5.Error} not in the LOGGED_IN or LOGGING_IN or PRELOGIN state: Vivox5.Error.CODE.ESTATE
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   */
  this.logout = function _Vivox5_LoginSession_logout() {
    var funcName = 'Vivox5.LoginSession.logout()';
    Vivox5.trace();

    // Check state
    if (
      state == Vivox5.LoginSession.LOGINSTATE.LOGGING_OUT ||
      state == Vivox5.LoginSession.LOGINSTATE.FINISHED
    ) {
      return true; // already in progress
    }

    if (
      state != Vivox5.LoginSession.LOGINSTATE.PRELOGIN &&
      state != Vivox5.LoginSession.LOGINSTATE.LOGGING_IN &&
      state != Vivox5.LoginSession.LOGINSTATE.LOGGED_IN
    ) {
      throw new Vivox5.Error(Vivox5.Error.CODE.ESTATE, Vivox5.Tools.findKeyByValue(Vivox5.LoginSession.LOGINSTATE, state), funcName, logSubsystemId);
    }

    // Check parameters
    if (arguments.length != 0) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);

    state = Vivox5.LoginSession.LOGINSTATE.LOGGING_OUT;

    releaseXhrPrelogin();

    var havePendingSessions = false;
    for (let channelJid in channelSessions) {
      if (channelSessions[channelJid].isEstablished) {
        havePendingSessions = true;
        channelSessions[channelJid].disconnect();
      }
      else {
        channelSessions[channelJid].close();
        delete channelSessions[channelJid];
      }
    }

    if (!havePendingSessions) {
      removeLoginSessionHandlers();
      stropheConnection.disconnect();
    }

    return true;
  };

  // -------------------------------------------------------------------------

  /**
   * Allow incoming subscription requests from the specified account(s).
   *
   * @param {(Vivox5.AccountId|Array<Vivox5.AccountId>|Set<Vivox5.AccountId>)} accountId - the account id(s) to allow
   *
   * @returns {boolean} true on success
   *
   * @throws {Vivox5.Error} accountId is neither a Vivox5.AccountId nor Array, nor Set: Vivox5.Error.CODE.EPARAMTYPE
   *
   * @see {@link Vivox5.LoginSession#removeAllowedSubscription}
   */
  this.addAllowedSubscription = function _Vivox5_LoginSession_addAllowedSubscription(accountId) {
    Vivox5.trace();
    // Parameters are checked inside private function implementation, because it can be called not only from here, but from this.login() too
    return addAllowedSubscription(accountId);
  };

  // -------------------------------------------------------------------------

  /**
   * Disallow incoming subscription requests from the specified account(s). Subscription requests from the specified account(s) will cause an event to be raised to the application.
   *
   * @param {(Vivox5.AccountId|Array<Vivox5.AccountId>|Set<Vivox5.AccountId>)} accountId - the account id(s) to disallow
   *
   * @returns {boolean} true on success
   *
   * @throws {Vivox5.Error} accountId is neither a Vivox5.AccountId nor Array, nor Set: Vivox5.Error.CODE.EPARAMTYPE
   *
   * @see {@link Vivox5.LoginSession#addAllowedSubscription}, {@link Vivox5.LoginSession#addBlockedSubscription}
   */
  this.removeAllowedSubscription = function _Vivox5_LoginSession_removeAllowedSubscription(accountId) {
    Vivox5.trace();
    // Parameters are checked inside private function implementation
    return removeAllowedSubscription(accountId);
  };

  // -------------------------------------------------------------------------

  /**
   * Block incoming subscription requests from the specified account(s).
   *
   * @param {(Vivox5.AccountId|Array<Vivox5.AccountId>|Set<Vivox5.AccountId>)} accountId - the account id(s) to block
   *
   * @returns {boolean} true on success
   *
   * @throws {Vivox5.Error} accountId is neither a Vivox5.AccountId nor Array, nor Set: Vivox5.Error.CODE.EPARAMTYPE
   *
   * @see {@link Vivox5.LoginSession#removeBlockedSubscription}
   */
  this.addBlockedSubscription = function _Vivox5_LoginSession_addBlockedSubscription(accountId) {
    Vivox5.trace();
    // Parameters are checked inside private function implementation, because it can be called not only from here, but from this.login() too
    return addBlockedSubscription(accountId);
  };

  // -------------------------------------------------------------------------

  /**
   * Unblock incoming subscription requests from the specified account(s). Subscription requests from the specified account(s) will cause an event to be raised to the application.
   *
   * @param {(Vivox5.AccountId|Array<Vivox5.AccountId>|Set<Vivox5.AccountId>)} accountId - the account id(s) to unblock
   *
   * @returns {boolean} true on success
   *
   * @throws {Vivox5.Error} accountId is neither a Vivox5.AccountId nor Array, nor Set: Vivox5.Error.CODE.EPARAMTYPE
   *
   * @see {@link Vivox5.LoginSession#addBlockedSubscription}, {@link Vivox5.LoginSession#addAllowedSubscription}
   */
  this.removeBlockedSubscription = function _Vivox5_LoginSession_removeBlockedSubscription(accountId) {
    Vivox5.trace();
    // Parameters are checked inside private function implementation
    return removeBlockedSubscription(accountId);
  };

  // -------------------------------------------------------------------------

  /**
   * Subscribe to the specified account(s).
   *
   * @param {(Vivox5.AccountId|Array<Vivox5.AccountId>|Set<Vivox5.AccountId>)} accountId - the account id(s) to subscribe to
   *
   * @returns {boolean} true on success
   *
   * @throws {Vivox5.Error} accountId is neither a Vivox5.AccountId nor Array, nor Set: Vivox5.Error.CODE.EPARAMTYPE
   *
   * @see {@link Vivox5.LoginSession#removePresenceSubscription}
   */
  this.addPresenceSubscription = function _Vivox5_LoginSession_addPresenceSubscription(accountId) {
    Vivox5.trace();
    // Parameters are checked inside private function implementation, because it can be called not only from here, but from this.login() too
    return addPresenceSubscription(accountId);
  };

  // -------------------------------------------------------------------------

  /**
   * Unsubscribe from the specified account(s).
   *
   * @param {(Vivox5.AccountId|Array<Vivox5.AccountId>|Set<Vivox5.AccountId>)} accountId - the account id(s) to unsubscribe from
   *
   * @returns {boolean} true on success
   *
   * @throws {Vivox5.Error} accountId is neither a Vivox5.AccountId nor Array, nor Set: Vivox5.Error.CODE.EPARAMTYPE
   *
   * @see {@link Vivox5.LoginSession#addPresenceSubscription}
   */
  this.removePresenceSubscription = function _Vivox5_LoginSession_removePresenceSubscription(accountId) {
    Vivox5.trace();
    // Parameters are checked inside private function implementation
    return removePresenceSubscription(accountId);
  };

  // -------------------------------------------------------------------------

  /**
   * Send a message to the specific account.
   * @async
   *
   * @param {Vivox5.AccountId} to - the intended recipient of the message
   * @param {string} text - the body of the message
   * @param {string} [subject] - the subject of the message
   * @param {string} [language] - the language of the message e.g. "en". This can be undefined to use the default language. This must conform to [RFC5646]{@link https://tools.ietf.org/html/rfc5646}
   * @param {string} [applicationStanzaNamespace] - an optional namespace element for additional application data
   * @param {string} [applicationStanzaBody] - the additional application data body
   *
   * @returns {boolean} true on success
   *
   * @throws {Vivox5.Error} not in the LOGGED_IN state: Vivox5.Error.CODE.ESTATE
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} to is not a Vivox5.AccountId: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} to is empty: Vivox5.Error.CODE.EPARAMVALUE
   * @throws {Vivox5.Error} text or subject or language or applicationStanzaNamespace or applicationStanzaBody is not a string: Vivox5.Error.CODE.EPARAMTYPE
   */
  this.sendDirectedMessage = function _Vivox5_LoginSession_sendDirectedMessage(to, text, subject = undefined, language = undefined, applicationStanzaNamespace = undefined, applicationStanzaBody = undefined) {
    var funcName = 'Vivox5.LoginSession.sendDirectedMessage()';
    Vivox5.trace();

    // Check state
    if (state != Vivox5.LoginSession.LOGINSTATE.LOGGED_IN) {
      throw new Vivox5.Error(Vivox5.Error.CODE.ESTATE, Vivox5.Tools.findKeyByValue(Vivox5.LoginSession.LOGINSTATE, state), funcName, logSubsystemId);
    }

    // Check parameters
    if (arguments.length < 2 || arguments.length > 6) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);
    if (!(to instanceof Vivox5.AccountId)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'to', 'Vivox5.AccountId', funcName, logSubsystemId);
    if (to.isEmpty()) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'to', funcName, logSubsystemId);
    if (typeof text !== 'string') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'text', 'string', funcName, logSubsystemId);

    if (typeof subject !== 'string' && subject !== undefined) {
      throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'text', 'subject or undefined', funcName, logSubsystemId);
    }

    if (typeof language !== 'string' && language !== undefined) {
      throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'text', 'language or undefined', funcName, logSubsystemId);
    }

    if (typeof applicationStanzaNamespace !== 'string' && applicationStanzaNamespace !== undefined) {
      throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'applicationStanzaNamespace', 'string or undefined', funcName, logSubsystemId);
    }

    if (typeof applicationStanzaBody !== 'string' && applicationStanzaBody !== undefined) {
      throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'applicationStanzaBody', 'string or undefined', funcName, logSubsystemId);
    }

    // Send message
    sendDirectedMessage(to, text, subject, language, applicationStanzaNamespace, applicationStanzaBody);
    return true;
  };

  // -------------------------------------------------------------------------

  /**
   * Get a login token for this account.
   *
   * @param {string} tokenSigningKey - the key corresponding to the issuer for this account that is used to sign the token
   * @param {number} tokenExpirationDuration - the length of time the token is valid for
   *
   * @returns {string} an access token that can be used to log this account in
   *
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} tokenSigningKey is not a string: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} tokenExpirationDuration is not a number: Vivox5.Error.CODE.EPARAMTYPE
   */
  this.getLoginToken = function _Vivox5_LoginSession_getLoginToken(tokenSigningKey, tokenExpirationDuration) {
    var funcName = 'Vivox5.LoginSession.getLoginToken()';
    Vivox5.trace();

    if (arguments.length != 2) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);
    if (typeof tokenSigningKey !== 'string') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'tokenSigningKey', 'string', funcName, logSubsystemId);
    if (typeof tokenExpirationDuration !== 'number') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'tokenExpirationDuration', 'number', funcName, logSubsystemId);

    var token = Vivox5.Token.debugGenerateAccessToken(loginSessionId.issuer, tokenExpirationDuration, 'login', '', accountJid, '', tokenSigningKey);
    return token;
  };

  // -------------------------------------------------------------------------

  // NB: internal function it MUST NOT be called by the SDK users. Must be declared as {enumerable: false} below
  this.close = function _Vivox5_LoginSession_close() {
    Vivox5.trace();

    if (!self) {
      // already closed
      return;
    }

    releaseLoginSessionResources();
    self = undefined;
  };

  // -------------------------------------------------------------------------

  this.toString = function _Vivox5_LoginSession_toString() {
    // just for debugging
    return '[object Vivox5.LoginSession(' + accountJid + ')]';
  }

  // ===========================================================================================
  // Vivox5.LoginSession public methods configuration

  Object.defineProperties(this, {
    login: { writable: false, configurable: false, enumerable: true },
    getChannelSession: { writable: false, configurable: false, enumerable: true },
    deleteChannelSession: { writable: false, configurable: false, enumerable: true },
    onChannelSessionTerminatedInternal: { writable: false, configurable: false, enumerable: false },
    logout: { writable: false, configurable: false, enumerable: true },
    addAllowedSubscription: { writable: false, configurable: false, enumerable: true },
    removeAllowedSubscription: { writable: false, configurable: false, enumerable: true },
    addBlockedSubscription: { writable: false, configurable: false, enumerable: true },
    removeBlockedSubscription: { writable: false, configurable: false, enumerable: true },
    addPresenceSubscription: { writable: false, configurable: false, enumerable: true },
    removePresenceSubscription: { writable: false, configurable: false, enumerable: true },
    sendDirectedMessage: { writable: false, configurable: false, enumerable: true },
    getLoginToken: { writable: false, configurable: false, enumerable: true },
    close: { writable: false, configurable: false, enumerable: false },
    toString: { writable: false, configurable: false, enumerable: true },
  });

  // ===========================================================================================
  // Vivox5.LoginSession private functions

  function log(msg) {
    Vivox5.log(logSubsystemId, msg);
  }

  // -------------------------------------------------------------------------

  function onStropheRawInput(data) {
    Vivox5.logTraffic('RECV ' + data);
  }

  // -------------------------------------------------------------------------

  function onStropheRawOutput(data) {
    Vivox5.logTraffic('SENT ' + data);
  }

  // -------------------------------------------------------------------------

  function onStropheConnectionEvent(status, error) {
    Vivox5.trace();

    switch (status) {
      case Strophe.Status.CONNECTING: log('XMPP Client is connecting'); break;
      case Strophe.Status.DISCONNECTING: log('XMPP Client is disconnecting'); break;
      case Strophe.Status.AUTHENTICATING: log('XMPP Client is authenticating'); break;
      case Strophe.Status.CONNFAIL: {
        log('XMPP Client connection failure');
        //onConnFailureInternal(); // do nothing here, this is handled in DISCONNECTED event handler which occurs right after this event
        break;
      }
      case Strophe.Status.DISCONNECTED: {
        log('XMPP Client is disconnected');
        onDisconnectedInternal();
        break;
      }
      case Strophe.Status.AUTHFAIL: {
        log('XMPP Client authentication failure');
        state = Vivox5.LoginSession.LOGINSTATE.FINISHED;
        stropheConnection.disconnect();
        break;
      }
      case Strophe.Status.ERROR: {
        log('XMPP Client error: ' + error);
        state = Vivox5.LoginSession.LOGINSTATE.FINISHED;
        stropheConnection.disconnect();
        break;
      }
      case Strophe.Status.CONNECTED: {
        log('XMPP Client is connected');
        onStropheConnected();
        break;
      }
      default: {
        var logString = 'onStropheConnectionEvent unknown status = ' + status + ', error = ' + error;
        log(logString);
        break;
      }
    }
  }

  // -------------------------------------------------------------------------

  function performStropheConnection() {
    Vivox5.trace();

    if (!uriWsXmpp) {
      return false;
    }

    log('Connecting and logging in as ' + accountJid + ' to XMPP server at ' + uriWsXmpp);

    // Setup
    stropheConnection = new Strophe.Connection(uriWsXmpp);
    stropheConnection.rawInput = onStropheRawInput;
    stropheConnection.rawOutput = onStropheRawOutput;
    stropheConnection.ping.addPingHandler(pingHandler);

    // Initiate connection
    stropheConnection.connect(accountJid, loginAccessToken, onStropheConnectionEvent);

    return true;
  }

  var pingHandler = function (ping) {
    stropheConnection.ping.pong(ping);
    return true;
  }

  // -------------------------------------------------------------------------

  function performPrelogin() {
    Vivox5.trace();
    log('Performing prelogin procedure');

    var uriPrelogin = (forceSecurePreloginURI ? 'https://' : 'http://') + loginSessionId.domain.replace(/vivox.com/, 'www.vivox.com') + '/api2/viv_get_prelogin.php';
    xhrPrelogin = new XMLHttpRequest();
    xhrPrelogin.onload = onPreloginResponse;
    xhrPrelogin.onerror = onPreloginError;
    xhrPrelogin.ontimeout = onPreloginTimeout;
    xhrPrelogin.open('POST', uriPrelogin, true);
    xhrPrelogin.send();

    return true;
  }

  // -------------------------------------------------------------------------

  function onPreloginResponse() {
    Vivox5.trace();

    if (this.status != 200) {
      log('Prelogin failed: ' + this.status + ' ' + this.statusText);
      return;
    }

    if (this.getResponseHeader('Content-Type').substr(0, 8) != 'text/xml') {
      log('Failed to parse prelogin response, not XML: ' + this.getResponseHeader('Content-Type'));
      return;
    }

    var objPrelogin = Vivox5.Xml.xmlToObj(this.responseXML);
    if (!objPrelogin) {
      log('Failed to parse prelogin XML responseas object');
      return;
    }

    if (!objPrelogin.VCConfiguration || !objPrelogin.VCConfiguration.WSXMPPServer) {
      log('Prelogin failed: VCConfiguration/WSXMPPServer parameter not found');
    }

    uriWsXmpp = objPrelogin.VCConfiguration.WSXMPPServer;
    log('Prelogin succeeded, server URI is ' + uriWsXmpp);

    releaseXhrPrelogin();

    performStropheConnection();
  }

  // -------------------------------------------------------------------------

  function onPreloginTimeout() {
    Vivox5.trace();
    log('Timeout occurred during prelogin download');
    releaseXhrPrelogin();
    onDisconnectedInternal();
  }

  // -------------------------------------------------------------------------

  function onPreloginError() {
    Vivox5.trace();
    log('An error occurred during prelogin download'); // error code is not available to this event listener
    releaseXhrPrelogin();
    onDisconnectedInternal();
  }

  // -------------------------------------------------------------------------

  function onDisconnectedInternal() {
    Vivox5.trace();

    state = Vivox5.LoginSession.LOGINSTATE.FINISHED;

    for (let channelJid in channelSessions) {
      channelSessions[channelJid].onConnectionLost();
    }

    if (cbOnDisconnected && typeof cbOnDisconnected === 'function') {
      cbOnDisconnected(self);
    }

    // Callback to the Vivox5.Client
    Vivox5.Client.onLoginSessionDisconnectedInternal(self);

    // releaseLoginSessionResources(); // don't call it here: will be called in close() called from Vivox5.Client.onLoginSessionDisconnectedInternal()
  }

  // -------------------------------------------------------------------------

  function onStropheConnected() {
    // This function is called immediately after Strophe.js is connected and authenticated.
    Vivox5.trace();

    state = Vivox5.LoginSession.LOGINSTATE.LOGGED_IN;

    addLoginSessionHandlers();

    // Retreive the Roster. This makes us an 'interested resource' on the server side. Recreate subscriptions state.
    requestInitialRoster();

    // external callback to SDK user
    if (cbOnConnected && typeof cbOnConnected === 'function') {
      cbOnConnected(self);
    }
  }

  // -------------------------------------------------------------------------

  function onConnectedFinal() {
    // This function is called after all post-connect actions (e.g. roster update) are completed
    Vivox5.trace();

    if (Vivox5.Tools.propCount(self.presenceSubscriptionsScheduled) > 0) {
      addPresenceSubscription(self.presenceSubscriptionsScheduled);
      Vivox5.Tools.clearObject(self.presenceSubscriptionsScheduled);
    }
  }

  // -------------------------------------------------------------------------

  var handOnDirectedMessage = undefined;
  var handOnDirectedMessageError = undefined;
  var handOnIncomingSubscription = undefined;
  var handOnRosterPush = undefined;

  function addLoginSessionHandlers() {
    Vivox5.trace();

    if (!stropheConnection) {
      return;
    }

    // Incoming directed messages
    handOnDirectedMessage = stropheConnection.addHandler(
      onDirectedMessage,
      Strophe.NS.CLIENT,
      'message',
      'normal', // type
      null,
      null, // from any
      null
    );

    // Errors when delivering outgoing directed messages
    handOnDirectedMessageError = stropheConnection.addHandler(
      onDirectedMessageError,
      Strophe.NS.CLIENT,
      'message',
      'error', // type
      null,
      null, // from any
      null
    );

    // Incoming presence subscriptions
    handOnIncomingSubscription = stropheConnection.addHandler(
      onIncomingSubscription,
      null, // ns none
      'presence',
      'subscribe', // type
      null,
      null, // from any
      null
    );

    // Roster pushes
    handOnRosterPush = stropheConnection.addHandler(
      onRosterPush,
      Strophe.NS.ROSTER, // ns
      'iq',
      'set', // type
      null,
      null, // from any (accepted without from or from our bare jid)
      null
    );
  }

  // -------------------------------------------------------------------------

  function removeLoginSessionHandlers() {
    Vivox5.trace();

    if (!stropheConnection) {
      return;
    }

    if (handOnDirectedMessage) {
      stropheConnection.deleteHandler(handOnDirectedMessage);
      handOnDirectedMessage = undefined;
    }

    if (handOnDirectedMessageError) {
      stropheConnection.deleteHandler(handOnDirectedMessageError);
      handOnDirectedMessageError = undefined;
    }

    if (handOnIncomingSubscription) {
      stropheConnection.deleteHandler(handOnIncomingSubscription);
      handOnIncomingSubscription = undefined;
    }

    if (handOnRosterPush) {
      stropheConnection.deleteHandler(handOnRosterPush);
      handOnRosterPush = undefined;
    }
  }

  // -------------------------------------------------------------------------

  function requestInitialRoster() {
    Vivox5.trace();

    log('Retrieving the Roster');

    var iq =
      $iq({ id: Vivox5.Tools.makeUuid(), type: 'get' })
        .c('query', { xmlns: Strophe.NS.ROSTER })
        .tree();

    stropheConnection.sendIQ(iq, onRequestInitialRosterResponse, onRequestInitialRosterError, Vivox5.Xmpp.DEFAULT_TRANSACTION_TIMEOUT);
  }

  // -------------------------------------------------------------------------

  function onRequestInitialRosterResponse(iq) {
    Vivox5.trace();

    // We got here when the response to the roster request arrived.
    // Sure that the stanza is <iq/> and the type='result' (checked in Strophe).

    // Example:
    //   <iq from='.wrtc_ff.@mg.softjoys.com' to='.wrtc_ff.@mg.softjoys.com/980C58334A97326C1496938931174817' id='d0e5df39-87ac-1ec1-d5ea-20601489bf0e' type='result' xmlns='jabber:client'>
    //     <query xmlns='jabber:iq:roster'>
    //       <item subscription='both' jid='.wrtc_win.@mg.softjoys.com'/>
    //       <item subscription='both' jid='.wrtc_mac.@mg.softjoys.com'/>
    //     </query>
    //   </iq>

    var elsQuery = iq.getElementsByTagName('query');
    if (!elsQuery || !elsQuery[0] || elsQuery[0].getAttribute('xmlns') != Strophe.NS.ROSTER) {
      log('Failed to parse roster response: element <query type=\'' + Strophe.NS.ROSTER + '\' not found');
      return;
    }

    var elsItem = elsQuery[0].getElementsByTagName('item');

    log('Roster received with ' + elsItem.length + ' items');

    for (let n = 0; n < elsItem.length; ++n) {
      updatePresenceSubscriptionByRosterItem(elsItem[n], true);
    }

    // Continue post-connect actions
    onConnectedFinal();

  }

  // -------------------------------------------------------------------------

  function onRequestInitialRosterError(iq) {
    Vivox5.trace();

    // Ignore roster retrieval errors, continue post-connect actions
    onConnectedFinal();

    return false; // remove this stanza handler
  }

  // -------------------------------------------------------------------------

  function onRosterPush(iq) {
    Vivox5.trace();

    // A receiving client MUST ignore the stanza unless it has no 'from' attribute (i.e., implicitly from the bare JID of the user's account)
    // or it has a 'from' attribute whose value matches the user's bare JID <user@domainpart>.
    if (iq.hasAttribute('from') && Strophe.getBareJidFromJid(iq.getAttribute('from')) != accountJid) {
      // Igrored roster push from an unknown/unauthorized source.
      // Don't reply anything to avoid presence leak.
      return true; // don't remove this stanza handler
    }

    // The <query/> element in a roster push MUST contain one and only one <item/> element.
    // Example:
    //   <iq from='.wrtc_igor1.@mg.softjoys.com' to='.wrtc_igor1.@mg.softjoys.com/1DFBC47F11BA0AE149694759229087' id='push24E175650D4283F8' type='set' xmlns='jabber:client'>
    //     <query xmlns='jabber:iq:roster'>
    //       <item ask='subscribe' subscription='none' jid='.wrtc_igor3.@mg.softjoys.com'/>
    //     </query>
    //   </iq>

    var elsQuery = iq.getElementsByTagName('query');
    if (!elsQuery || !elsQuery[0] || elsQuery[0].getAttribute('xmlns') != Strophe.NS.ROSTER) {
      log('Failed to parse roster push: element <query type=\'' + Strophe.NS.ROSTER + '\' not found');
      return;
    }

    var elsItem = elsQuery[0].getElementsByTagName('item');
    //log('Roster push received with ' + elsItem.length + ' items');

    for (let n = 0; n < elsItem.length; ++n) {
      updatePresenceSubscriptionByRosterItem(elsItem[n], false);
    }

    return true; // don't remove this stanza handler
  }

  // -------------------------------------------------------------------------

  function sendOwnPresence() {
    Vivox5.trace();

    if (state != Vivox5.LoginSession.LOGINSTATE.LOGGED_IN) {
      return;
    }

    log('Sending own presence update: ' + self.presence);

    var show = undefined;
    switch (self.presence.status) {
      case Vivox5.Presence.STATUS.CHAT: show = 'chat'; break;
      case Vivox5.Presence.STATUS.DONOTDISTURB: show = 'dnd'; break;
      case Vivox5.Presence.STATUS.AWAY: show = 'away'; break;
      case Vivox5.Presence.STATUS.EXTENDEDAWAY: show = 'xa'; break;
      default: show = undefined; // including AVAILABLE
    }

    var presParams = { priority: '1' };
    if (self.presence.status == Vivox5.Presence.STATUS.UNAVAILABLE) {
      presParams.type = 'unavailable';
    }

    var pres = $pres(presParams);

    if (show) {
      pres.c('show').t(show).up();
    }

    if (self.presence.message && self.presence.status != Vivox5.Presence.STATUS.UNAVAILABLE) {
      pres.c('status').t(self.presence.message);
    }

    // Send the stanza (no response required, no timeout):
    stropheConnection.send(pres.tree());
  }

  // -------------------------------------------------------------------------

  function sendDirectedMessage(to, text, subject = undefined, language = undefined, applicationStanzaNamespace = undefined, applicationStanzaBody = undefined) {
    Vivox5.trace();

    if (!text) {
      text = '';
    }

    var msgAttrs = {
      to: to.toString(),
      from: accountJid,
      type: 'normal',
      id: Vivox5.Tools.makeUuid(),
    };

    var builderMsg = $msg(msgAttrs).c('body').t(text).up();

    if (subject) {
      builderMsg.c('subject').t(subject).up();
    }

    if (applicationStanzaNamespace && applicationStanzaBody) {
      builderMsg.c('application', { xmlns: applicationStanzaNamespace }).t(applicationStanzaBody).up();
    }

    var msg = builderMsg.tree();

    // Send the stanza (no response, no timeout):
    stropheConnection.send(msg);
  }

  // -------------------------------------------------------------------------

  function onDirectedMessage(message) {
    Vivox5.trace();

    //log('Directed message received, raw input = ' + Vivox5.Xml.xmlToString(message));

    var from = message.getAttribute('from');
    if (!from) {
      log('Ignored unidentified directed message: ' + Vivox5.Xml.xmlToString(message));
      return true; // don't remove this stanza handler
    }

    var jid = Strophe.getBareJidFromJid(from);
    //var jid = from;

    var subject = undefined;
    var elsSubject = message.getElementsByTagName('subject');
    if (elsSubject && elsSubject[0] && elsSubject[0].childNodes.length >= 1 && elsSubject[0].childNodes[0].nodeType == 3 /*text*/) {
      body = elsSubject[0].childNodes[0].nodeValue;
    }

    var body = undefined;
    var elsBody = message.getElementsByTagName('body');
    if (elsBody && elsBody[0] && elsBody[0].childNodes.length >= 1 && elsBody[0].childNodes[0].nodeType == 3 /*text*/) {
      body = elsBody[0].childNodes[0].nodeValue;
    }

    var language = message.getAttribute('xml:lang');
    if (!language) {
      language = undefined;
    }

    var applicationStanzaNamespace = undefined;
    var applicationStanzaBody = undefined;
    var elsApplication = message.getElementsByTagName('application');
    if (elsApplication && elsApplication[0] && elsApplication[0].childNodes.length >= 1 && elsApplication[0].childNodes[0].nodeType == 3 /*text*/) {
      applicationStanzaNamespace = elsApplication[0].getAttribute('xmlns');
      applicationStanzaBody = elsApplication[0].childNodes[0].nodeValue;
    }

    var logString = (
      'Directed message received' +
      ': from ' + jid +
      ', subject = ' + (subject === undefined ? '<undefined>' : ('\'' + subject + '\'')) +
      ', body = ' + (body === undefined ? '<undefined>' : ('\'' + body + '\''))
    );
    log(logString);

    var sender = new Vivox5.AccountId(jid);

    if (cbOnDirectedTextMessage && typeof cbOnDirectedTextMessage === 'function') {
      var textMessage = new Vivox5.TextMessage(body, subject, sender, undefined, undefined, undefined, language, applicationStanzaNamespace, applicationStanzaBody);
      cbOnDirectedTextMessage(self, textMessage);
    }

    return true; // don't remove this stanza handler
  }

  // -------------------------------------------------------------------------

  function onDirectedMessageError(message) {
    Vivox5.trace();

    var body = undefined;
    var elsBody = message.getElementsByTagName('body');
    if (elsBody && elsBody[0] && elsBody[0].childNodes.length >= 1 && elsBody[0].childNodes[0].nodeType == 3 /*text*/) {
      body = elsBody[0].childNodes[0].nodeValue;
    }

    Vivox5.logXmppError(logSubsystemId, message, 'Error delivering message \'' + body + '\'');

    return true; // don't remove this stanza handler
  }

  // -------------------------------------------------------------------------

  function onIncomingSubscription(pres) {
    Vivox5.trace();

    // Handle incoming subscription request. Must have from attribute and type='subscribe' (type is checked in handler setup)
    // Example:
    // <presence from='.sa_igor1.@mg.softjoys.com' to='.sa_igor2.@mg.softjoys.com/B55E258C5A4094DA1496155587661897' xml:lang='en' type='subscribe'/>

    var from = pres.getAttribute('from');
    if (!from) {
      log('Ignored unidentified incoming subscription request: ' + Vivox5.Xml.xmlToString(pres));
      return true; // don't remove this stanza handler
    }

    var jid = Strophe.getBareJidFromJid(from);

    var accept = (subscrMode == Vivox5.LoginSession.SUBSCRIPTIONMODE.ACCEPT && !self.blockedSubscriptions.has(jid)) || self.allowedSubscriptions.has(jid);
    var block = (subscrMode == Vivox5.LoginSession.SUBSCRIPTIONMODE.BLOCK && !self.allowedSubscriptions.has(jid)) || self.blockedSubscriptions.has(jid);

    if (accept && block) {
      accept = false;
    }
    // else if (!accept && !block) means DEFER

    log('Incoming subscription request from ' + jid + (accept ? ' automatically accepted' : block ? ' automatically blocked' : ' deferred'));

    if (accept) {
      acceptIncomingSubscription(jid);
    }
    else if (block) {
      denyIncomingSubscription(jid)
    }
    else { // i.e. deferred
      self.incomingSubscriptionRequests.add(jid);
    }

    if (!block) { // i.e. accepted and deferred
      if (cbOnIncomingSubscriptionRequest && typeof cbOnIncomingSubscriptionRequest === 'function') { // function(loginSession, accountId, autoAccepted)
        var subscriberAccountId = new Vivox5.AccountId(jid);
        cbOnIncomingSubscriptionRequest(self, subscriberAccountId, accept);
      }
    }

    return true; // don't remove this stanza handler
  }

  // -------------------------------------------------------------------------

  function acceptIncomingSubscription(jid) {
    Vivox5.trace();

    if (self.incomingSubscriptionRequests.has(jid)) {
      self.incomingSubscriptionRequests.delete(jid);
    }

    // Send XMPP presence subscribed (no response, no timeout handlers)
    var pres = $pres({ to: jid, type: 'subscribed', id: Vivox5.Tools.makeUuid() });
    stropheConnection.send(pres.tree());

    log(jid + ' subscribed to our presence');
  }

  // -------------------------------------------------------------------------

  function denyIncomingSubscription(jid) {
    Vivox5.trace();

    if (self.incomingSubscriptionRequests.has(jid)) {
      self.incomingSubscriptionRequests.delete(jid);
    }

    // Send XMPP presence unsubscribed (no response, no timeout handlers)
    var pres = $pres({ to: jid, type: 'unsubscribed', id: Vivox5.Tools.makeUuid() });
    stropheConnection.send(pres.tree());

    log(jid + ' NOT subscribed to our presence');
  }

  // -------------------------------------------------------------------------

  function addAllowedSubscription(accountIds) {
    var funcName = 'Vivox5.LoginSession.addAllowedSubscription()';
    Vivox5.trace();

    return Vivox5.Tools.callFunctionForSetOrObject(accountIds, Vivox5.AccountId, function (accountId) {
      var jid = accountId.toString();

      if (self.allowedSubscriptions.has(jid)) {
        return true; // already allowed
      }

      self.allowedSubscriptions.add(jid);
      log('Added to allowed subscriptions list: ' + jid);

      if (self.incomingSubscriptionRequests.has(jid) && state == Vivox5.LoginSession.LOGINSTATE.LOGGED_IN) {
        acceptIncomingSubscription(jid);
      }

      return true;
    },
      'accountId', 'Array or Set or Map of Vivox5.AccountId, or plain Vivox5.AccountId', funcName, logSubsystemId);
  }

  // -------------------------------------------------------------------------

  function removeAllowedSubscription(accountIds) {
    var funcName = 'Vivox5.LoginSession.removeAllowedSubscription()';
    Vivox5.trace();

    return Vivox5.Tools.callFunctionForSetOrObject(accountIds, Vivox5.AccountId, function (accountId) {
      var jid = accountId.toString();

      if (!self.allowedSubscriptions.has(jid)) {
        return true; // already removed or never added
      }

      self.allowedSubscriptions.delete(jid);
      log('Removed from allowed subscriptions list: ' + jid);

      if (self.incomingSubscriptionRequests.has(jid) && state == Vivox5.LoginSession.LOGINSTATE.LOGGED_IN) {
        denyIncomingSubscription(jid); // remove from incomingSubscriptionRequests and send XMPP presence unsubscribed
      }

      return true;
    },
      'accountId', 'Array or Set or Map of Vivox5.AccountId, or plain Vivox5.AccountId', funcName, logSubsystemId);
  }

  // -------------------------------------------------------------------------

  function addBlockedSubscription(accountIds) {
    var funcName = 'Vivox5.LoginSession.addBlockedSubscription()';
    Vivox5.trace();

    return Vivox5.Tools.callFunctionForSetOrObject(accountIds, Vivox5.AccountId, function (accountId) {
      var jid = accountId.toString();

      if (self.blockedSubscriptions.has(jid)) {
        return true; // already blocked
      }

      self.blockedSubscriptions.add(jid);
      log('Added to blocked subscriptions list: ' + jid);

      if (self.incomingSubscriptionRequests.has(jid) && state == Vivox5.LoginSession.LOGINSTATE.LOGGED_IN) {
        denyIncomingSubscription(jid); // remove from incomingSubscriptionRequests and send XMPP presence unsubscribed
      }

      return true;
    },
      'accountId', 'Array or Set or Map of Vivox5.AccountId, or plain Vivox5.AccountId', funcName, logSubsystemId);
  }

  // -------------------------------------------------------------------------

  function removeBlockedSubscription(accountIds) {
    var funcName = 'Vivox5.LoginSession.removeBlockedSubscription()';
    Vivox5.trace();

    return Vivox5.Tools.callFunctionForSetOrObject(accountIds, Vivox5.AccountId, function (accountId) {
      var jid = accountId.toString();

      if (!self.blockedSubscriptions.has(jid)) {
        return true; // already removed or never added
      }

      self.blockedSubscriptions.delete(jid);
      log('Removed from blocked subscriptions list: ' + jid);

      // Don't send anything here.
      return true;
    },
      'accountId', 'Array or Set or Map of Vivox5.AccountId, or plain Vivox5.AccountId', funcName, logSubsystemId);
  }

  // -------------------------------------------------------------------------

  function addPresenceSubscription(accountIds) {
    var funcName = 'Vivox5.LoginSession.addPresenceSubscription()';
    Vivox5.trace();

    return Vivox5.Tools.callFunctionForSetOrObject(accountIds, Vivox5.AccountId, function (accountId) {
      var jid = accountId.toString();

      if (state != Vivox5.LoginSession.LOGINSTATE.LOGGED_IN) {
        self.presenceSubscriptionsScheduled.add(jid);
        return true;
      }

      else {
        var presenceSubscription = undefined;
        if (self.presenceSubscriptions.has(jid)) {
          presenceSubscription = self.presenceSubscriptions.get(jid);
        }
        else {
          presenceSubscription = new Vivox5.PresenceSubscription(self, accountId, Vivox5.PresenceSubscription.SUBSTATE.NONE);
          self.presenceSubscriptions.set(jid, presenceSubscription);
          presenceSubscription.onSubscribed = onPresSubSubscribed;
          presenceSubscription.onUnsubscribed = onPresSubUnsubscribed;
          presenceSubscription.onBuddyPresence = onPresSubBuddyPresence;
          presenceSubscription.subscribe();

          if (cbOnBuddyAdded && typeof cbOnBuddyAdded === 'function') {
            cbOnBuddyAdded(self, accountId);
          }
        }

        return true;
      }
    },
      'accountId', 'Array or Set or Map of Vivox5.AccountId, or plain Vivox5.AccountId', funcName, logSubsystemId);
  }

  // -------------------------------------------------------------------------

  function removePresenceSubscription(accountIds) {
    var funcName = 'Vivox5.LoginSession.removePresenceSubscription()';
    Vivox5.trace();

    return Vivox5.Tools.callFunctionForSetOrObject(accountIds, Vivox5.AccountId, function (accountId) {
      var jid = accountId.toString();

      if (state != Vivox5.LoginSession.LOGINSTATE.LOGGED_IN) {
        if (self.presenceSubscriptionsScheduled.has(jid)) {
          self.presenceSubscriptionsScheduled.delete(jid);
          return true;
        }
        return false; // not found
      }

      else {
        if (!self.presenceSubscriptions.has(jid)) {
          return false; // not found
        }

        var presenceSubscription = self.presenceSubscriptions.get(jid);
        self.presenceSubscriptions.delete(jid);
        presenceSubscription.unsubscribe();
        presenceSubscription.onSubscribed = undefined;
        presenceSubscription.onUnsubscribed = undefined;
        presenceSubscription.onBuddyPresence = undefined;
        presenceSubscription = undefined;
        return true;
      }
    },
      'accountId', 'Array or Set or Map of Vivox5.AccountId, or plain Vivox5.AccountId', funcName, logSubsystemId);
  }

  // -------------------------------------------------------------------------

  function updatePresenceSubscriptionByRosterItem(elItem, forceSubscribe = false) {
    Vivox5.trace();

    // Examples:
    //   Ignore:         <item subscription='none'   jid='.wrtc_user1.@mg.softjoys.com'/>
    //   Ignore:         <item subscription='from'   jid='.wrtc_user2.@mg.softjoys.com'/>
    //   Create ready:   <item subscription='to'     jid='.wrtc_user3.@mg.softjoys.com'/>
    //   Create ready:   <item subscription='both'   jid='.wrtc_user4.@mg.softjoys.com'/>
    //   Create pending: <item ask='subscribe' subscription='none' jid='.wrtc_user5.@mg.softjoys.com'/>
    //   Create pending: <item ask='subscribe' subscription='from' jid='.wrtc_user6.@mg.softjoys.com'/>
    //   Remove:         <item subscription='remove' jid='.wrtc_user7.@mg.softjoys.com'/>

    if (!elItem.hasAttribute('jid') || !elItem.hasAttribute('subscription')) {
      // Validation failed. Silently ignore.
      return false; // PresenceSubscription NOT created
    }

    var jid = elItem.getAttribute('jid');
    var attrSubscription = elItem.getAttribute('subscription');
    var isSubscribedTo = (attrSubscription == 'to' || attrSubscription == 'both');
    var isSubscribedFrom = (attrSubscription == 'from' || attrSubscription == 'both');
    var isPendingOut = !isSubscribedTo && (elItem.hasAttribute('ask') && elItem.getAttribute('ask') == 'subscribe');
    var isRemoval = (attrSubscription == 'remove');

    if (isRemoval) {
      // Remove presence subscription, don't sent anything: use .remove instead of .unsubscribe
      if (self.presenceSubscriptions.has(jid)) {
        log('Removing presenceSubscription to ' + jid + ' by roster push');
        var presenceSubscription = presenceSubscriptions.get(jid);
        self.presenceSubscriptions.delete(jid);
        presenceSubscription.remove();
        presenceSubscription.close();
        presenceSubscription = undefined;
        return true;
      }
      // else do nothing
    }

    else if (!isSubscribedTo && !isPendingOut) {
      // Our account is not subscribed to this buddy. Silently ignore.
      return false; // PresenceSubscription NOT created
    }

    else if (isSubscribedTo || isPendingOut) {
      if (self.presenceSubscriptions.has(jid)) {
        // Already exists, do nothing
      }
      else { // i.e. does not exist
        var initialState = isPendingOut ? Vivox5.PresenceSubscription.SUBSTATE.SUBSCRIBING : Vivox5.PresenceSubscription.SUBSTATE.SUBSCRIBED;
        log('Creating presenceSubscription to ' + jid + ' by roster item (isPendingOut = ' + isPendingOut + ', initialState = ' + initialState + ')');

        var subscribedAccount = new Vivox5.AccountId(jid);
        var presenceSubscription = new Vivox5.PresenceSubscription(self, subscribedAccount, initialState);
        self.presenceSubscriptions.set(jid, presenceSubscription);
        presenceSubscription.onSubscribed = onPresSubSubscribed;
        presenceSubscription.onUnsubscribed = onPresSubUnsubscribed;
        presenceSubscription.onBuddyPresence = onPresSubBuddyPresence;
        presenceSubscription.subscribe(forceSubscribe);

        if (cbOnBuddyAdded && typeof cbOnBuddyAdded === 'function') {
          cbOnBuddyAdded(self, subscribedAccount);
        }
      }

      return true; // PresenceSubscription created or already exists
    }

    else {
      // Unknown state, ignore
      return false;
    }
  }

  // -------------------------------------------------------------------------

  function onPresSubSubscribed(presenceSubscription) {
    Vivox5.trace();

    // Don't call the cbOnBuddyAdded callback here, it was called on creation. See WRT-65 for details.
  }

  // -------------------------------------------------------------------------

  function onPresSubUnsubscribed(presenceSubscription) {
    Vivox5.trace();

    var subscribedAccount = presenceSubscription.subscribedAccount;
    var jidBuddy = subscribedAccount.toString();

    if (self.presenceSubscriptions.has(jidBuddy)) {
      self.presenceSubscriptions.delete(jidBuddy);
    }

    if (cbOnBuddyRemoved && typeof cbOnBuddyRemoved === 'function') {
      cbOnBuddyRemoved(self, subscribedAccount);
    }
  }

  // -------------------------------------------------------------------------

  function onPresSubBuddyPresence(presenceSubscription) {
    Vivox5.trace();

    if (cbOnBuddyPresence && typeof cbOnBuddyPresence === 'function') {
      cbOnBuddyPresence(self, presenceSubscription.subscribedAccount, presenceSubscription.locations);
    }
  }

  // -------------------------------------------------------------------------

  function releaseLoginSessionResources() {
    Vivox5.trace();

    releasePresence();
    releasePresenceSubscriptions();
    releaseChannelSessions();
    releaseStropheConnection();
    releaseXhrPrelogin();
    releaseCallbacks();
  }

  // -------------------------------------------------------------------------

  function releaseStropheConnection() {
    Vivox5.trace();

    if (stropheConnection) {
      removeLoginSessionHandlers();
      stropheConnection.rawInput = undefined;
      stropheConnection.rawOutput = undefined;

      if (
        state == Vivox5.LoginSession.LOGINSTATE.LOGGING_IN ||
        state == Vivox5.LoginSession.LOGINSTATE.LOGGED_IN
      ) {
        stropheConnection.disconnect();
      }

      state = Vivox5.LoginSession.LOGINSTATE.FINISHED;
      stropheConnection.reset();
      stropheConnection = null;
    }
  }

  // -------------------------------------------------------------------------

  function releaseXhrPrelogin() {
    Vivox5.trace();

    if (xhrPrelogin) {
      xhrPrelogin.onload = undefined;
      xhrPrelogin.onerror = undefined;
      xhrPrelogin.ontimeout = undefined;
      xhrPrelogin.abort();
      xhrPrelogin = null;
    }
  }

  // -------------------------------------------------------------------------

  function releasePresence() {
    Vivox5.trace();
    presence = null;
  }

  // -------------------------------------------------------------------------

  function releasePresenceSubscriptions() {
    Vivox5.trace();

    for (let jid of self.presenceSubscriptions.keys()) {
      self.presenceSubscriptions.get(jid).close();
      self.presenceSubscriptions.delete(jid);
    }
  }

  // -------------------------------------------------------------------------

  function releaseChannelSessions() {
    Vivox5.trace();

    for (let channelJid in channelSessions) {
      channelSessions[channelJid].close();
      delete channelSessions[channelJid];
    }
  }

  // -------------------------------------------------------------------------

  function releaseCallbacks() {
    Vivox5.trace();
    cbOnConnected = undefined;
    cbOnDisconnected = undefined;
    cbOnChannelSessionAdded = undefined;
    cbOnChannelSessionRemoved = undefined;
    cbOnDirectedTextMessage = undefined;
    cbOnIncomingSubscriptionRequest = undefined;
    cbOnBuddyPresence = undefined;
    cbOnBuddyAdded = undefined;
    cbOnBuddyRemoved = undefined;
  }

  // -------------------------------------------------------------------------
};

// ===========================================================================================

/**
 * Enum of constants used to reflect the {@link Vivox5.LoginSession} state
 * @typedef
 * @property LOGGED_OUT - Login Session is logged out and ready to {@link Vivox5.LoginSession#login}
 * @property PRELOGIN - Login Session is performing the prelogin procedure (retrieving configuration parameters from a Vivox provisioning server)
 * @property LOGGING_IN - Login Session is in the process of logging in
 * @property LOGGED_IN - Login Session is logged in
 * @property LOGGING_OUT - Login Session is in the process of logging out
 * @property FINISHED - Login Session finished the process of logging out and will be removed soon
 */
Vivox5.LoginSession.LOGINSTATE = Object.create(Object.prototype, {
  LOGGED_OUT: { value: 0, writable: false, configurable: false, enumerable: true, },
  PRELOGIN: { value: 1, writable: false, configurable: false, enumerable: true, },
  LOGGING_IN: { value: 2, writable: false, configurable: false, enumerable: true, },
  LOGGED_IN: { value: 3, writable: false, configurable: false, enumerable: true, },
  LOGGING_OUT: { value: 4, writable: false, configurable: false, enumerable: true, },
  FINISHED: { value: 5, writable: false, configurable: false, enumerable: true, },
});

Object.defineProperty(Vivox5.LoginSession, 'LOGINSTATE', { writable: false, configurable: false, enumerable: true });

// -------------------------------------------------------------------------

/**
 * How to handle incoming subscriptions (enum)
 * @typedef
 * @property ACCEPT - Automatically accept all incoming subscription requests
 * @property BLOCK - Automatically reject all incoming subscription requests
 * @property DEFER - Defer incoming subscription request handling to the application (the LoginSession will invoke the {@link Vivox5.LoginSession#onIncomingSubscriptionRequest} callback)
 */
Vivox5.LoginSession.SUBSCRIPTIONMODE = Object.create(Object.prototype, {
  ACCEPT: { value: 0, writable: false, configurable: false, enumerable: true, },
  BLOCK: { value: 1, writable: false, configurable: false, enumerable: true, },
  DEFER: { value: 2, writable: false, configurable: false, enumerable: true, },
});

Object.defineProperty(Vivox5.LoginSession, 'SUBSCRIPTIONMODE', { writable: false, configurable: false, enumerable: true });

// ===========================================================================================

Object.defineProperty(Vivox5, 'LoginSession', { writable: false, configurable: false, enumerable: true });

// *****************************************************************************************************************************

Vivox5.PresenceSubscription = function _Vivox5_PresenceSubscription(loginSession, subscribedAccount, initialState = Vivox5.PresenceSubscription.SUBSTATE.NONE) {
  Vivox5.trace();

  // Internal identification
  var self = this; // store 'this' in the closure for private functions
  this.internalId = Vivox5.Tools.generateInternalId();
  Object.defineProperty(this, 'internalId', { writable: false, configurable: false, enumerable: false });
  var logSubsystemId = 'PS-' + ((loginSession instanceof Vivox5.LoginSession && 'internalId' in loginSession) ? loginSession.internalId : '?') + '-' + this.internalId; // used primarily for logging
  var className = 'Vivox5.PresenceSubscription'; // used primarily for error reporting

  // Check parameters
  if (arguments.length < 2 || arguments.length > 3) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, className, logSubsystemId);
  if (!(loginSession instanceof Vivox5.LoginSession)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'loginSession', 'Vivox5.LoginSession', className, logSubsystemId);
  if (!(subscribedAccount instanceof Vivox5.AccountId)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'subscribedAccount', 'Vivox5.AccountId', className, logSubsystemId);
  if (subscribedAccount.isEmpty()) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'subscribedAccount', className, logSubsystemId);
  if (typeof initialState !== 'number') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'initialState', 'number', className, logSubsystemId);

  // ===========================================================================================
  // Vivox5.PresenceSubscription public properties

  // Parent LoginSession
  Object.defineProperty(this, 'loginSession', { configurable: false, enumerable: true, get: () => loginSession });

  // AccountId of the subscribed buddy
  this.subscribedAccount = subscribedAccount;
  Object.defineProperty(this, 'subscribedAccount', { writable: false, configurable: false, enumerable: true });

  // Subscription state
  var substate = initialState;
  Object.defineProperty(this, 'substate', { configurable: false, enumerable: true, get: () => substate });

  // All presence locations of this buddy
  var locations = new Vivox5.PresenceLocations(); // Vivox5.PresenceLocations = map resource -> Vivox5.Presence
  Object.defineProperty(this, 'locations', { configurable: false, enumerable: true, get: () => locations });

  // ===========================================================================================
  // Callback functions (to be set from outside)
  var cbOnSubscribed = undefined; // function(self)
  var cbOnUnsubscribed = undefined; // function(self)
  var cbOnBuddyPresence = undefined; // function(self)

  function validateCB(cb, name) { if (typeof cb !== 'function' && cb !== undefined) throw new Vivox5.Error(Vivox5.Error.CODE.EPROPTYPE, name, 'function', className, logSubsystemId); }

  Object.defineProperties(this, {
    onSubscribed: { configurable: false, enumerable: true, get: () => cbOnSubscribed, set: (cb) => { validateCB(cb, 'onSubscribed'); cbOnSubscribed = cb; } },
    onUnsubscribed: { configurable: false, enumerable: true, get: () => cbOnUnsubscribed, set: (cb) => { validateCB(cb, 'onUnsubscribed'); cbOnUnsubscribed = cb; } },
    onBuddyPresence: { configurable: false, enumerable: true, get: () => cbOnBuddyPresence, set: (cb) => { validateCB(cb, 'onBuddyPresence'); cbOnBuddyPresence = cb; } },
  });

  // ===========================================================================================
  // Private data
  var jidBuddy = subscribedAccount.toString(); // Expand to 'user@realm' string just once, because it is used everywhere as a string, not an object
  var stropheConnection = loginSession._stropheConnection; // Strophe.JS Connection object

  // ===========================================================================================
  // Init
  addPresenceSubscriptionHandlers();

  // ===========================================================================================
  // Vivox5.PresenceSubscription public methods
  // !!! Important: when adding or removing public methods, don't forget to update the Object.defineProperties table located below all public methods

  this.subscribe = function _Vivox5_PresenceSubscription_subscribe(forceSubscribe = false) {
    var funcName = 'Vivox5.PresenceSubscription.subscribe()';
    Vivox5.trace();

    // Check parameters
    if (arguments.length > 1) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);
    if (typeof forceSubscribe !== 'boolean' && forceSubscribe !== undefined) {
      throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'forceSubscribe', 'boolean or undefined', funcName, logSubsystemId);
    }

    // Check whether the operation is already in progress or completed
    if (
      substate == Vivox5.PresenceSubscription.SUBSTATE.SUBSCRIBING ||
      substate == Vivox5.PresenceSubscription.SUBSTATE.SUBSCRIBED
    ) {
      if (!forceSubscribe) {
        return true;
      }
    }

    // Initiate subscription
    log('Subscribing to buddy ' + jidBuddy);

    // Send XMPP presence subscribe (no response, no timeout handlers)
    var pres = $pres({ to: jidBuddy, type: 'subscribe', id: Vivox5.Tools.makeUuid() });
    stropheConnection.send(pres.tree());

    substate = Vivox5.PresenceSubscription.SUBSTATE.SUBSCRIBING;
    return true;
  };

  // -------------------------------------------------------------------------

  this.unsubscribe = function _Vivox5_PresenceSubscription_unsubscribe() {
    var funcName = 'Vivox5.PresenceSubscription.unsubscribe()';
    Vivox5.trace();

    // Check parameters
    if (arguments.length != 0) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);

    // Check whether the operation is already completed
    if (substate == Vivox5.PresenceSubscription.SUBSTATE.NONE) {
      return true;
    }

    removePresenceSubscriptionHandlers();

    log('Unsubscribing from buddy ' + jidBuddy);

    // Send XMPP presence unsubscribe (no response, no timeout handlers)
    var pres = $pres({ to: jidBuddy, type: 'unsubscribe', id: Vivox5.Tools.makeUuid() });
    stropheConnection.send(pres.tree());

    // And unsubscribe him from us
    var pres = $pres({ to: jidBuddy, type: 'unsubscribed', id: Vivox5.Tools.makeUuid() });
    stropheConnection.send(pres.tree());

    // And remove from the roster (ignore response)
    var iq =
      $iq({ type: 'set', id: Vivox5.Tools.makeUuid() })
        .c('query', { xmlns: Strophe.NS.ROSTER })
        .c('item', { jid: jidBuddy, subscription: 'remove' });
    stropheConnection.send(iq.tree());

    afterUnsubscribed();

    return true;
  };

  // -------------------------------------------------------------------------

  this.remove = function _Vivox5_PresenceSubscription_remove() {
    var funcName = 'Vivox5.PresenceSubscription.remove()';
    Vivox5.trace();
    // Silently remove the subscription by an external event (e.g. by roster update), don't send anything.

    // Check parameters
    if (arguments.length != 0) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);

    // Check whether the operation is already completed
    if (substate == Vivox5.PresenceSubscription.SUBSTATE.NONE) {
      return true;
    }

    // Remove subscription
    removePresenceSubscriptionHandlers();
    log('Removed subscription to buddy ' + jidBuddy);
    afterUnsubscribed();
    return true;
  };

  // -------------------------------------------------------------------------

  // NB: internal function it MUST NOT be called by the SDK users. Must be declared as {enumerable: false} below
  this.close = function _Vivox5_PresenceSubscription_close() {
    Vivox5.trace();

    if (!self) {
      // already closed
      return;
    }

    releaseSubscriptionResources();

    delete self['presence'];
    self = undefined;
  };

  // -------------------------------------------------------------------------

  this.toString = function _Vivox5_PresenceSubscription_toString() {
    // just for debugging
    return '[object Vivox5.PresenceSubscription(' + loginSession.loginSessionId.toString() + ' -> ' + subscribedAccount.toString() + ')]';
  };

  // ===========================================================================================
  // Vivox5.PresenceSubscription public methods configuration

  Object.defineProperties(this, {
    subscribe: { writable: false, configurable: false, enumerable: true },
    unsubscribe: { writable: false, configurable: false, enumerable: true },
    remove: { writable: false, configurable: false, enumerable: false },
    close: { writable: false, configurable: false, enumerable: false },
    toString: { writable: false, configurable: false, enumerable: true },
  });

  // ===========================================================================================
  // Vivox5.PresenceSubscription private functions

  function log(msg) {
    Vivox5.log(logSubsystemId, msg);
  }

  // -------------------------------------------------------------------------

  var handOnSubscribed = undefined;
  var handOnUnsubscribed = undefined;
  var handOnPresence = undefined;

  function addPresenceSubscriptionHandlers() {
    Vivox5.trace();

    handOnSubscribed = stropheConnection.addHandler(
      onPresSubscribed,
      null, // ns none
      'presence',
      'subscribed', // type
      null,
      jidBuddy,
      { matchBareFromJid: true }
    );

    handOnUnsubscribed = stropheConnection.addHandler(
      onPresUnsubscribed,
      null, // ns none
      'presence',
      'unsubscribed', // type
      null,
      jidBuddy,
      { matchBareFromJid: true }
    );

    handOnPresence = stropheConnection.addHandler(
      onPresence,
      null, // ns none
      'presence',
      null, // any type (will be checked inside the handler: only without type or with type='unavailable')
      null,
      jidBuddy,
      { matchBareFromJid: true }
    );
  }

  // -------------------------------------------------------------------------

  function removePresenceSubscriptionHandlers() {
    Vivox5.trace();

    if (handOnSubscribed) {
      stropheConnection.deleteHandler(handOnSubscribed);
      handOnSubscribed = undefined;
    }

    if (handOnUnsubscribed) {
      stropheConnection.deleteHandler(handOnUnsubscribed);
      handOnUnsubscribed = undefined;
    }

    if (handOnPresence) {
      stropheConnection.deleteHandler(handOnPresence);
      handOnPresence = undefined;
    }
  }

  // -------------------------------------------------------------------------

  function onPresSubscribed(pres) {
    Vivox5.trace();

    substate = Vivox5.PresenceSubscription.SUBSTATE.SUBSCRIBED;
    log('Subscribed to buddy ' + jidBuddy);

    if (cbOnSubscribed && typeof cbOnSubscribed === 'function') {
      cbOnSubscribed(self);
    }

    return true; // don't remove this stanza handler
  }

  // -------------------------------------------------------------------------

  function onPresUnsubscribed(pres) {
    Vivox5.trace();

    removePresenceSubscriptionHandlers();
    log('Unsubscribed from buddy ' + jidBuddy);
    afterUnsubscribed();

    return false; // remove this stanza handler
  }

  // -------------------------------------------------------------------------

  function afterUnsubscribed() {
    Vivox5.trace();

    substate = Vivox5.PresenceSubscription.SUBSTATE.NONE;
    Vivox5.Tools.clearObject(locations);

    if (cbOnUnsubscribed && typeof cbOnUnsubscribed === 'function') {
      cbOnUnsubscribed(self);
    }
  }

  // -------------------------------------------------------------------------

  function releaseSubscriptionResources() {
    Vivox5.trace();

    Vivox5.Tools.clearObject(locations);
    locations = undefined;

    cbOnSubscribed = undefined;
    cbOnUnsubscribed = undefined;
    cbOnBuddyPresence = undefined;

    removePresenceSubscriptionHandlers();
    stropheConnection = undefined;
  }

  // -------------------------------------------------------------------------

  function onPresence(pres) {
    if (pres.hasAttribute('type') && pres.getAttribute('type') != 'unavailable') {
      // Participant presence arrives without type or with type='unavailable'.
      // Other types are handled in other places.
      return true; // don't remove this stanza handler
    }

    Vivox5.trace();

    //log('A buddy presence changed, raw input = ' + Vivox5.Xml.xmlToString(pres));
    // Examples:
    //   <presence from='.sa_igor1.@mg.softjoys.com/8C41668825C2FDAF1496340852507971' to='.sa_igor2.@mg.softjoys.com/272933B4FEDF73A01496340855751049' xml:lang='en' priority='1'>
    //     <show>chat</show>
    //     <status>test pres msg</status>
    //   </presence>
    // (both <show> and <status> are optional)
    // or
    //   <presence from='.sa_igor1.@mg.softjoys.com/8C41668825C2FDAF1496340852507971' to='.sa_igor2.@mg.softjoys.com/272933B4FEDF73A01496340855751049' type='unavailable'/>

    if (substate == Vivox5.PresenceSubscription.SUBSTATE.SUBSCRIBING) {
      substate = Vivox5.PresenceSubscription.SUBSTATE.SUBSCRIBED;
      log('Subscription state of ' + jidBuddy + ' unexpectedly changed from SUBSCRIBING to SUBSCRIBED due to presence arrival');
    }

    var status = Vivox5.Presence.STATUS.AVAILABLE; // default value if cannot detect other specific
    if (pres.hasAttribute('type') && pres.getAttribute('type') == 'unavailable') {
      status = Vivox5.Presence.STATUS.UNAVAILABLE;
    }
    else { // i.e. without type
      var show = null;
      var elsShow = pres.getElementsByTagName('show');
      if (elsShow && elsShow[0] && elsShow[0].childNodes.length >= 1 && elsShow[0].childNodes[0].nodeType == 3 /*text*/) {
        show = elsShow[0].childNodes[0].nodeValue;
      }

      switch (show) {
        case 'chat': status = Vivox5.Presence.STATUS.CHAT; break;
        case 'dnd': status = Vivox5.Presence.STATUS.DONOTDISTURB; break;
        case 'away': status = Vivox5.Presence.STATUS.AWAY; break;
        case 'xa': status = Vivox5.Presence.STATUS.EXTENDEDAWAY; break;
        default: status = Vivox5.Presence.STATUS.AVAILABLE; break;
      }
    }

    var message = undefined;
    var elsStatus = pres.getElementsByTagName('status');
    if (elsStatus && elsStatus[0] && elsStatus[0].childNodes.length >= 1 && elsStatus[0].childNodes[0].nodeType == 3 /*text*/) {
      message = elsStatus[0].childNodes[0].nodeValue;
    }

    var from = pres.getAttribute('from');
    //if (!from) {
    //  log('Internal error: no from attribute in presence for ' + jidBuddy + ': ' + Vivox5.Xml.xmlToString(pres));
    //  return true; // don't remove this stanza handler
    //}
    // the 'from' attribute is always here: this function was called due to 'from' existence
    var resource = Strophe.getResourceFromJid(from);

    this.locations[resource] = new Vivox5.Presence(status, message);
    log('Presence for buddy ' + jidBuddy + ' (resource ' + resource + ') updated to ' + this.locations[resource]);

    // callback to LoginSession
    if (cbOnBuddyPresence && typeof cbOnBuddyPresence === 'function') {
      cbOnBuddyPresence(self);
    }

    return true; // don't remove this stanza handler
  }

  // -------------------------------------------------------------------------
};

// ===========================================================================================

Vivox5.PresenceSubscription.SUBSTATE = Object.create(Object.prototype, {
  NONE: { value: 0, writable: false, configurable: false, enumerable: true },
  SUBSCRIBING: { value: 1, writable: false, configurable: false, enumerable: true },
  SUBSCRIBED: { value: 2, writable: false, configurable: false, enumerable: true },
});

Object.defineProperty(Vivox5.PresenceSubscription, 'SUBSTATE', { writable: false, configurable: false, enumerable: true });

// ===========================================================================================

Object.defineProperty(Vivox5, 'PresenceSubscription', { writable: false, configurable: false, enumerable: true });

// *****************************************************************************************************************************

/**
 * @class
 * @classdesc A connection to a channel. The constructor must not be called directly by the application, use the {@link Vivox5.LoginSession#getChannelSession} method instead.
 * @hideconstructor
 */
Vivox5.ChannelSession = function _Vivox5_ChannelSession(channel, loginSession) {
  Vivox5.trace();

  // Internal identification
  var self = this; // store 'this' in the closure for private functions
  this.internalId = Vivox5.Tools.generateInternalId();
  Object.defineProperty(this, 'internalId', { writable: false, configurable: false, enumerable: false });
  var logSubsystemId = 'CS-' + loginSession.internalId + '-' + this.internalId; // used primarily for logging
  var className = 'Vivox5.ChannelSession'; // used primarily for error reporting

  // Check parameters
  if (arguments.length != 2) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, className, logSubsystemId);
  if (!(channel instanceof Vivox5.ChannelId)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'channel', 'Vivox5.ChannelId', className, logSubsystemId);
  if (channel.isEmpty()) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'channel', className, logSubsystemId);
  if (!(loginSession instanceof Vivox5.LoginSession)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'loginSession', 'Vivox5.LoginSession', className, logSubsystemId);

  // Check that realm and issuer in channel are equal with LoginSession's loginSessionId
  if (
    channel.domain != loginSession.loginSessionId.domain ||
    channel.issuer != loginSession.loginSessionId.issuer
  ) {
    throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'channel', className, logSubsystemId, 'domain and issuer in channel must match with domain and issuer of the LoginSession');
  }

  // ===========================================================================================
  // Vivox5.ChannelSession public properties

  /**
   * The channel session id
   * @member {Vivox5.ChannelId} Vivox5.ChannelSession#channel
   * @readonly
   */
  this.channel = channel;
  Object.defineProperty(this, 'channel', { writable: false, configurable: false, enumerable: true });

  /**
   * The login session that owns this channel session
   * @member {Vivox5.LoginSession} Vivox5.ChannelSession#parent
   * @readonly
   */
  Object.defineProperty(this, 'parent', { configurable: false, enumerable: true, get: () => loginSession });

  /**
   * The local media stream from the microphone.
   * @member {MediaStream} Vivox5.ChannelSession#streamLocal
   * @see Important: this property MUST be set by the application before calling the {@link Vivox5.ChannelSession#connect} method!
   */
  var streamLocal = undefined;
  Object.defineProperty(this, 'streamLocal', {
    configurable: false, enumerable: true,
    get: () => streamLocal,
    set: newStreamLocal => {
      if (!(newStreamLocal instanceof MediaStream)) throw new Vivox5.Error(Vivox5.Error.CODE.EPROPTYPE, 'streamLocal', 'MediaStream', className, logSubsystemId);
      streamLocal = newStreamLocal;
    },
  });

  /**
   * The state of the audio portion of this channel session (changes to this value may occur at anytime due to network or moderator events)
   * @member {Vivox5.ChannelSession.OPERSTATE} Vivox5.ChannelSession#audioState
   * @readonly
   */
  var audioState = Vivox5.ChannelSession.OPERSTATE.OFF;

  /**
   * The state of the text portion of this channel session (changes to this value may occur at anytime due to network or moderator events)
   * @member {Vivox5.ChannelSession.OPERSTATE} Vivox5.ChannelSession#textState
   * @readonly
   */
  var textState = Vivox5.ChannelSession.OPERSTATE.OFF;

  Object.defineProperties(this, {
    audioState: { configurable: false, enumerable: true, get: () => audioState },
    textState: { configurable: false, enumerable: true, get: () => textState },
  });

  /**
   * The list of participants in this channel, including the current user (object keys are internal participant ids).
   * @member {Object<string, Vivox5.Participant>} Vivox5.ChannelSession#participants
   * @readonly
   * @see Use the [onParticipantAdded]{@link Vivox5.ChannelSession#onParticipantAdded}, [onParticipantUpdated]{@link Vivox5.ChannelSession#onParticipantUpdated} and [onParticipantRemoved]{@link Vivox5.ChannelSession#onParticipantRemoved} callbacks to get notifications of participants joining, leaving, etc.
   */
  var participants = {}; // jid -> participant
  Object.defineProperty(this, 'participants', { configurable: false, enumerable: true, get: () => participants });

  // Quick access to one of the state variables
  // FIXME: replace isStarted and isEstablished with a single state variable
  /**
   * true if the channel session is already established (i.e. after the onSessionEstablished callback, and before onSessionTerminated), false otherwise
   * @member {boolean} Vivox5.ChannelSession#isEstablished
   * @readonly
   */
  Object.defineProperty(this, 'isEstablished', { configurable: false, enumerable: true, get: () => isEstablished });

  // ===========================================================================================
  // Callback functions (to be set from outside)

  // Callback to session establishment/termination

  /**
   * Callback function provided by the application, invoked when the Channel Session is established, i.e. when the current user joined to the channel.
   * @member {Vivox5.ChannelSession#callbackOnSessionEstablished} Vivox5.ChannelSession#onSessionEstablished
   * @see Note that this event usually happens before the audio and text capabilities are enabled (see {@link Vivox5.ChannelSession#onAudioStateUpdated} and {@link Vivox5.ChannelSession#onTextStateUpdated}).
   * @see Also note that the server can send participant notifications before the session is fully established and the {@link Vivox5.ChannelSession#onParticipantAdded} callback can be called before this function.
   */
  /**
   * The signature of a callback function which is invoked when the Channel Session is established, i.e. when the current user joined to the channel.
   * @callback Vivox5.ChannelSession#callbackOnSessionEstablished
   * @param {Vivox5.ChannelSession} channelSession - the Channel Session which invoked this callback function
   */
  var cbOnSessionEstablished = undefined; // function(channelSession)

  /**
   * Callback function provided by the application, invoked when the Channel Session is terminated, i.e. when the user has disconnected from the channel.
   * @member {Vivox5.ChannelSession#callbackOnSessionTerminated} Vivox5.ChannelSession#onSessionTerminated
   */
  /**
   * The signature of a callback function which is invoked when the Channel Session is terminated, i.e. when the user has disconnected from the channel.
   * @callback Vivox5.ChannelSession#callbackOnSessionTerminated
   * @param {Vivox5.ChannelSession} channelSession - the Channel Session which invoked this callback function
   */
  var cbOnSessionTerminated = undefined; // function(channelSession)

  // Callbacks to participant add/update/remove

  /**
   * Callback function provided by the application, invoked when a participant is added to this channel (including the current user, including all participants who are in the channel at the moment of the session establishment). Note that this event may happen before [onSessionEstablished]{@link Vivox5.ChannelSession#onSessionEstablished} when joining to a channel where already there are other paricipants.
   * @member {Vivox5.ChannelSession#callbackOnParticipantAdded} Vivox5.ChannelSession#onParticipantAdded
   * @see Note that this event may happen before {@link Vivox5.ChannelSession#onSessionEstablished} when joining to a channel where already there are other paricipants.
   */
  /**
   * The signature of a callback function which is invoked when a participant is added to this channel (including the current user, including all participants who are in the channel at the moment of the session establishment).
   * @callback Vivox5.ChannelSession#callbackOnParticipantAdded
   * @param {Vivox5.ChannelSession} channelSession - the Channel Session which invoked this callback function
   * @param {Vivox5.Participant} participant - the object describing participant properties and capabilities
   */
  var cbOnParticipantAdded = undefined; // function(channelSession, participant)

  /**
   * Callback function provided by the application, invoked when a channel participant information is changed (including the current user).
   * @member {Vivox5.ChannelSession#callbackOnParticipantUpdated} Vivox5.ChannelSession#onParticipantUpdated
   */
  /**
   * The signature of a callback function which is invoked when a channel participant information is changed (including the current user).
   * @callback Vivox5.ChannelSession#callbackOnParticipantUpdated
   * @param {Vivox5.ChannelSession} channelSession - the Channel Session which invoked this callback function
   * @param {Vivox5.Participant} participant - the object describing participant properties and capabilities
   */
  var cbOnParticipantUpdated = undefined; // function(channelSession, participant)

  /**
   * Callback function provided by the application, invoked when a participant is removed from this channel.
   * @member {Vivox5.ChannelSession#callbackOnParticipantRemoved} Vivox5.ChannelSession#onParticipantRemoved
   */
  /**
   * The signature of a callback function which is invoked when a participant is removed from this channel.
   * @callback Vivox5.ChannelSession#callbackOnParticipantRemoved
   * @param {Vivox5.ChannelSession} channelSession - the Channel Session which invoked this callback function
   * @param {Vivox5.Participant} participant - the object describing the participant which was ramoved from the channel
   */
  var cbOnParticipantRemoved = undefined; // function(channelSession, participant)

  // Incoming channel messages callback

  /**
   * Callback function provided by the application, invoked when a text message is received in the channel.
   * @member {Vivox5.ChannelSession#callbackOnChannelTextMessage} Vivox5.ChannelSession#onChannelTextMessage
   */
  /**
   * The signature of a callback function which is invoked when a text message is received in the channel.
   * @callback Vivox5.ChannelSession#callbackOnChannelTextMessage
   * @param {Vivox5.ChannelSession} channelSession - the Channel Session which invoked this callback function
   * @param {Vivox5.TextMessage} textMessage - the message received
   */
  var cbOnChannelTextMessage = undefined; // function(channelSession, textMessage)

  // Callbacks for session text/audio capabilities update

  /**
   * Callback function provided by the application, invoked when the state of the audio portion of this Channel Session is changed.
   * @member {Vivox5.ChannelSession#callbackOnAudioStateUpdated} Vivox5.ChannelSession#onAudioStateUpdated
   * @see The audio state is also available in the {@link Vivox5.ChannelSession#audioState} property.
   * @see Use the {@link Vivox5.ChannelSession#setAudioConnected} method to add or remove audio from the channel session.
   */
  /**
   * The signature of a callback function which is invoked when the state of the audio portion of this Channel Session is changed.
   * @callback Vivox5.ChannelSession#callbackOnAudioStateUpdated
   * @param {Vivox5.ChannelSession} channelSession - the Channel Session which invoked this callback function
   * @param {Vivox5.ChannelSession.OPERSTATE} audioState - the new audio state
   */
  var cbOnAudioStateUpdated = undefined; // function(channelSession, stateNew)

  /**
   * Callback function provided by the application, invoked when the state of the text portion of this Channel Session is changed.
   * @member {Vivox5.ChannelSession#callbackOnTextStateUpdated} Vivox5.ChannelSession#onTextStateUpdated
   * @see The text state is also available in the {@link Vivox5.ChannelSession#textState} property.
   * @see Use the {@link Vivox5.ChannelSession#setTextConnected} method to add or remove text from the channel session.
   */
  /**
   * The signature of a callback function which is invoked when the state of the text portion of this Channel Session is changed.
   * @callback Vivox5.ChannelSession#callbackOnTextStateUpdated
   * @param {Vivox5.ChannelSession} channelSession - the Channel Session which invoked this callback function
   * @param {Vivox5.ChannelSession.OPERSTATE} textState - the new audio state
   */
  var cbOnTextStateUpdated = undefined; // function(channelSession, stateNew)

  // Callback to adding media stream

  /**
   * Callback function provided by the application, invoked when a remote audio stream has arrived.
   * Important: this callback MUST be set by the application before calling the {@link Vivox5.ChannelSession#connect} method!
   * The application MUST handle this callback: the stream must be attached to an HTML audio element for playback.
   * @member {Vivox5.ChannelSession#callbackOnRemoteStream} Vivox5.ChannelSession#onRemoteStream
   * @see {@link Vivox5.ChannelSession#connect}
   */
  /**
   * @callback Vivox5.ChannelSession#callbackOnRemoteStream
   * @param {Vivox5.ChannelSession} channelSession - the Channel Session which invoked this callback function
   * @param {MediaStream} stream - remote MediaStream object
   * @param {string} idPeerConnection - an internal id of an RTCPeerConnection object created inside the Vivox Web SDK associated with this stream
   */
  var cbOnRemoteStream = undefined; // function(channelSession, stream, idRTCPC)

  function validateCB(cb, name) { if (typeof cb !== 'function' && cb !== undefined) throw new Vivox5.Error(Vivox5.Error.CODE.EPROPTYPE, name, 'function', className, logSubsystemId); }

  Object.defineProperties(this, {
    onSessionEstablished: { configurable: false, enumerable: true, get: () => cbOnSessionEstablished, set: (cb) => { validateCB(cb, 'onSessionEstablished'); cbOnSessionEstablished = cb; } },
    onSessionTerminated: { configurable: false, enumerable: true, get: () => cbOnSessionTerminated, set: (cb) => { validateCB(cb, 'onSessionTerminated'); cbOnSessionTerminated = cb; } },
    onParticipantAdded: { configurable: false, enumerable: true, get: () => cbOnParticipantAdded, set: (cb) => { validateCB(cb, 'onParticipantAdded'); cbOnParticipantAdded = cb; } },
    onParticipantUpdated: { configurable: false, enumerable: true, get: () => cbOnParticipantUpdated, set: (cb) => { validateCB(cb, 'onParticipantUpdated'); cbOnParticipantUpdated = cb; } },
    onParticipantRemoved: { configurable: false, enumerable: true, get: () => cbOnParticipantRemoved, set: (cb) => { validateCB(cb, 'onParticipantRemoved'); cbOnParticipantRemoved = cb; } },
    onChannelTextMessage: { configurable: false, enumerable: true, get: () => cbOnChannelTextMessage, set: (cb) => { validateCB(cb, 'onChannelTextMessage'); cbOnChannelTextMessage = cb; } },
    onAudioStateUpdated: { configurable: false, enumerable: true, get: () => cbOnAudioStateUpdated, set: (cb) => { validateCB(cb, 'onAudioStateUpdated'); cbOnAudioStateUpdated = cb; } },
    onTextStateUpdated: { configurable: false, enumerable: true, get: () => cbOnTextStateUpdated, set: (cb) => { validateCB(cb, 'onTextStateUpdated'); cbOnTextStateUpdated = cb; } },
    onRemoteStream: { configurable: false, enumerable: true, get: () => cbOnRemoteStream, set: (cb) => { validateCB(cb, 'onRemoteStream'); cbOnRemoteStream = cb; } },
  });

  // ===========================================================================================
  // Private data

  // Expand some Channel parameters
  var channelJid = channel.toString(); // Actually channelJid is used only in token generation (in all other places the version with 'muc' is used)
  var accountJid = loginSession.loginSessionId.toString(); // Expand to 'user@realm' string just once, because it is used everywhere as a string, not an object
  var userIdStripped = loginSession.loginSessionId.accountName.replace(/\./g, ''); // e.g. '.sa_9898eaef.' -> 'sa_9898eaef'
  var mucJid = channel.channelName + '@muc.' + channel.domain;
  var mucJidRes = mucJid + '/' + userIdStripped;

  // State and substates
  // FIXME: replace isStarted and isEstablished with the single state variable
  var isStarted = false;
  var isEstablished = false;
  var inviteInProgress = false;
  var inviteCompleted = false;
  var audioConnectScheduled = false;
  var audioDisconnectScheduled = false;
  var textConnectScheduled = false;
  var textDisconnectScheduled = false;

  // Connections with XMPP server and PP
  var stropheConnection = loginSession._stropheConnection; // Strophe.JS Connection object
  var wsUriPatchPanel = undefined;
  var wsConnectionPatchPanel = undefined;
  var isWSPPConnected = false;

  // Participants collection
  var participantsByRoomNick = {}; // in addition to participants property: roomNickname -> participant

  // Collection of RTCPeerConnection objects created in this session
  var arrRTCPC = {}; // (map idRTCPC -> RTCPeerConnection)

  // Logging
  var logPrefixSession = 'ChannelSession with ' + channel.channelName;
  var logPrefixSessionStarting = 'Starting ' + logPrefixSession;
  var logPrefixSessionRemoving = 'Removing ' + logPrefixSession;

  // ===========================================================================================
  // Vivox5.ChannelSession public methods
  // !!! Important: when adding or removing public methods, don't forget to update the Object.defineProperties table located below all public methods

  /**
   * Perform the initial connection to the channel.
   * @async
   *
   * @param {boolean} enableAudio - true to connect audio (at least one of enableAudio and enableText, or both, must be true)
   * @param {boolean} enableText - true to connect text (at least one of enableAudio and enableText, or both, must be true)
   * @param {string} connectToken - the access token granting the user access to the channel
   *
   * @returns {boolean} true on success
   *
   * @throws {Vivox5.Error} called in incorrect state (e.g. already connecting, connected, disconnecting): Vivox5.Error.CODE.ESTATE
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} enableAudio or enableText is not boolean: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} enableAudio and enableText both are false: Vivox5.Error.CODE.EINCONSISTPARAMS
   * @throws {Vivox5.Error} connectToken is not string: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} connectToken is empty: Vivox5.Error.CODE.EPARAMVALUE
   * @throws {Vivox5.Error} the application did not set the streamLocal property: Vivox5.Error.CODE.EMISSINGPROP
   * @throws {Vivox5.Error} the streamLocal property set by the application is not an instance of the MediaStream type: Vivox5.Error.CODE.EPROPTYPE
   * @throws {Vivox5.Error} the application did not set the streamLocal property: Vivox5.Error.CODE.EMISSINGPROP
   * @throws {Vivox5.Error} the streamLocal property set by the application is not an instance of the MediaStream type: Vivox5.Error.CODE.EPROPTYPE
   *
   * @see Developer of games that do not have secure communications requirements can use {@link Vivox5.ChannelSession#getConnectToken} to generate the required access token.
   */
  this.connect = function _Vivox5_ChannelSession_connect(enableAudio, enableText, connectToken) {
    var funcName = 'Vivox5.ChannelSession.connect()';
    Vivox5.trace();

    // Check state
    if (isStarted) throw new Vivox5.Error(Vivox5.Error.CODE.ESTATE, 'already started', funcName, logSubsystemId);

    // Check parameters
    if (arguments.length != 3) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);
    if (typeof enableAudio !== 'boolean') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'enableAudio', 'boolean', funcName, logSubsystemId);
    if (typeof enableText !== 'boolean') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'enableText', 'boolean', funcName, logSubsystemId);
    if (!enableAudio && !enableText) throw new Vivox5.Error(Vivox5.Error.CODE.EINCONSISTPARAMS, 'enableAudio', funcName, logSubsystemId, 'at least one of enableAudio and enableText must be true');
    if (typeof connectToken !== 'string') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'connectToken', 'string', funcName, logSubsystemId);
    if (!connectToken) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'connectToken', funcName, logSubsystemId);

    // Check properties (must be set by the caller before connect())
    if (streamLocal === undefined) throw new Vivox5.Error(Vivox5.Error.CODE.EMISSINGPROP, 'streamLocal', funcName, logSubsystemId);
    if (!(streamLocal instanceof MediaStream)) throw new Vivox5.Error(Vivox5.Error.CODE.EPROPTYPE, 'streamLocal', 'MediaStream', funcName, logSubsystemId);
    if (!streamLocal) throw new Vivox5.Error(Vivox5.Error.CODE.EPROPVALUE, 'streamLocal', funcName, logSubsystemId);

    if (cbOnRemoteStream === undefined) throw new Vivox5.Error(Vivox5.Error.CODE.EMISSINGPROP, 'onRemoteStream', funcName, logSubsystemId);
    if (typeof cbOnRemoteStream !== 'function') throw new Vivox5.Error(Vivox5.Error.CODE.EPROPTYPE, 'onRemoteStream', 'function', funcName, logSubsystemId);
    if (!this.onRemoteStream) throw new Vivox5.Error(Vivox5.Error.CODE.EPROPVALUE, 'onRemoteStream', funcName, logSubsystemId);

    // Start session
    startSession(enableAudio, enableText, connectToken);

    return true;
  };

  // -------------------------------------------------------------------------

  /**
   * Disconnect the user from this channel.
   * The application must monitor audioState and textState property changes to the "OFF" state in order to determine when it's ok rejoin the channel.
   * @async
   *
   * @returns {boolean} true on success
   *
   * @throws {Vivox5.Error} if the session is not in the established state: Vivox5.Error.CODE.ESTATE
   * @throws {Vivox5.Error} called with unexpected parameters: Vivox5.Error.CODE.ENUMPARAMS
   */
  this.disconnect = function _Vivox5_ChannelSession_disconnect() {
    var funcName = 'Vivox5.ChannelSession.disconnect()';
    Vivox5.trace();

    // Check state
    if (!isEstablished) throw new Vivox5.Error(Vivox5.Error.CODE.ESTATE, 'not established', funcName, logSubsystemId);

    // Check absence of parameters
    if (arguments.length != 0) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);

    // Initiate channel session removal
    removeSession();

    return true;
  };

  // -------------------------------------------------------------------------

  /**
   * Send a message to this channel.
   * @async
   *
   * @param {string} text - the body of the message
   * @param {string} [subject] - the subject of the message (it's not recommended to use this field, it can be unsupported or restricted on the server side)
   *
   * @returns {boolean} true on success
   *
   * @throws {Vivox5.Error} the session is not connected: Vivox5.Error.CODE.ESTATE
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} text or subject is not a string: Vivox5.Error.CODE.EPARAMTYPE
   */
  this.sendText = function _Vivox5_ChannelSession_sendText(text = undefined, subject = undefined) {
    var funcName = 'Vivox5.ChannelSession.sendText()';
    Vivox5.trace();

    // Check state
    if (!isEstablished) throw new Vivox5.Error(Vivox5.Error.CODE.ESTATE, 'not established', funcName, logSubsystemId);

    // Check parameters
    if (arguments.length < 1 || arguments.length > 2) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);
    if (typeof text !== 'string' && text !== undefined) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'text', 'string or undefined', funcName, logSubsystemId);
    if (typeof subject !== 'string' && subject !== undefined) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'subject', 'string or undefined', funcName, logSubsystemId);

    // Perform sendText operation
    sendText(text, subject);

    return true;
  };

  // -------------------------------------------------------------------------

  /**
   * Mute another user in the channel only for current user (other channel participants continue hear the muted participant), or unmute.
   * The application must monitor the {@link Vivox5.Participant}'s localMute property changes ({@link Vivox5.ChannelSession#onParticipantUpdated}) in order to determine when the operation actually completed.
   * @async
   *
   * @param {boolean} doMute - true to mute, false to unmute
   * @param {Vivox5.AccountId} participant - account of the participant which will be muted (or unmuted) for the current user
   *
   * @returns {boolean} true on success (the request was sent to the server)
   *
   * @throws {Vivox5.Error} the session is not connected: Vivox5.Error.CODE.ESTATE
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} doMute is not boolean: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} participant is not Vivox5.AccountId: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} participant not found in the participants list of this channel: Vivox5.Error.CODE.EPARAMVALUE
   */
  this.muteForMe = function _Vivox5_ChannelSession_muteForMe(doMute, participant) {
    var funcName = 'Vivox5.ChannelSession.muteForMe()';
    Vivox5.trace();

    // Check state
    if (!isEstablished) throw new Vivox5.Error(Vivox5.Error.CODE.ESTATE, 'not established', funcName, logSubsystemId);

    // Check parameters
    if (arguments.length != 2) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);
    if (typeof doMute !== 'boolean') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'doMute', 'boolean', funcName, logSubsystemId);
    if (!(participant instanceof Vivox5.Participant)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'participant', 'Vivox5.Participant', funcName, logSubsystemId);
    if (!(participant.participantId in participants)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'participant', funcName, logSubsystemId, 'participant ' + participant.toString() + ' does not exist in ChannelSession');

    // Skip operation if not supported by the participant
    if (participant.localMute == Vivox5.ChannelSession.OPERSTATE.NOTSUPPORTED) {
      return false;
    }

    // Schedule next state or return code
    var errorString = undefined;
    if (doMute) {
      switch (participant.localMute) {
        case Vivox5.ChannelSession.OPERSTATE.ON: return true; // already muted, nothing to do
        case Vivox5.ChannelSession.OPERSTATE.ENABLING: return true; // operation in progress, nothing to do
        // case Vivox5.ChannelSession.OPERSTATE.DISABLING: throw new Vivox5.Error(Vivox5.Error.CODE.ESTATE, 'reverse operation is in progress', funcName, logSubsystemId);
        case Vivox5.ChannelSession.OPERSTATE.OFF: participant.localMute = Vivox5.ChannelSession.OPERSTATE.ENABLING; break; // ok
      }
    }
    else { // if !doMute
      switch (participant.localMute) {
        case Vivox5.ChannelSession.OPERSTATE.OFF: return true; // not muted, nothing to do
        case Vivox5.ChannelSession.OPERSTATE.DISABLING: return true; // operation in progress, nothing to do
        // case Vivox5.ChannelSession.OPERSTATE.ENABLING: throw new Vivox5.Error(Vivox5.Error.CODE.ESTATE, 'reverse operation is in progress', funcName, logSubsystemId);
        case Vivox5.ChannelSession.OPERSTATE.ON: participant.localMute = Vivox5.ChannelSession.OPERSTATE.DISABLING; break; // ok
      }
    }

    // Send mute/unmute request
    var reqName = doMute ? 'local_mute' : 'local_unmute';
    return performMuteRequest(participant, reqName);
  };

  // -------------------------------------------------------------------------

  /**
   * Mute another user in the channel (all channel participants will not hear the muted participant anymore), or unmute.
   * Note: the Vivox Web SDK does not provide the information whether a particular participant is actually muted in the channel.
   * @async
   *
   * @param {boolean} doMute - true to mute, false to unmute
   * @param {Vivox5.AccountId} participant - account of the participant which will be muted (or unmuted) in the channel
   * @param {string} operationToken - the access token granting the user the privilege to mute other users
   *
   * @returns {boolean} true on success (the request was sent to the server)
   *
   * @throws {Vivox5.Error} the session is not connected: Vivox5.Error.CODE.ESTATE
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} doMute is not boolean: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} participant is not Vivox5.AccountId: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} participant not found in the participants list of this channel: Vivox5.Error.CODE.EPARAMVALUE
   * @throws {Vivox5.Error} operationToken is not a string: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} operationToken is empty: Vivox5.Error.CODE.EPARAMVALUE
   *
   * @see Developer of games that do not have secure communications requirements can use {@link Vivox5.ChannelSession#getOperationToken} to generate the required access token.
   */
  this.muteUser = function _Vivox5_ChannelSession_muteUser(doMute, participant, operationToken) {
    var funcName = 'Vivox5.ChannelSession.muteUser()';
    Vivox5.trace();

    // Check state
    if (!isEstablished) throw new Vivox5.Error(Vivox5.Error.CODE.ESTATE, 'not established', funcName, logSubsystemId);

    // Check parameters
    if (arguments.length != 3) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);
    if (typeof doMute !== 'boolean') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'doMute', 'boolean', funcName, logSubsystemId);
    if (!(participant instanceof Vivox5.Participant)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'participant', 'Vivox5.Participant', funcName, logSubsystemId);
    if (!(participant.participantId in participants)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'participant', funcName, logSubsystemId, 'participant ' + participant.toString() + ' does not exist in ChannelSession');
    if (typeof operationToken !== 'string') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'operationToken', 'string', funcName, logSubsystemId);
    if (!operationToken) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'operationToken', funcName, logSubsystemId);

    // Send mute/unmute request
    var reqName = doMute ? 'mute' : 'unmute';
    return performMuteRequest(participant, reqName, operationToken);
  };

  // -------------------------------------------------------------------------

  /**
   * Kick another user in the channel.
   * The application must handle the {@link Vivox5.ChannelSession#onParticipantRemoved} callback function in order to determine when the operation actually completed.
   * @async
   *
   * @param {Vivox5.AccountId} participant - account of the participant which will be kicked from the channel
   * @param {string} operationToken - the access token granting the user the privilege to kick other users
   *
   * @returns {boolean} true on success (the request was sent to the server)
   *
   * @throws {Vivox5.Error} the session is not connected: Vivox5.Error.CODE.ESTATE
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} participant is not Vivox5.AccountId: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} participant not found in the participants list of this channel: Vivox5.Error.CODE.EPARAMVALUE
   * @throws {Vivox5.Error} operationToken is not a string: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} operationToken is empty: Vivox5.Error.CODE.EPARAMVALUE
   *
   * @see Developer of games that do not have secure communications requirements can use {@link Vivox5.ChannelSession#getOperationToken} to generate the required access token.
   */
  this.kickUser = function _Vivox5_ChannelSession_kickUser(participant, operationToken) {
    var funcName = 'Vivox5.ChannelSession.kickUser()';
    Vivox5.trace();

    // Check state
    if (!isEstablished) throw new Vivox5.Error(Vivox5.Error.CODE.ESTATE, 'not established', funcName, logSubsystemId);

    // Check parameters
    if (arguments.length != 2) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);
    if (!(participant instanceof Vivox5.Participant)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'participant', 'Vivox5.Participant', funcName, logSubsystemId);
    if (!(participant.participantId in participants)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'participant', funcName, logSubsystemId, 'participant ' + participant.toString() + ' does not exist in ChannelSession');
    if (typeof operationToken !== 'string') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'operationToken', 'string', funcName, logSubsystemId);
    if (!operationToken) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'operationToken', funcName, logSubsystemId);

    // Send kick request
    return performMuteRequest(participant, 'kick', operationToken);
  };

  // -------------------------------------------------------------------------

  /**
   * Add or remove audio from the channel session.
   * The application must monitor audioState property changes ({@link Vivox5.ChannelSession#onAudioStateUpdated}) in order to determine when the operation actually completed.
   * @async
   *
   * @param {boolean} enable - true to add audio, false to remove audio
   *
   * @returns {boolean} true on success (the request was sent to the server)
   *
   * @throws {Vivox5.Error} the session is not connected: Vivox5.Error.CODE.ESTATE
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} enable is not boolean: Vivox5.Error.CODE.EPARAMTYPE
   */
  this.setAudioConnected = function _Vivox5_ChannelSession_setAudioConnected(enable) {
    var funcName = 'Vivox5.ChannelSession.setAudioConnected()';
    Vivox5.trace();

    // Check state
    if (!isEstablished) throw new Vivox5.Error(Vivox5.Error.CODE.ESTATE, 'not established', funcName, logSubsystemId);

    // Check parameters
    if (arguments.length != 1) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);
    if (typeof enable !== 'boolean') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'enable', 'boolean', funcName, logSubsystemId);

    // Perform the operation
    return setAudioConnected(enable);
  };

  // -------------------------------------------------------------------------

  /**
   * Add or remove text from the channel session.
   * The application must monitor textState property changes ({@link Vivox5.ChannelSession#onTextStateUpdated}) in order to determine when the operation actually completed.
   * @async
   *
   * @param {boolean} enable - true to add text, false to remove text
   *
   * @returns {boolean} true on success (the request was sent to the server)
   *
   * @throws {Vivox5.Error} the session is not connected: Vivox5.Error.CODE.ESTATE
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} enable is not boolean: Vivox5.Error.CODE.EPARAMTYPE
   */
  this.setTextConnected = function _Vivox5_ChannelSession_setTextConnected(enable) {
    var funcName = 'Vivox5.ChannelSession.setTextConnected()';
    Vivox5.trace();

    // Check state
    if (!isEstablished) throw new Vivox5.Error(Vivox5.Error.CODE.ESTATE, 'not established', funcName, logSubsystemId);

    // Check parameters
    if (arguments.length != 1) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);
    if (typeof enable !== 'boolean') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'enable', 'boolean', funcName, logSubsystemId);

    // Perform the operation
    return setTextConnected(enable);
  };

  // -------------------------------------------------------------------------

  /**
   * Get a token that can be used to connect to this channel.
   * To be used only by applications without secure communications requirements.
   *
   * @param {string} tokenSigningKey - the key corresponding to the issuer for this account that is used to sign the token
   * @param {number} tokenExpirationDuration - the length of time the token is valid for
   *
   * @returns {string} A token that can be used to join this channel
   *
   * @throws {Vivox5.Error} the session is not connected: Vivox5.Error.CODE.ESTATE
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} tokenSigningKey is not a string or tokenExpirationDuration is not a number: Vivox5.Error.CODE.EPARAMTYPE
   */
  this.getConnectToken = function _Vivox5_ChannelSession_getConnectToken(tokenSigningKey, tokenExpirationDuration) {
    var funcName = 'Vivox5.ChannelSession.getConnectToken()';
    Vivox5.trace();

    // Check parameters
    if (arguments.length != 2) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);
    if (typeof tokenSigningKey !== 'string') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'tokenSigningKey', 'string', funcName, logSubsystemId);
    if (typeof tokenExpirationDuration !== 'number') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'tokenExpirationDuration', 'number', funcName, logSubsystemId);

    // Generate token
    var token = Vivox5.Token.debugGenerateAccessToken(channel.issuer, tokenExpirationDuration, 'join', '', accountJid, channelJid, tokenSigningKey);
    return token;
  };

  // -------------------------------------------------------------------------

  /**
   * Get a token for specific operations (mute for all, kick) in this channel.
   * To be used only by applications without secure communications requirements.
   *
   * @param {string} operation - the operation for whitch the token is generated: 'mute' for the [muteUser]{@link Vivox5.ChannelSession#muteUser} operation, 'kick' for the [kickUser]{@link Vivox5.ChannelSession#kickUser} operation
   * @param {Vivox5.AccountId} subject - the account of a user for which this operation will be performed
   * @param {string} tokenSigningKey - the key corresponding to the issuer for this account that is used to sign the token
   * @param {number} tokenExpirationDuration - the length of time the token is valid for
   *
   * @returns {string} A token that can be used to perform the requested operation
   *
   * @throws {Vivox5.Error} the session is not connected: Vivox5.Error.CODE.ESTATE
   * @throws {Vivox5.Error} called with incorrect number of parameters: Vivox5.Error.CODE.ENUMPARAMS
   * @throws {Vivox5.Error} tokenSigningKey is not a string or tokenExpirationDuration is not a number: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} operation is not a string: Vivox5.Error.CODE.EPARAMTYPE
   * @throws {Vivox5.Error} operation is not 'mute' or 'kick': Vivox5.Error.CODE.EPARAMVALUE
   * @throws {Vivox5.Error} subject is not a Vivox5.AccountId: Vivox5.Error.CODE.EPARAMTYPE
   */
  this.getOperationToken = function _Vivox5_ChannelSession_getOperationToken(operation, subject, tokenSigningKey, tokenExpirationDuration) {
    var funcName = 'Vivox5.ChannelSession.getOperationToken()';
    Vivox5.trace();

    // Check parameters
    if (arguments.length != 4) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, funcName, logSubsystemId);
    if (typeof operation !== 'string') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'operation', 'string', funcName, logSubsystemId);
    if (operation != 'mute' && operation != 'kick') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'operation', funcName, logSubsystemId);
    if (!(subject instanceof Vivox5.AccountId)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'subject', 'Vivox5.AccountId', funcName, logSubsystemId);
    if (typeof tokenSigningKey !== 'string') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'tokenSigningKey', 'string', funcName, logSubsystemId);
    if (typeof tokenExpirationDuration !== 'number') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'tokenExpirationDuration', 'number', funcName, logSubsystemId);

    // Generate token
    var token = Vivox5.Token.debugGenerateAccessToken(channel.issuer, tokenExpirationDuration, operation, subject.toString(), accountJid, channelJid, tokenSigningKey);
    return token;
  };

  // -------------------------------------------------------------------------

  // NB: this function is called internally be Vivox5.LoginSession, it MUST NOT be called by the SDK users. Must be declared as {enumerable: false} below.
  this.onConnectionLost = function _Vivox5_ChannelSession_onConnectionLost() {
    Vivox5.trace();
    onChannelSessionTerminatedInternal();
  };

  // -------------------------------------------------------------------------

  // NB: internal function, it MUST NOT be called by the SDK users. Must be declared as {enumerable: false} below.
  this.close = function _Vivox5_ChannelSession_close() {
    Vivox5.trace();

    if (!self) {
      // already closed
      return;
    }

    releaseChannelSessionResourcesAll();
    self = undefined;
  };

  // -------------------------------------------------------------------------

  this.toString = function _Vivox5_ChannelSession_toString() {
    // just for debugging
    return '[object Vivox5.ChannelSession(' + channelJid + ')]';
  };

  // ===========================================================================================
  // Vivox5.ChannelSession public methods configuration

  Object.defineProperties(this, {
    connect: { writable: false, configurable: false, enumerable: true },
    disconnect: { writable: false, configurable: false, enumerable: true },
    sendText: { writable: false, configurable: false, enumerable: true },
    muteForMe: { writable: false, configurable: false, enumerable: true },
    muteUser: { writable: false, configurable: false, enumerable: true },
    kickUser: { writable: false, configurable: false, enumerable: true },
    setAudioConnected: { writable: false, configurable: false, enumerable: true },
    setTextConnected: { writable: false, configurable: false, enumerable: true },
    getConnectToken: { writable: false, configurable: false, enumerable: true },
    getOperationToken: { writable: false, configurable: false, enumerable: true },
    onConnectionLost: { writable: false, configurable: false, enumerable: false },
    close: { writable: false, configurable: false, enumerable: false },
    toString: { writable: false, configurable: false, enumerable: true },
  });

  // ===========================================================================================
  // Vivox5.ChannelSession private functions

  function log(msg) {
    Vivox5.log(logSubsystemId, msg);
  }

  // -------------------------------------------------------------------------

  function startSession(enableAudio, enableText, connectToken) {
    Vivox5.trace();

    // Send presence with token (XEP-0045 Multi-User Chat, section 7.2 Entering a Room)
    log(logPrefixSessionStarting + ', sending presence with token');
    isStarted = true;

    addChannelSessionHandlers();

    var pres =
      $pres({ to: mucJidRes, id: Vivox5.Tools.makeUuid() })
        .c('m', { xmlns: Vivox5.Xmpp.NS.URN_VIVOX_PRESENCE })
        .up()
        .c('x', { xmlns: Strophe.NS.MUC })
        .c('password', {})
        .t(connectToken)
        .tree();

    // Send the presence stanza with timeout
    // (the response will be matched by id, however we can match it by mucJidRes + Vivox5.Xmpp.NS.URN_VIVOX_PRESENCE, like in previous version)
    stropheConnection.sendPresence(
      pres,
      function (presResp) { return onStartSessionResponse(presResp, enableAudio, enableText); },
      onStartSessionError,
      Vivox5.Xmpp.DEFAULT_TRANSACTION_TIMEOUT
    );

    // NB: after sending the initial request, the session is established on the server side
    // and some notifications (e.g. presence of the MUC other members) can arrive immediately, before the response.
    // Thus addChannelSessionHandlers() must be called BEFORE sending the request.
  }

  // -------------------------------------------------------------------------

  function onStartSessionResponse(pres, enableAudio, enableText) {
    Vivox5.trace();

    // This is the response to the initial <presence> with token (matched by id).
    // On success consider the session established.
    // Enable text and audio capabilities if needed (send presence with <text/> and <audio/>).

    isEstablished = true;
    log(logPrefixSession + ' established successfully');

    // Callback onSessionEstablished
    if (cbOnSessionEstablished && typeof cbOnSessionEstablished === 'function') {
      cbOnSessionEstablished(self);
    }

    // Enable or disable text and audio capabilities
    setTextConnected(enableText);
    setAudioConnected(enableAudio);
  }

  // -------------------------------------------------------------------------

  function onStartSessionError(pres) {
    Vivox5.trace();

    if (pres) {
      // presence with type='error' (checked by Strophe)
      Vivox5.logXmppError(logSubsystemId, pres, logPrefixSessionStarting);
    }
    else { // timeout
      log(logPrefixSessionStarting + ': timeout, no expected response from the server');
    }

    onChannelSessionTerminatedInternal();
  }

  // -------------------------------------------------------------------------

  function removeSession(forceUnavailable = false) {
    Vivox5.trace();

    // Disconnect audio and text capabilities (skip this step if forceUnavailable is true).
    // After that send presence with type=unavailable.
    // On error consider that we have disconnected the channel.

    if (!forceUnavailable) {
      setTextConnected(false);
      setAudioConnected(false);
    }

    isEstablished = false;

    if (
      forceUnavailable || (
        audioState == Vivox5.ChannelSession.OPERSTATE.OFF &&
        textState == Vivox5.ChannelSession.OPERSTATE.OFF
      )
    ) {
      removeSessionSetUnavailable();
    }
  }

  // -------------------------------------------------------------------------

  function removeSessionSetUnavailable() {
    Vivox5.trace();
    // Send presence with type=unavailable.

    releaseChannelSessionResourcesMedia();
    isEstablished = false;

    log(logPrefixSessionRemoving + ': sending presence with unavailable indicator');

    var pres =
      $pres({ type: 'unavailable', to: mucJidRes, id: Vivox5.Tools.makeUuid() })
        .tree();
    //console.log(pres);

    // Send the presence stanza with timeout
    // (the response will be matched by id, however we can match it by mucJidRes + Vivox5.Xmpp.NS.URN_VIVOX_PRESENCE, like in previous version)
    stropheConnection.sendPresence(pres, onRemoveSessionSetUnavailableResponse, onRemoveSessionSetUnavailableResponse, Vivox5.Xmpp.DEFAULT_TRANSACTION_TIMEOUT);
    // NB: the final response will not contain Vivox5.Xmpp.NS.URN_VIVOX_PRESENCE
  }

  // -------------------------------------------------------------------------

  function onRemoveSessionSetUnavailableResponse(pres) {
    Vivox5.trace();

    // This is the 'response' to <presence> with type=unavailable.
    // Ignore the response. Errors and timeouts are handled here the same way.
    // Consider that we have disconnected the channel in any case.

    if (!pres) {
      log(logPrefixSessionRemoving + ' warning: timeout while waiting response to the presence type=unavailable');
    }

    onChannelSessionTerminatedInternal();
  }

  // -------------------------------------------------------------------------

  function onChannelSessionTerminatedInternal() {
    Vivox5.trace();
    log(logPrefixSession + ' terminated');

    releaseChannelSessionResourcesMedia();
    isEstablished = false;

    // Callback onSessionTerminated
    if (cbOnSessionTerminated && typeof cbOnSessionTerminated === 'function') {
      cbOnSessionTerminated(self);
    }

    // Callback to the login session
    loginSession.onChannelSessionTerminatedInternal(self);
  }

  // -------------------------------------------------------------------------

  function setAudioConnected(enable) {
    // When setTextConnected is in progress then schedule setAudioConnected after its completion
    if (textState == Vivox5.ChannelSession.OPERSTATE.ENABLING || textState == Vivox5.ChannelSession.OPERSTATE.DISABLING) {
      if (enable) { audioConnectScheduled = true; audioDisconnectScheduled = false; }
      else { audioConnectScheduled = false; audioDisconnectScheduled = true; }
      return true;
    }

    // Check operation state and possible action
    switch (checkOperStateAndAction(audioState, enable, 'setAudioConnected')) {
      case Vivox5.ChannelSession.ACTIONPOSSIBLE.CANSTART: break;        // initiate operation below
      case Vivox5.ChannelSession.ACTIONPOSSIBLE.NOTHINGTODO: return true;  // already in this state or going into this state
      case Vivox5.ChannelSession.ACTIONPOSSIBLE.NOTPOSSIBLE: return false; // operation is not possible in the current state
      case Vivox5.ChannelSession.ACTIONPOSSIBLE.NOTSUPPORTED: return false; // operation is not supported
      default: return false; // must not get here
    }

    if (enable) { // connect audio
      audioState = Vivox5.ChannelSession.OPERSTATE.ENABLING;
      if (!inviteCompleted) {
        // First need to perform iq-with-invite transaction. Will enable the audio indicator later, after the invite transaction completed.
        performInviteHandshake();
        return true;
      }
    }
    else { // !enable, i.e. disconnect audio
      audioState = Vivox5.ChannelSession.OPERSTATE.DISABLING;
      // FIXME: We don't send 'bye' or 'chan_remove' now. Possibly, it will be added in the future versions.
    }

    // Send the presence-with-m stanza:
    sendTextAudioIndicatorsStanza(function (resp) { return onSetAudioConnectedResponse(resp, enable); });
    return true;
  }

  // -------------------------------------------------------------------------

  function setTextConnected(enable) {
    // when setAudioConnected is in progress then schedule setTextConnected after its completion
    if (audioState == Vivox5.ChannelSession.OPERSTATE.ENABLING || audioState == Vivox5.ChannelSession.OPERSTATE.DISABLING) {
      if (enable) { textConnectScheduled = true; textDisconnectScheduled = false; }
      else { textConnectScheduled = false; textDisconnectScheduled = true; }
      return true;
    }

    switch (checkOperStateAndAction(textState, enable, 'setTextConnected')) {
      case Vivox5.ChannelSession.ACTIONPOSSIBLE.CANSTART: break;        // initiate operation below
      case Vivox5.ChannelSession.ACTIONPOSSIBLE.NOTHINGTODO: return true;  // already in this state or going into this state
      case Vivox5.ChannelSession.ACTIONPOSSIBLE.NOTPOSSIBLE: return false; // operation is not possible in the current state
      case Vivox5.ChannelSession.ACTIONPOSSIBLE.NOTSUPPORTED: return false; // operation is not supported
      default: return false; // must not get here
    }

    if (enable) { // connect text
      textState = Vivox5.ChannelSession.OPERSTATE.ENABLING;
    }
    else { // !enable, i.e. disconnect text
      textState = Vivox5.ChannelSession.OPERSTATE.DISABLING;
    }

    // Send the presence-with-m stanza:
    sendTextAudioIndicatorsStanza(function (resp) { return onSetTextConnectedResponse(resp, enable); });
    return true;
  }

  // -------------------------------------------------------------------------

  function checkOperStateAndAction(state, enable, operationName) {
    // check operation state and possibility of the requested action for some operations (setAudioConnected, setTextConnected, etc.)
    Vivox5.trace();

    if (state == Vivox5.ChannelSession.OPERSTATE.NOTSUPPORTED) {
      return Vivox5.ChannelSession.ACTIONPOSSIBLE.NOTSUPPORTED;
    }

    var errorString = undefined;

    if (enable) {
      switch (state) {
        case Vivox5.ChannelSession.OPERSTATE.ON: return Vivox5.ChannelSession.ACTIONPOSSIBLE.NOTHINGTODO; // already enabled, nothing to do
        case Vivox5.ChannelSession.OPERSTATE.ENABLING: return Vivox5.ChannelSession.ACTIONPOSSIBLE.NOTHINGTODO; // operation in progress, nothing to do
        case Vivox5.ChannelSession.OPERSTATE.DISABLING: errorString = 'Error: reverse operation is still in progress when ' + operationName + '(' + enable + ') called'; break;
        case Vivox5.ChannelSession.OPERSTATE.OFF: return Vivox5.ChannelSession.ACTIONPOSSIBLE.CANSTART; // ok
      }
    }
    else { // if !enable
      switch (state) {
        case Vivox5.ChannelSession.OPERSTATE.OFF: return true; // already disabled, nothing to do
        case Vivox5.ChannelSession.OPERSTATE.DISABLING: return true; // operation in progress, nothing to do
        case Vivox5.ChannelSession.OPERSTATE.ENABLING: errorString = 'Error: reverse operation is still in progress when ' + operationName + '(' + enable + ') called'; break;
        case Vivox5.ChannelSession.OPERSTATE.ON: return Vivox5.ChannelSession.ACTIONPOSSIBLE.CANSTART; // ok
      }
    }

    if (errorString) {
      log(errorString);
      return Vivox5.ChannelSession.ACTIONPOSSIBLE.NOTPOSSIBLE;
    }

  }

  // -------------------------------------------------------------------------

  function createTextAudioIndicatorsStanza() {
    Vivox5.trace();

    var addAudio = (audioState == Vivox5.ChannelSession.OPERSTATE.ON || audioState == Vivox5.ChannelSession.OPERSTATE.ENABLING);
    var addText = (textState == Vivox5.ChannelSession.OPERSTATE.ON || textState == Vivox5.ChannelSession.OPERSTATE.ENABLING);

    var pres =
      $pres({ to: mucJidRes, id: Vivox5.Tools.makeUuid() })
        .c('m', { xmlns: Vivox5.Xmpp.NS.URN_VIVOX_PRESENCE });

    if (addAudio) {
      pres.c('audio', {}).up();
    }

    if (addText) {
      pres.c('text', {}).up();
    }

    return pres.tree();
  }

  // -------------------------------------------------------------------------

  function sendTextAudioIndicatorsStanza(callbackOnResponse) {
    Vivox5.trace();

    var pres = createTextAudioIndicatorsStanza();

    // Send the stanza with response, error and timeout handlers
    // (important: waiting response from the ChannelUriJidRes, not the ChannelUriJid!)
    stropheConnection.sendPresence(pres, callbackOnResponse, onSetTextOrAudioConnectedError, Vivox5.Xmpp.DEFAULT_TRANSACTION_TIMEOUT);
  }

  // -------------------------------------------------------------------------

  function onSetAudioConnectedResponse(resp, enable) {
    Vivox5.trace();

    // This is the 'response' to <presence> with <audio/> indicator (or without it if !enable).
    // Ignore the response contents (except of errors handled in onSetTextOrAudioConnectedError). Consider the operation completed.

    if (audioState == Vivox5.ChannelSession.OPERSTATE.ENABLING) {
      audioState = Vivox5.ChannelSession.OPERSTATE.ON;
      log('audioState changed to ON');
    }
    else if (audioState == Vivox5.ChannelSession.OPERSTATE.DISABLING) {
      audioState = Vivox5.ChannelSession.OPERSTATE.OFF;
      log('audioState changed to OFF');
    }

    // Callback for session audio capability update
    if (cbOnAudioStateUpdated && typeof cbOnAudioStateUpdated === 'function') {
      cbOnAudioStateUpdated(self, audioState)
    }

    // Perform delayed actions
    performSetAudioOrTextConnectedScheduledActions();

    return false; // remove current handler
  }

  // -------------------------------------------------------------------------

  function onSetTextConnectedResponse(resp, enable) {
    Vivox5.trace();

    // This is the 'response' to <presence> with <text/> indicator (or without it if !enable).
    // Ignore the response contents (except of errors handled in onSetTextOrAudioConnectedError). Consider the operation completed.

    if (textState == Vivox5.ChannelSession.OPERSTATE.ENABLING) {
      textState = Vivox5.ChannelSession.OPERSTATE.ON;
      log('textState changed to ON');
    }
    else if (textState == Vivox5.ChannelSession.OPERSTATE.DISABLING) {
      textState = Vivox5.ChannelSession.OPERSTATE.OFF;
      log('textState changed to OFF');
    }

    // Callback for session audio capability update
    if (cbOnTextStateUpdated && typeof cbOnTextStateUpdated === 'function') {
      cbOnTextStateUpdated(self, textState)
    }

    // Perform delayed actions
    performSetAudioOrTextConnectedScheduledActions();

    return false; // remove current handler
  }

  // -------------------------------------------------------------------------

  function onSetTextOrAudioConnectedError(resp) {
    Vivox5.trace();

    if (resp) {
      // presence with type='error' (checked by Strophe)
      Vivox5.logXmppError(logSubsystemId, resp, logPrefixSession);
    }
    else {
      // timeout
      log('Timeout while waiting a response to the text/audio indicators stanza');
    }

    onChannelSessionTerminatedInternal();
  }

  // -------------------------------------------------------------------------

  function performSetAudioOrTextConnectedScheduledActions() {
    Vivox5.trace();

    if (audioConnectScheduled) {
      audioConnectScheduled = false;
      audioDisconnectScheduled = false;
      setAudioConnected(true);
    }

    if (audioDisconnectScheduled) {
      audioConnectScheduled = false;
      audioDisconnectScheduled = false;
      setAudioConnected(false);
    }

    if (textConnectScheduled) {
      textConnectScheduled = false;
      textDisconnectScheduled = false;
      setTextConnected(true);
    }

    if (audioDisconnectScheduled) {
      textConnectScheduled = false;
      textDisconnectScheduled = false;
      setTextConnected(false);
    }

    if (
      audioState == Vivox5.ChannelSession.OPERSTATE.OFF &&
      textState == Vivox5.ChannelSession.OPERSTATE.OFF
    ) {
      removeSessionSetUnavailable();
    }
  }

  // -------------------------------------------------------------------------

  function performInviteHandshake() {
    Vivox5.trace();
    // Send IQ with <invite> and fake SDP here.

    if (inviteCompleted) {
      // Now the invite is sent only one time in a session. This behavior can be changed later (when bye or chan_remove added).
      return;
    }

    log(logPrefixSessionStarting + ', sending IQ with INVITE and fake SDP');

    var id = Vivox5.Tools.makeUuid();
    var callId = Vivox5.Tools.getRandomInt(100000000, 999999999);

    var isEdge = (window.navigator.userAgent.indexOf("Edge/") > -1);
    var isDumb = isEdge;
    if (self.overrideRTCPCMode == 'dumb') {
      isDumb = true;
    }
    else if (self.overrideRTCPCMode == 'multi') {
      isDumb = false;
    }

    var fakeSdp = isDumb ? 'webrtc-dumb' : 'webrtc';

    var iq =
      $iq({ id: id, to: mucJid, type: 'set' })
        .c('invite', { callid: callId, xmlns: Vivox5.Xmpp.NS.URN_VIVOX_MUC })
        .t(fakeSdp)
        .tree();
    //console.log(iq);

    // Send the iq stanza with timeout (the response will be matched by id):
    stropheConnection.sendIQ(iq, onInviteResponse, onInviteError, Vivox5.Xmpp.DEFAULT_TRANSACTION_TIMEOUT);
  }

  // -------------------------------------------------------------------------

  function onInviteResponse(iq) {
    Vivox5.trace();
    // This is the response to <iq> with <invite> (type='result' here, checked by Strophe)
    // Take from the response: iq -> invite -> fake SDP (WS URI to PatchPanel or encoded RTCPeerConnections description)
    // and then establish WS connection to PP or decode base64-encoded XML with RTCPeerConnection description

    var elsInvite = iq.getElementsByTagName('invite');
    if (elsInvite && elsInvite.length > 0) {
      var inviteContents = elsInvite[0].innerHTML;
      var obj;
      if (inviteContents.substring(0, 5) == 'ws://' || inviteContents.substring(0, 6) == 'wss://') {
        // Invite succeeded, we have WS URI, initiate WebSocket connection to the PatchPanel.
        log(logPrefixSessionStarting + ': invite succeeded, WS URI is ' + inviteContents);
        initiateWsConnectionWithPatchPanel(inviteContents);
        // afterInviteSucceeded() will be called later, in onWSPPConnected()
      }
      else if ((obj = parsePPMessage(inviteContents, true)) !== null && obj.addrtcpc) {
        afterInviteSucceeded();
        initPeerConnection(obj.addrtcpc);
      }
      else {
        // Error: invalid WebSocket URI or b64/XML command
        log(logPrefixSessionStarting + ': invite succeeded, but the response is invalid, neither WS URI nor b64/XML command: ' + inviteContents);
        onInviteFailed();
      }
    } // end of if invite present
    else {
      // Error: no <invite> element inside <iq>
      log(logPrefixSessionStarting + ': <invite> element is absent in response to the invite request');
      onInviteFailed();
    }
  }

  // -------------------------------------------------------------------------

  function onInviteError(iq) {
    Vivox5.trace();
    // Error response to the iq/invite request (if iq is not null) or timeout (otherwise)

    if (!iq) {
      log(logPrefixSessionStarting + ' fatal error: timeout while waiting response to the invite request');
    }
    else {
      var sLogPrefix = logPrefixSessionStarting + ': invite failed';
      Vivox5.logXmppError(logSubsystemId, iq, sLogPrefix);
    }

    onInviteFailed();
  }

  // -------------------------------------------------------------------------

  function onInviteFailed() {
    Vivox5.trace();
    inviteInProgress = false;
    inviteCompleted = false;
    removeSession(true);
  }

  // -------------------------------------------------------------------------

  function afterInviteSucceeded() {
    Vivox5.trace();

    inviteInProgress = false;
    inviteCompleted = true;

    // Enable audio indicator: send the presence-with-m stanza:
    sendTextAudioIndicatorsStanza(function (resp) { return onSetAudioConnectedResponse(resp, true); });
  }

  // -------------------------------------------------------------------------

  var handOnParticipantUpdated = undefined;
  var handOnParticipantRemoved = undefined;
  var handOnGroupMessage = undefined;

  function addChannelSessionHandlers() {
    Vivox5.trace();

    handOnParticipantUpdated = stropheConnection.addHandler(
      onParticipantUpdatedInternal,
      Strophe.NS.MUC /*Vivox5.Xmpp.NS.URN_VIVOX_PRESENCE*/,
      'presence',
      null,
      null,
      mucJid,
      { matchBareFromJid: true, ignoreNamespaceFragment: true }
    );

    handOnParticipantRemoved = stropheConnection.addHandler(
      onParticipantRemovedInternal,
      Strophe.NS.MUC,
      'presence',
      'unavailable',
      null,
      mucJid,
      { matchBareFromJid: true, ignoreNamespaceFragment: true }
    );

    handOnGroupMessage = stropheConnection.addHandler(
      onGroupMessage,
      Strophe.NS.CLIENT,
      'message',
      'groupchat',
      null,
      mucJid,
      { matchBareFromJid: true, ignoreNamespaceFragment: true }
    );
  }

  // -------------------------------------------------------------------------

  function removeChannelSessionHandlers() {
    Vivox5.trace();

    if (handOnParticipantUpdated) {
      stropheConnection.deleteHandler(handOnParticipantUpdated);
      handOnParticipantUpdated = undefined;
    }

    if (handOnParticipantRemoved) {
      stropheConnection.deleteHandler(handOnParticipantRemoved);
      handOnParticipantRemoved = undefined;
    }

    if (handOnGroupMessage) {
      stropheConnection.deleteHandler(handOnGroupMessage);
      handOnGroupMessage = undefined;
    }
  }

  // -------------------------------------------------------------------------

  function onParticipantUpdatedInternal(pres) {
    if (pres.hasAttribute('type')) {
      // Participant add/update arrives without type.
      // type='unavailable' and type='error' are handled in other places.
      return true; // don't remove this stanza handler
    }

    Vivox5.trace();

    //log('Participant updated, raw input = ' + Vivox5.Xml.xmlToString(pres));
    /*
      Example:
      <presence from="confctl-g-12345678@muc.dd00.vivox.com/mamay" to=".babay.@dd00.vivox.com/823D90049D54052E1486565469388348" xmlns="jabber:client">
        <m xmlns="urn:vivox:presence">
          <audio/>
          <text/>
        </m>
        <x xmlns="http://jabber.org/protocol/muc#user">
          <item jid=".mamay.@dd00.vivox.com/8F78CB121D0637541486565618781938" affiliation="none" role="participant"/>
        </x>
      </presence>
    */

    var jidWithRes = undefined;
    var jid = undefined;
    var isSelf = false;
    var affiliation = undefined;
    var role = undefined;
    var roomNickname = undefined;
    var roomNicknameOld = undefined;

    var elsX = pres.getElementsByTagName('x');
    if (elsX && elsX.length > 0) {
      var x = elsX[0];
      // <item> -- single
      var elsItem = x.getElementsByTagName('item');
      if (elsItem && elsItem.length > 0) {
        var it = elsItem[0];
        jidWithRes = it.getAttribute('jid');
        jid = Strophe.getBareJidFromJid(jidWithRes);
        affiliation = it.getAttribute('affiliation');
        role = it.getAttribute('role');
      }

      // <status> -- can be multiple, can be absent
      var elsStatus = x.getElementsByTagName('status');
      if (elsStatus && elsStatus.length > 0) {
        for (let n = 0; n < elsStatus.length; ++n) {
          var elStatus = elsStatus[0];
          if (elStatus.getAttribute('code') == '110') {
            // 110 = Inform user that presence refers to one of its own room occupants (q) https://xmpp.org/registrar/mucstatus.html
            isSelf = true;
          }
        }
      }
    }

    if (!jid) {
      log('Ignored presence update for unidentified participant: ' + Vivox5.Xml.xmlToString(pres));
      return true; // don't remove this stanza handler
    }

    // Participant ID is a unique string, we use full JID with resource
    var participantId = jidWithRes;

    // Added or updated?
    var isAdded = !(participantId in participants);

    // Create new participant or take reference to the existing object
    var participant;
    if (isAdded) {
      var account = new Vivox5.AccountId(jid);
      participant = new Vivox5.Participant(self, participantId, account, isSelf);
    }
    else {
      participant = participants[participantId];
    }

    // Room nickname
    var from = pres.getAttribute('from');
    if (from) {
      roomNickname = Strophe.getResourceFromJid(from);
    }
    if (!isAdded) {
      roomNicknameOld = participant.roomNickname;
    }

    var hasAudio = false;
    var hasText = false;
    var elsM = pres.getElementsByTagName('m');
    if (elsM && elsM.length > 0) {
      var m = elsM[0];
      if (m.getElementsByTagName('audio').length > 0) hasAudio = true;
      if (m.getElementsByTagName('text').length > 0) hasText = true;
    }

    // Fill participant properties

    // 1. The following fields always arrive in <presence>:
    participant.roomNickname = roomNickname; // resource part of the Occupant JID (q) XEP-0045
    participant.affiliation = affiliation;
    participant.role = role;
    participant.inAudio = hasAudio;
    participant.inText = hasText;
    //participant.isSelf       = isSelf; // don't modify it, use the value filled on creation

    // 2. The following fields don't arrive in <presence>, add them only when adding new participant
    if (isAdded) {
      // Server not always sends correct audio indicator. The following line temporarily commented out. TODO: check this!
      //participant.localMute  = hasAudio ? Vivox5.ChannelSession.OPERSTATE.OFF : Vivox5.ChannelSession.OPERSTATE.NOTSUPPORTED;
      participant.localMute = Vivox5.ChannelSession.OPERSTATE.OFF;
    }

    // Add new participant to participants collection
    if (isAdded) {
      participants[participantId] = participant;
      participantsByRoomNick[roomNickname] = participant;
    }
    // else it is already there

    // The case when the roomNickname changes
    if (!isAdded && roomNickname != roomNicknameOld) {
      delete participantsByRoomNick[roomNicknameOld];
      participantsByRoomNick[roomNickname] = participant;
    }

    log(
      'Participant ' + (isAdded ? 'added' : 'updated') + ': ' + participant.account.toString() +
      ' roomNickname=' + participant.roomNickname +
      ' affiliation=' + participant.affiliation + ' role=' + participant.role +
      ' inAudio=' + participant.inAudio + ' inText=' + participant.inText +
      ' isSelf=' + participant.isSelf +
      ' in ' + logPrefixSession
    );

    // Callback for added/updated participant
    var callback = isAdded ? cbOnParticipantAdded : cbOnParticipantUpdated;
    if (callback && typeof callback === 'function') {
      callback(self, participant);
    }

    return true; // don't remove this stanza handler
  }

  // -------------------------------------------------------------------------

  function onParticipantRemovedInternal(pres) {
    Vivox5.trace();

    //log('Participant removed, raw input = ' + Vivox5.Xml.xmlToString(pres));
    /*
      Example:
      <presence from='confctl-g-12345678@muc.dd00.vivox.com/papay' to='.babay.@dd00.vivox.com/9F5E0581137B94C81486553525148041' type='unavailable' xmlns='jabber:client'>
        <x xmlns='http://jabber.org/protocol/muc#user'>
          <item jid='.papay.@dd00.vivox.com/B69048C92A9CC45D1486553708720476' affiliation='none' role='none'/>
        </x>
      </presence>
    */

    var jidWithRes = undefined;
    var jid = undefined;

    var elsX = pres.getElementsByTagName('x');
    if (elsX && elsX.length > 0) {
      var x = elsX[0];
      var elsItem = x.getElementsByTagName('item');
      if (elsItem && elsItem.length > 0) {
        var it = elsItem[0];
        jidWithRes = it.getAttribute('jid');
        jid = Strophe.getBareJidFromJid(jidWithRes);
      }
    }

    if (!jid) {
      log('Ignored presence removal for unidentified participant: ' + Vivox5.Xml.xmlToString(pres));
      return true; // don't remove this stanza handler
    }

    var participantId = jidWithRes;

    if (!(participantId in participants)) {
      log('Ignored presence removal for unknown participant: ' + jidWithRes);
      return true; // don't remove this stanza handler
    }

    var roomNickname = participants[participantId].roomNickname

    log('Participant removed: ' + jid + ' (' + roomNickname + ') from ' + logPrefixSession);

    // Callback for removed participant
    if (cbOnParticipantRemoved && typeof cbOnParticipantRemoved === 'function') {
      cbOnParticipantRemoved(self, participants[participantId]);
    }

    delete participantsByRoomNick[roomNickname];
    delete participants[participantId];

    // If the server reports that we are unavailable but we consider the session established, it means that we were kicked by the server or something like this.
    // Do explicit hangup in this case (see also WRT-34 WebRTC client cannot be kicked from a conference)
    var isSelf = (jid == accountJid);
    if (isSelf && isEstablished) {
      //removeSession(true); // send presence with type=unavailable (no response from the server)
      removeSession(); // send presence with disabled audio and text (workaround)
    }

    return true; // don't remove this stanza handler
  }

  // -------------------------------------------------------------------------

  function onGroupMessage(message) {
    Vivox5.trace();

    //log('Group message received, raw input = ' + Vivox5.Xml.xmlToString(message));
    /*
      Example:
      <message from='confctl-g-448@muc.mg.softjoys.com/sa_IgorSampleApp' to='.wrtc_IgorOfficeFF.@mg.softjoys.com/9E52F1E233620811489743824562349' xml:lang='en' id='10e611bf-4c9b-4fc8-afac-6ece673f8d29' type='groupchat' xmlns='jabber:client'>
        <body>Message to the Session</body>
      </message>
    */

    /*
    // TODO: handle case 7.2.16 Room Subject
After the room has optionally sent the discussion history to the new occupant, it SHALL send the current room subject.
This is a <message/> stanza from the room JID (or from the occupant JID of the entity that set the subject),
with a <subject/> element but no <body/> element, as shown in the following example.
If there is no subject set, the room MUST return an empty <subject/> element.

Note: In accordance with the core definition of XML stanzas, any message can contain a <subject/> element;
only a message that contains a <subject/> but no <body/> element shall be considered a subject change for MUC purposes.
    // End of TODO
    */

    var from = message.getAttribute('from'); // 'from' is always present (ensured by Strophe's filter, by which we get into this function)

    var roomNickname = Strophe.getResourceFromJid(from);
    if (!roomNickname) {
      log('Warning: room nickname is absent in ' + Vivox5.Xml.xmlToString(message));
      return true; // don't remove this stanza handler
      // TODO: what to do with this?
      // ??? Server always sends on session beginning after history something like this:
      /*
        <message from="confctl-g-448@muc.mg.softjoys.com" to=".wrtc_e3d9449e.@mg.softjoys.com/E9B935E20D151E7C1489782451766005" type="groupchat" xmlns="jabber:client">
          <subject/>
          <body/>
        </message>
      */
    }

    var jid = undefined;
    var participant = (roomNickname && (roomNickname in participantsByRoomNick)) ? participantsByRoomNick[roomNickname] : undefined;
    if (participant) {
      jid = participant.account.toString();
    }
    else {
      log('Warning: participant not found for room nickname ' + roomNickname);
    }

    var subject = undefined;
    var elsSubject = message.getElementsByTagName('subject');
    if (elsSubject && elsSubject[0] && elsSubject[0].childNodes.length >= 1 && elsSubject[0].childNodes[0].nodeType == 3 /*text*/) {
      body = elsSubject[0].childNodes[0].nodeValue;
    }

    var body = undefined;
    var elsBody = message.getElementsByTagName('body');
    if (elsBody && elsBody[0] && elsBody[0].childNodes.length >= 1 && elsBody[0].childNodes[0].nodeType == 3 /*text*/) {
      body = elsBody[0].childNodes[0].nodeValue;
    }

    var delayTimestamp = undefined;
    var elsDelay = message.getElementsByTagName('delay');
    if (elsDelay && elsDelay[0]) {
      // Message history according to XEP-0045, section 7.2.14 Discussion History
      // Example:
      /*
        <message from="confctl-g-448@muc.mg.softjoys.com/sa_IgorSampleApp" to=".wrtc_2cfc8e5a.@mg.softjoys.com/6123AB592C1F5411489780291996900" xml:lang="en" id="ac81b3a7-4253-4d69-b941-08ebbcbc022b" type="groupchat" xmlns="jabber:client">
          <body>Message to the Session 1234567890abc</body>
          <delay xmlns="urn:xmpp:delay" from=".sa_IgorSampleApp.@mg.softjoys.com/1720DBF44D437B55148967979961508" stamp="2017-03-17T19:51:27Z"/>
        </message>
      */
      var stamp = elsDelay[0].getAttribute('stamp');
      if (stamp) {
        delayTimestamp = Date.parse(stamp);
      }

      var delayFrom = elsDelay[0].getAttribute('from');
      if (delayFrom && !jid) {
        jid = Strophe.getBareJidFromJid(delayFrom);
        //log('Debug: jid taken from delay: ' + jid);
      }
    }

    var language = message.getAttribute('xml:lang');
    if (!language) {
      language = undefined;
    }

    var applicationStanzaNamespace = undefined;
    var applicationStanzaBody = undefined;
    var elsApplication = message.getElementsByTagName('application');
    if (elsApplication && elsApplication[0] && elsApplication[0].childNodes.length >= 1 && elsApplication[0].childNodes[0].nodeType == 3 /*text*/) {
      applicationStanzaNamespace = elsApplication[0].getAttribute('xmlns');
      applicationStanzaBody = elsApplication[0].childNodes[0].nodeValue;
    }

    var logString = (
      'Group message received in ' + logPrefixSession +
      ': from ' + roomNickname + ' (' + (jid ? jid : 'JID unknown') + ')' +
      ', body = ' + (body === undefined ? '<undefined>' : ('\'' + body + '\''))
    );
    if (subject !== undefined) {
      logString += (', subject = ' + '\'' + subject + '\'');
    }
    if (delayTimestamp) {
      logString += (', delayTimestamp = ' + delayTimestamp);
    }
    log(logString);

    if (cbOnChannelTextMessage && typeof cbOnChannelTextMessage === 'function') {
      var sender = jid ? new Vivox5.AccountId(jid) : null;
      var textMessage = new Vivox5.TextMessage(body, subject, sender, participant, roomNickname, delayTimestamp, language, applicationStanzaNamespace, applicationStanzaBody);
      cbOnChannelTextMessage(self, textMessage);
    }

    return true; // don't remove this stanza handler
  }

  // -------------------------------------------------------------------------

  function sendText(text = undefined, subject = undefined) {
    Vivox5.trace();

    var builderMsg = $msg({ to: mucJid, type: 'groupchat', id: Vivox5.Tools.makeUuid() });

    if (text) {
      builderMsg.c('body').t(text).up();
    }

    if (subject) {
      builderMsg.c('subject').t(subject).up();
    }

    var msg = builderMsg.tree();

    // Send the stanza (no response, no timeout):
    stropheConnection.send(msg);
  }

  // -------------------------------------------------------------------------

  function performMuteRequest(participant, reqName, operationToken = undefined) {
    Vivox5.trace();

    var id = Vivox5.Tools.makeUuid();

    var builderIq =
      $iq({ id: id, to: mucJid, type: 'set' })
        .c(reqName, { xmlns: Vivox5.Xmpp.NS.URN_VIVOX_MUC })
        .c('sub', { jid: participant.account.toString() })
      ;

    if (operationToken) {
      builderIq
        .c('password', {})
        .t(operationToken)
        ;
    }

    var iq = builderIq.tree();

    log('Performing ' + reqName + ' operation for ' + participant.account.toString());

    // Send the iq stanza with timeout (the response will be matched by id):
    stropheConnection.sendIQ(
      iq,
      function (iqResp) { return onMuteResponse(iqResp, reqName, participant); },
      function (iqResp) { return onMuteError(iqResp, reqName, participant); },
      Vivox5.Xmpp.DEFAULT_TRANSACTION_TIMEOUT
    );

    // Callback for updated participant
    if (cbOnParticipantUpdated && typeof cbOnParticipantUpdated === 'function') {
      cbOnParticipantUpdated(self, participant);
    }

    return true; // request sent successfully
  }

  // -------------------------------------------------------------------------

  function onMuteResponse(iq, reqName, participant) {
    Vivox5.trace();
    // This is a response to <iq> with mute/unmute/local_mute/local_unmute with type='result' (type checked by Strophe).
    // Ignore it for mute and unmute commands: we cannot have actual state because this operation can be performed without notification by other participants too.
    // For local_mute and local_unmute commands save the mute state in the participant object and call the onParticipantUpdated callback (if defined)

    var bUpdateLocalMuteState = (reqName == 'local_mute' || reqName == 'local_unmute');
    var isSuccess = false;

    var elsMute = iq.getElementsByTagName(reqName);
    if (elsMute && elsMute.length > 0) {
      isSuccess = true;
      log(reqName + ' operation succeeded for ' + participant.account.toString());
    }
    else {
      isSuccess = false;
      log(reqName + ' operation failed for ' + participant.account.toString() + ': no <' + reqName + '> element in response');
    }

    if (bUpdateLocalMuteState) {
      updateLocalMuteStateForParticipant(participant, reqName, isSuccess);
    }
  }

  // -------------------------------------------------------------------------

  function onMuteError(iq, reqName, participant) {
    Vivox5.trace();
    // This is an error response to <iq> with mute/unmute/local_mute/local_unmute.
    // Also this function is called on timeout. In this case iq will be undefined.

    var bUpdateLocalMuteState = (reqName == 'local_mute' || reqName == 'local_unmute');

    if (iq) { // with type='error' (checked by Strophe)
      var sLogPrefix = reqName + ' operation failed for ' + participant.account.toString();
      Vivox5.logXmppError(logSubsystemId, iq, sLogPrefix);
    }
    else { // !iq, i.e. timeout
      log(reqName + ' operation timed out for ' + participant.account.toString());
    }

    if (bUpdateLocalMuteState) {
      updateLocalMuteStateForParticipant(participant, reqName, false);
    }
  }

  // -------------------------------------------------------------------------

  // in: reqName, old state participant.localMute, isSuccess
  // out: new new state participant.localMute or error (inconsistency)
  var localMuteStateTransitions = {
    'local_mute': {
      [true]: { // local_mute operation succeeded
        [Vivox5.ChannelSession.OPERSTATE.OFF]: Vivox5.ChannelSession.OPERSTATE.ON, // strange but ok
        [Vivox5.ChannelSession.OPERSTATE.ON]: Vivox5.ChannelSession.OPERSTATE.ON, // strange but ok
        [Vivox5.ChannelSession.OPERSTATE.ENABLING]: Vivox5.ChannelSession.OPERSTATE.ON, // normal case
        [Vivox5.ChannelSession.OPERSTATE.DISABLING]: Vivox5.ChannelSession.OPERSTATE.ON, // inconsistency: performing unmute, but received response to mute; may be requires special handling?
      },
      [false]: { // local_mute operation failed
        [Vivox5.ChannelSession.OPERSTATE.OFF]: Vivox5.ChannelSession.OPERSTATE.OFF, // strange but ok
        [Vivox5.ChannelSession.OPERSTATE.ON]: Vivox5.ChannelSession.OPERSTATE.ON,  // strange but ok
        [Vivox5.ChannelSession.OPERSTATE.ENABLING]: Vivox5.ChannelSession.OPERSTATE.OFF, // normal case
        [Vivox5.ChannelSession.OPERSTATE.DISABLING]: Vivox5.ChannelSession.OPERSTATE.ON,  // inconsistency: performing unmute, but received error response to mute; may be requires special handling?
      },
    },
    'local_unmute': {
      [true]: { // local_unmute operation succeeded
        [Vivox5.ChannelSession.OPERSTATE.OFF]: Vivox5.ChannelSession.OPERSTATE.OFF, // strange but ok
        [Vivox5.ChannelSession.OPERSTATE.ON]: Vivox5.ChannelSession.OPERSTATE.OFF, // strange but ok
        [Vivox5.ChannelSession.OPERSTATE.ENABLING]: Vivox5.ChannelSession.OPERSTATE.OFF, // inconsistency: performing mute, but received response to unmute; may be requires special handling?
        [Vivox5.ChannelSession.OPERSTATE.DISABLING]: Vivox5.ChannelSession.OPERSTATE.OFF, // normal case
      },
      [false]: { // local_unmute operation failed
        [Vivox5.ChannelSession.OPERSTATE.OFF]: Vivox5.ChannelSession.OPERSTATE.OFF, // strange but ok
        [Vivox5.ChannelSession.OPERSTATE.ON]: Vivox5.ChannelSession.OPERSTATE.ON,  // strange but ok
        [Vivox5.ChannelSession.OPERSTATE.ENABLING]: Vivox5.ChannelSession.OPERSTATE.OFF, // inconsistency: performing mute, but received error response to unmute; may be requires special handling?
        [Vivox5.ChannelSession.OPERSTATE.DISABLING]: Vivox5.ChannelSession.OPERSTATE.ON,  // normal case
      },
    },
  };

  function updateLocalMuteStateForParticipant(participant, reqName, isSuccess) {
    Vivox5.trace();

    var localMuteOld = participant.localMute;
    participant.localMute = localMuteStateTransitions[reqName][isSuccess][localMuteOld];
    //log('localMute updated [' + localMuteOld + ' -> ' + participant.localMute + '] for '  + participant.account.toString());

    // Callback for updated participant
    if (cbOnParticipantUpdated && typeof cbOnParticipantUpdated === 'function') {
      cbOnParticipantUpdated(self, participant);
    }
  }

  // -------------------------------------------------------------------------

  function initiateWsConnectionWithPatchPanel(uri) {
    Vivox5.trace();

    if (wsConnectionPatchPanel) {
      log('Already have a connection with PatchPanel [' + wsUriPatchPanel + '], will not initiate new connection to ' + uri);
      return false;
    }

    log('Initiating WebSocket connection with PatchPanel to ' + uri);

    wsUriPatchPanel = uri;
    wsConnectionPatchPanel = new WebSocket(wsUriPatchPanel);
    wsConnectionPatchPanel.onopen = onWSPPConnected;
    wsConnectionPatchPanel.onclose = onWSPPDisconnected;
    wsConnectionPatchPanel.onerror = onWSPPError;
    wsConnectionPatchPanel.onmessage = onWSPPMessage;

    return true;
  }

  // -------------------------------------------------------------------------

  function releaseChannelSessionResourcesMedia() {
    Vivox5.trace();

    destroyWsConnectionWithPatchPanel();
    destroyRTCPC();
    removeChannelSessionHandlers();
  }

  // -------------------------------------------------------------------------

  function releaseChannelSessionResourcesAll() {
    Vivox5.trace();

    destroyWsConnectionWithPatchPanel();
    destroyRTCPC();
    removeChannelSessionHandlers();
    releaseCallbacks();
    stropheConnection = undefined;
    for (let jid in participants) delete participants[jid];
    participants = undefined;
    for (let nick in participantsByRoomNick) delete participantsByRoomNick[nick];
    participantsByRoomNick = undefined;
  }

  // -------------------------------------------------------------------------

  function releaseCallbacks() {
    Vivox5.trace();

    cbOnSessionEstablished = undefined;
    cbOnSessionTerminated = undefined;
    cbOnParticipantAdded = undefined;
    cbOnParticipantUpdated = undefined;
    cbOnParticipantRemoved = undefined;
    cbOnChannelTextMessage = undefined;
    cbOnAudioStateUpdated = undefined;
    cbOnTextStateUpdated = undefined;
    cbOnRemoteStream = undefined;
  }

  // -------------------------------------------------------------------------

  function destroyWsConnectionWithPatchPanel() {
    if (!wsConnectionPatchPanel) {
      return;
    }

    Vivox5.trace();

    log('Destroying WebSocket connection with PatchPanel');

    isWSPPConnected = false;
    wsUriPatchPanel = undefined;
    wsConnectionPatchPanel.onopen = undefined;
    wsConnectionPatchPanel.onclose = undefined;
    wsConnectionPatchPanel.onerror = undefined;
    wsConnectionPatchPanel.onmessage = undefined;
    wsConnectionPatchPanel.close();
    wsConnectionPatchPanel = undefined;
  }

  // -------------------------------------------------------------------------

  function destroyRTCPC() {
    Vivox5.trace();

    for (let id in arrRTCPC) {
      arrRTCPC[id].onaddstream = undefined;
      arrRTCPC[id].ontrack = undefined;
      arrRTCPC[id].close();
      delete arrRTCPC[id];
    }

    streamLocal = undefined;
  }

  // -------------------------------------------------------------------------

  function onWSPPConnected() {
    Vivox5.trace();
    log('WebSocket connection with PatchPanel established successfully');
    isWSPPConnected = true;
    afterInviteSucceeded(); // may be move this step after msg from PP received?
  }

  // -------------------------------------------------------------------------

  function onWSPPDisconnected(ev) {
    Vivox5.trace();
    if (isWSPPConnected) {
      log('WebSocket connection with PatchPanel closed unexpectedly, code=' + ev.code);
      removeSession();
    }
    else { // if !isWSPPConnected, i.e. during session establishment
      log('Failed to establish WebSocket connection with PatchPanel, code=' + ev.code);
      removeSession(true);
    }
  }

  // -------------------------------------------------------------------------

  function onWSPPError(err) {
    Vivox5.trace();
    log('WebSocket connection error');
    // Do nothing here, handled in onWSPPDisconnected();
  }

  // -------------------------------------------------------------------------

  function onWSPPMessage(msg) {
    Vivox5.trace();

    if (!isWSPPConnected) {
      // should not happen
      return;
    }

    log('Msg from PatchPanel: ' + msg.data);
    //console.log(msg.data);

    var obj = parsePPMessage(msg.data);
    if (obj && obj.addrtcpc) {
      initPeerConnection(obj.addrtcpc);
    }
  }

  // -------------------------------------------------------------------------

  function parsePPMessage(msg, isBase64 = false) {
    Vivox5.trace();

    /*
      Message format:
      <addrtcpc id="ID" session="SESSIONID">
        <addr>IPADDRESS</addr>
        <port>PORT</port>
        <pt>PAYLOADTYPE</pt>
        <iceufrag>ICEUSERNAMEFRAGMENT</iceufrag>
        <icepwd>ICEPASSWORD</icepwd>
        <fingerprint>FINGERPRINT</fingerprint>
        <stream ssrc="SSRC1Decimal" sid="MediaStreamID1" tid="MediaTrackID1" cname="RTCPCName1"/>
        <stream ssrc="SSRC2Decimal" sid="MediaStreamID2" tid="MediaTrackID2" cname="RTCPCName2"/>
      </addrtcpc>
    */

    if (isBase64) {
      try {
        msg = atob(msg);
        console.log('Base64-decoded PP msg:', msg);
      }
      catch (e) {
        log('Failed to decode the message from PP as base64, ignored');
        return null;
      }
    }

    var xml = Vivox5.Xml.parseXml(msg);
    if (!xml) {
      log('Failed to parse the message from PP as XML, ignored');
      return null;
    }

    var obj = Vivox5.Xml.xmlToObj(xml);
    if (!obj) {
      log('Failed to parse XML message from PP as object, ignored');
      return null;
    }

    if ('wrtc' in obj) {
      // remove external container, e.g. <wrtc><addrtcpc>...</addrtcpc><addrtcpc>...</addrtcpc></wrtc>
      obj = obj.wrtc;
    }

    console.log('Parsed XML from PP msg (obj):', obj);
    console.log('Parsed XML from PP msg (JSON): ' + JSON.stringify(obj));

    // addrtcpc command validating, but other messages can appear later

    if (!obj.addrtcpc) {
      log('unknown message (not <addrtcpc>), ignored');
      return null;
    }

    if (!validateMsgAddrtcpc(obj.addrtcpc)) {
      return null;
    }

    return obj;
  }

  // -------------------------------------------------------------------------

  function validateMsgAddrtcpc(addrtcpc) {
    Vivox5.trace();

    if (Array.isArray(addrtcpc)) {
      log(addrtcpc.length + ' <addrtcpc> messages received in one command');
      var result = (addrtcpc.length > 0);
      for (let n in addrtcpc) {
        result = result && validateMsgAddrtcpc(addrtcpc[n]);
        if (!result) {
          break;
        }
      }
      return result;
    }

    log('Validating <addrtcpc> message');

    /*
    addrtcpc is the object with the following parameters:
    "_attrs":{"id":"26","session":"9269809"}
    "addr":"192.168.50.39",
    "port":"50177"
    "pt":"109"
    "iceufrag":"IceServerName"
    "icepwd":"RandomIcePassword22chr335"
    "fingerprint":"sha-256 1C:8C:15:3B:DB:22:8C:EA:DA:60:D0:96:3B:0B:5E:99:22:52:7D:3B:35:90:1E:EB:E0:2D:C8:4A:66:B5:D3:80"
    "self":"1"
    "stream":[
      {"_attrs":{"ssrc":"111"}},
      {"_attrs":{"ssrc":"222"}}
    ]
    OR
    "stream":{"_attrs":{"ssrc":"111"}}}}
    OR
    without "stream"
    */

    // Generate ID if is absent in the command
    if (!addrtcpc._attrs) addrtcpc._attrs = {};
    if (!addrtcpc._attrs.id) {
      var idRandom = 0;
      do {
        idRandom = Vivox5.Tools.getRandomInt(1, 4294967295);
      } while (idRandom in arrRTCPC);
      addrtcpc._attrs.id = idRandom;
    }

    // Default for 'self'
    if (!('self' in addrtcpc)) {
      addrtcpc.self = (Vivox5.Tools.propCount(arrRTCPC) == 0) ? '1' : '0';
      console.log('addrtcpc.self defaults to ' + addrtcpc.self + ' (arrRTCPC has ' + Vivox5.Tools.propCount(arrRTCPC) + ' objects)');
    }

    // Check for mandatory parameters
    var isFound = true;
    var arrMandaParam = ['addr', 'port', 'pt', 'iceufrag', 'icepwd', 'fingerprint', 'self'];
    for (let n = 0; n < arrMandaParam.length; ++n) {
      if (!(arrMandaParam[n] in addrtcpc)) {
        log('Mandatory parameter ' + arrMandaParam[n] + ' not found in the <addrtcpc> message ' + JSON.stringify(addrtcpc));
        isFound = false;
      }
      else if (addrtcpc[arrMandaParam[n]].length == 0) {
        log('Mandatory parameter ' + arrMandaParam[n] + ' is empty in the <addrtcpc> message ' + JSON.stringify(addrtcpc));
        isFound = false;
      }
    }
    if (!isFound) {
      return false;
    }

    // Validate values of numeric parameters
    function _logInvalidValue(paramName, paramValue) {
      log('Parameter ' + paramName + ' has invalid value ' + paramValue + ' in the <addrtcpc> message ' + JSON.stringify(addrtcpc));
    }

    if (!Vivox5.Tools.isValidIpAddress(addrtcpc.addr)) { _logInvalidValue('addr', addrtcpc.addr); return false; }
    if (!Vivox5.Tools.isIntegerInRange(addrtcpc.port, 1, 65535)) { _logInvalidValue('port', addrtcpc.port); return false; }
    if (!Vivox5.Tools.isIntegerInRange(addrtcpc.pt, 0, 127)) { _logInvalidValue('pt', addrtcpc.pt); return false; }

    // Validate SSRC and generate stream attributes if stream(s) exist(s) but without IDs
    var streamsCount = !('stream' in addrtcpc) ? 0 : Array.isArray(addrtcpc.stream) ? addrtcpc.stream.length : 1;
    if (streamsCount > 0) {
      for (let n = 0; n < streamsCount; ++n) {
        var streamAttrs = (streamsCount == 1) ? addrtcpc.stream._attrs : addrtcpc.stream[n]._attrs;
        if (!('ssrc' in streamAttrs)) {
          log('Mandatory attribute ssrc not found in the <stream> element of the <addrtcpc> message ' + JSON.stringify(addrtcpc));
          return false;
        }
        else if (!Vivox5.Tools.isIntegerInRange(streamAttrs.ssrc, 0, 4294967295)) {
          _logInvalidValue('ssrc', streamAttrs.ssrc);
          return false;
        }

        if (!('sid' in streamAttrs)) streamAttrs.sid = 'sid' + streamAttrs.ssrc;
        if (!('tid' in streamAttrs)) streamAttrs.tid = 'tid' + streamAttrs.ssrc;
        if (!('cname' in streamAttrs)) streamAttrs.cname = 'rtcpcname' + streamAttrs.ssrc;
      }
    }

    return true;
  }

  // -------------------------------------------------------------------------

  function initPeerConnection(addrtcpc) {
    Vivox5.trace();

    if (Array.isArray(addrtcpc)) {
      for (let n in addrtcpc) {
        initPeerConnection(addrtcpc[n]);
      }
      return;
    }

    var idRTCPC = addrtcpc._attrs.id;
    log('Initializing RTCPeerConnection[' + idRTCPC + ']');

    var configPeerConnection = { 'iceServers': [] };
    var rtcPeerConnection = new RTCPeerConnection(configPeerConnection);
    arrRTCPC[idRTCPC] = rtcPeerConnection;

    /*
    var onIceCandidate = function onIceCandidate(event) {
      // Do nothing.
      //console.log('onIceCandidate', event);
    };
    */

    var onRemoteStreamInternal = function onRemoteStreamInternal(event) {
      Vivox5.trace();
      log('Adding media stream for RTCPeerConnection[' + idRTCPC + '], sid=' + event.stream.id);
      if (cbOnRemoteStream && typeof cbOnRemoteStream === 'function') {
        cbOnRemoteStream(self, event.stream, idRTCPC);
      }
      else {
        var errorString = 'fatal error: SDK user did not set the callback for onRemoteStream';
        log(errorString);
      }
    };

    //rtcPeerConnection.onicecandidate = onIceCandidate;
    rtcPeerConnection.onaddstream = onRemoteStreamInternal;
    /*
    TODO WRT-51
    rtcPeerConnection.ontrack = function OnRemoteTrack(track) {
      Vivox5.trace();
    };
    */

    if (streamLocal && addrtcpc.self != '0') {
      rtcPeerConnection.addStream(streamLocal);
    }

    var sdpOffer = reconstructServerSdpOffer(addrtcpc);
    processSDPOffer(idRTCPC, sdpOffer);
  }

  // -------------------------------------------------------------------------

  function reconstructServerSdpOffer(addrtcpc) {
    Vivox5.trace();

    var sdpOffer = '';
    var sdpRandomSessId = Vivox5.Tools.getRandomInt(1000000000, 9999999999);
    var iceRandomCandId = Vivox5.Tools.getRandomInt(1000000000, 9999999999);

    var isFirefox = typeof InstallTrigger !== 'undefined'; // Firefox 1.0+
    //var isChrome = !!window.chrome && !!window.chrome.webstore; // Chrome 1+
    var isChrome = !!window.chrome /* && !!window.chrome.webstore */; // Chrome && Chrome-like browsers

    var streamsCount = !('stream' in addrtcpc) ? 0 : Array.isArray(addrtcpc.stream) ? addrtcpc.stream.length : 1;
    var isSimpleSdp = streamsCount <= 1; // || (!isFirefox && !isChrome);
    var streamAttrs;
    var n;

    log('Reconstructing SDP Offer from the server [streamsCount=' + streamsCount + ', isFirefox=' + isFirefox + ', isChrome=' + isChrome + ', isSimpleSdp=' + isSimpleSdp + ']');

    var logSdpType = '';

    if (isSimpleSdp) {
      // Simple SDP offer with one MediaStream
      logSdpType = 'Simple, one stream';

      sdpOffer = 'v=0\r\n'
        + 'o=- ' + sdpRandomSessId + ' 0 IN IP4 ' + addrtcpc.addr + '\r\n'
        + 's=Vivox\r\n'
        + 'c=IN IP4 ' + addrtcpc.addr + '\r\n'
        + 't=0 0\r\n'
        + 'a=ice-lite\r\n'

        + 'm=audio ' + addrtcpc.port + ' UDP/TLS/RTP/SAVPF ' + addrtcpc.pt + '\r\n'
        + 'c=IN IP4 ' + addrtcpc.addr + '\r\n'
        + 'a=rtpmap:' + addrtcpc.pt + ' opus/48000/2\r\n'
        + 'a=fmtp:' + addrtcpc.pt + ' maxplaybackrate=48000\r\n'
        + 'a=setup:actpass\r\n'
        + 'a=' + (addrtcpc.self == "0" ? 'sendonly' : 'sendrecv') + '\r\n'
        + 'a=ice-ufrag:' + addrtcpc.iceufrag + '\r\n'
        + 'a=ice-pwd:' + addrtcpc.icepwd + '\r\n'
        + 'a=fingerprint:' + addrtcpc.fingerprint + '\r\n'
        + 'a=candidate:' + iceRandomCandId + ' 1 udp 2122260223 ' + addrtcpc.addr + ' ' + addrtcpc.port + ' typ host generation 0\r\n'
        + 'a=end-of-candidates\r\n'
        + 'a=rtcp-mux\r\n'
        ;

      if ('stream' in addrtcpc) {
        streamAttrs = addrtcpc.stream._attrs;
        sdpOffer += (
          'a=ssrc:' + streamAttrs.ssrc + ' cname:' + streamAttrs.cname + '\r\n' // required by MS Edge
          + 'a=ssrc:' + streamAttrs.ssrc + ' msid:' + streamAttrs.sid + ' ' + streamAttrs.tid + '\r\n' // like in Plan B (Chrome)
          + 'a=ssrc:' + streamAttrs.ssrc + ' mslabel:' + streamAttrs.sid + '\r\n' // like in Plan B (Chrome)
          + 'a=ssrc:' + streamAttrs.ssrc + ' label:' + streamAttrs.tid + '\r\n' // like in Plan B (Chrome)
          + 'a=msid:' + streamAttrs.sid + ' ' + streamAttrs.tid + '\r\n' // like in Plan Unified (Firefox)
        );
      }
    }
    else {
      if (isFirefox) {
        logSdpType = 'Plan Unified';

        var group = '';
        for (let n = 0; n < streamsCount; ++n) {
          group += ' sdparta_' + n;
        }

        sdpOffer = 'v=0\r\n'
          + 'o=- ' + sdpRandomSessId + ' 0 IN IP4 ' + addrtcpc.addr + '\r\n'
          + 's=Vivox\r\n'
          + 'c=IN IP4 ' + addrtcpc.addr + '\r\n'
          + 't=0 0\r\n'
          + 'a=ice-lite\r\n'
          + 'a=ice-ufrag:' + addrtcpc.iceufrag + '\r\n'
          + 'a=ice-pwd:' + addrtcpc.icepwd + '\r\n'
          + 'a=fingerprint:' + addrtcpc.fingerprint + '\r\n'
          + 'a=msid-semantic:WMS *\r\n'
          + 'a=group:BUNDLE' + group + '\r\n'
          ;

        for (let n = 0; n < streamsCount; ++n) {
          streamAttrs = addrtcpc.stream[n]._attrs;
          sdpOffer += (
            'm=audio ' + (n == 0 ? addrtcpc.port : 9) + ' UDP/TLS/RTP/SAVPF ' + addrtcpc.pt + '\r\n'
            + 'c=IN IP4 ' + (n == 0 ? addrtcpc.addr : '0.0.0.0') + '\r\n'
            + (n == 0 ? '' : 'a=bundle-only\r\n')
            + 'a=rtpmap:' + addrtcpc.pt + ' opus/48000/2\r\n'
            + 'a=fmtp:' + addrtcpc.pt + ' maxplaybackrate=48000\r\n'
            + 'a=setup:actpass\r\n'
            + 'a=' + (addrtcpc.self == "0" ? 'sendonly' : 'sendrecv') + '\r\n'
            + 'a=rtcp-mux\r\n'
            + 'a=mid:sdparta_' + n + '\r\n'
            + 'a=msid:' + streamAttrs.sid + ' ' + streamAttrs.tid + '\r\n'
            + 'a=ssrc:' + streamAttrs.ssrc + ' cname:' + streamAttrs.cname + '\r\n'
          );
          if (n == 0) {
            sdpOffer += (
              'a=candidate:' + iceRandomCandId + ' 1 udp 2122260223 ' + addrtcpc.addr + ' ' + addrtcpc.port + ' typ host generation 0\r\n'
              + 'a=end-of-candidates\r\n'
            );
          }
        }
      } // if isFirefox

      else /* if (isChrome) */ {
        logSdpType = 'Plan B';

        var msidSemantic = '';
        for (let n = 0; n < streamsCount; ++n) {
          msidSemantic += ' ' + addrtcpc.stream[n]._attrs.sid;
        }

        sdpOffer = 'v=0\r\n'
          + 'o=- ' + sdpRandomSessId + ' 0 IN IP4 ' + addrtcpc.addr + '\r\n'
          + 's=Vivox\r\n'
          + 'c=IN IP4 ' + addrtcpc.addr + '\r\n'
          + 't=0 0\r\n'
          + 'a=ice-lite\r\n'
          + 'a=group:BUNDLE audio\r\n'
          + 'a=msid-semantic: WMS' + msidSemantic + '\r\n'

          + 'm=audio ' + addrtcpc.port + ' UDP/TLS/RTP/SAVPF ' + addrtcpc.pt + '\r\n'
          + 'c=IN IP4 ' + addrtcpc.addr + '\r\n'
          + 'a=rtpmap:' + addrtcpc.pt + ' opus/48000/2\r\n'
          + 'a=fmtp:' + addrtcpc.pt + ' maxplaybackrate=48000\r\n'
          + 'a=setup:actpass\r\n'
          + 'a=' + (addrtcpc.self == "0" ? 'sendonly' : 'sendrecv') + '\r\n'
          + 'a=mid:audio\r\n'
          + 'a=ice-ufrag:' + addrtcpc.iceufrag + '\r\n'
          + 'a=ice-pwd:' + addrtcpc.icepwd + '\r\n'
          + 'a=fingerprint:' + addrtcpc.fingerprint + '\r\n'
          + 'a=candidate:' + iceRandomCandId + ' 1 udp 2122260223 ' + addrtcpc.addr + ' ' + addrtcpc.port + ' typ host generation 0\r\n'
          + 'a=end-of-candidates\r\n'
          + 'a=rtcp-mux\r\n'
          ;

        for (let n = 0; n < streamsCount; ++n) {
          streamAttrs = addrtcpc.stream[n]._attrs;
          sdpOffer += (
            'a=ssrc:' + streamAttrs.ssrc + ' cname:' + streamAttrs.cname + '\r\n'
            + 'a=ssrc:' + streamAttrs.ssrc + ' msid:' + streamAttrs.sid + ' ' + streamAttrs.tid + '\r\n'
            + 'a=ssrc:' + streamAttrs.ssrc + ' mslabel:' + streamAttrs.sid + '\r\n'
            + 'a=ssrc:' + streamAttrs.ssrc + ' label:' + streamAttrs.tid + '\r\n'
          );
        }
      } // if isChrome
    }

    log('Reconstructed SDP Offer (' + logSdpType + '):\n' + sdpOffer);

    return sdpOffer;
  }

  // -------------------------------------------------------------------------

  function processSDPOffer(idRTCPC, sdpOffer) {
    Vivox5.trace();
    log('Processing remote SDP Offer');

    var rtcPeerConnection = arrRTCPC[idRTCPC];

    var objOffer = {
      type: 'offer',
      sdp: sdpOffer,
    };

    var rtcSessionDescription = new RTCSessionDescription(objOffer);

    rtcPeerConnection.setRemoteDescription(rtcSessionDescription).then(function () {
      console.log('TRACE: after setRemoteDescription, before createAnswer'); // remove this
      rtcPeerConnection.createAnswer().then(function (description) { onSDPAnswerCreated(idRTCPC, description); }).catch(function (error) { sdpErrorHandler(idRTCPC, error, 'SDP createAnswer'); });
    }).catch(function (error) { sdpErrorHandler(idRTCPC, error, 'SDP setRemoteDescription'); });
  }

  // -------------------------------------------------------------------------

  function onSDPAnswerCreated(idRTCPC, description) {
    Vivox5.trace();
    log('SDP answer created for RTCPC[' + idRTCPC + ']:\n' + description.sdp);

    var rtcPeerConnection = arrRTCPC[idRTCPC];

    rtcPeerConnection.setLocalDescription(description).then(function () {
      console.log('Local SDP answer after setLocalDescription:\n', rtcPeerConnection.localDescription.sdp);
      // Do nothing, don't send local SDP Answer to the PatchPanel
    }).catch(function (error) { sdpErrorHandler(idRTCPC, error, 'SDP setLocalDescription'); });
  }

  // -------------------------------------------------------------------------

  function sdpErrorHandler(idRTCPC, error, sContextDescription) {
    Vivox5.trace();

    var errorFor = 'SDP error for RTCPC[' + idRTCPC + ']';
    var errorMessage = errorFor + ((sContextDescription === undefined) ? ':' : ' (' + sContextDescription + '):');
    log(errorMessage + ' ' + error.toString());
    console.log(errorMessage, error);
    //alert(errorMessage + ' ' + error.toString());
  }

  // -------------------------------------------------------------------------

};

// *****************************************************************************************************************************

// ChannelSession constants

/**
 * Enum of channel types
 * @typedef
 * @property CONF - a typical conferencing channel
 * @property ECHO - a conferencing channel where the user's text and audio is echoed back to that user
 */
Vivox5.ChannelSession.CHANTYPE = Object.create(Object.prototype, {
  ECHO: { value: 'e', writable: false, configurable: false, enumerable: true },
  CONF: { value: 'g', writable: false, configurable: false, enumerable: true },
});

/**
 * Enum of state codes for various asynchronous operations within a Channel Session (for example, localMute state of a {@link Vivox5.Participant})
 * @typedef
 * @property OFF - the operation/feature is in 'disabled' state
 * @property ON - the operation/feature is in 'enabled' state
 * @property ENABLING - the operation/feature is in intermediate ('disabled' to 'enabled') state
 * @property DISABLING - the operation/feature is in intermediate ('enaabled' to 'disabled') state
 * @property NOTSUPPORTED - the operation/feature is not supported for the target object
 */
Vivox5.ChannelSession.OPERSTATE = Object.create(Object.prototype, {
  OFF: { value: 0, writable: false, configurable: false, enumerable: true },
  ON: { value: 1, writable: false, configurable: false, enumerable: true },
  ENABLING: { value: 2, writable: false, configurable: false, enumerable: true },
  DISABLING: { value: 3, writable: false, configurable: false, enumerable: true },
  NOTSUPPORTED: { value: -1, writable: false, configurable: false, enumerable: true },
});

Vivox5.ChannelSession.ACTIONPOSSIBLE = Object.create(Object.prototype, {
  NOTHINGTODO: { value: 0, writable: false, configurable: false, enumerable: true },
  CANSTART: { value: 1, writable: false, configurable: false, enumerable: true },
  NOTPOSSIBLE: { value: -1, writable: false, configurable: false, enumerable: true },
  NOTSUPPORTED: { value: -2, writable: false, configurable: false, enumerable: true },
});

Object.defineProperties(Vivox5.ChannelSession, {
  CHANTYPE: { writable: false, configurable: false, enumerable: true },
  OPERSTATE: { writable: false, configurable: false, enumerable: true },
  ACTIONPOSSIBLE: { writable: false, configurable: false, enumerable: true },
});

// ===========================================================================================

Object.defineProperty(Vivox5, 'ChannelSession', { writable: false, configurable: false, enumerable: true });

// *****************************************************************************************************************************

/**
 * @class
 * @desc Presence information for a user logged in (the current local user or a remote buddy)
 *
 * @param {Vivox5.Presence.STATUS} status - The online status of the user
 * @param {string} [message] - An optional message (can be undefined)
 *
 * @property {Vivox5.Presence.STATUS} status - The online status of the user
 * @property {string} message - An option message published by that user (can be undefined)
 */
Vivox5.Presence = function _Vivox5_Presence(status = Vivox5.Presence.STATUS.UNAVAILABLE, message = undefined) {
  // Check parameters
  if (arguments.length > 2) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, 'Vivox5.Presence()', 'CORE');
  if (typeof status !== 'number' && typeof status !== 'string') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'status', 'number or string with number', 'Vivox5.Presence()', 'CORE');
  if (isNaN(+status)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'status', 'Vivox5.Presence()', 'CORE');
  if (typeof message !== 'string' && message !== undefined) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'message', 'string or undefined', 'Vivox5.Presence()', 'CORE');

  status = +status;
  if (status < Vivox5.Presence.STATUS._MIN || status > Vivox5.Presence.STATUS._MAX) {
    throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMVALUE, 'status', 'Vivox5.Presence()', 'CORE');
  }

  if (!message || message == '') {
    message = undefined;
  }

  // Fill mandatory object properties
  this.status = status;
  this.message = message;

  // Protect object properties from modification and removal
  Object.defineProperties(this, {
    'status': { writable: false, configurable: false, enumerable: true },
    'message': { writable: false, configurable: false, enumerable: true },
  });
};

// ===========================================================================================

Vivox5.Presence.prototype.toString = function _Vivox5_Presence_toString() {
  if (this.message) {
    return Vivox5.Presence.STATUS.DISPLAYNAME[this.status] + ' (' + this.message + ')';
  }
  else {
    return Vivox5.Presence.STATUS.DISPLAYNAME[this.status];
  }
};

// ===========================================================================================

/**
 * Enum of presence status constants
 * @typedef
 * @property UNAVAILABLE - Not available (offline)
 * @property AVAILABLE - Generally available
 * @property CHAT - Available to chat
 * @property DONOTDISTURB - Do Not Disturb
 * @property AWAY - Away
 * @property EXTENDEDAWAY - Away for an extended period of time
 */
Vivox5.Presence.STATUS = Object.create(Object.prototype, {
  _MIN: { writable: false, configurable: false, enumerable: false, value: 1 },
  UNAVAILABLE: { writable: false, configurable: false, enumerable: true, value: 1 },
  AVAILABLE: { writable: false, configurable: false, enumerable: true, value: 2 },
  CHAT: { writable: false, configurable: false, enumerable: true, value: 3 },
  DONOTDISTURB: { writable: false, configurable: false, enumerable: true, value: 4 },
  AWAY: { writable: false, configurable: false, enumerable: true, value: 5 },
  EXTENDEDAWAY: { writable: false, configurable: false, enumerable: true, value: 6 },
  _MAX: { writable: false, configurable: false, enumerable: false, value: 6 },

  DISPLAYNAME: {
    writable: false, configurable: false, enumerable: false, value: {
      1: 'Unavailable',
      2: 'Available',
      3: 'Chat',
      4: 'Do not disturb',
      5: 'Away',
      6: 'Extended away',
    }
  },

  RANK: {
    writable: false, configurable: false, enumerable: false, value: {
      1: 10,
      2: 50,
      3: 60,
      4: 40,
      5: 30,
      6: 20,
    }
  },
});

Object.defineProperty(Vivox5.Presence, 'STATUS', { writable: false, configurable: false, enumerable: true });

// ===========================================================================================

Object.defineProperty(Vivox5, 'Presence', { writable: false, configurable: false, enumerable: true });

// *****************************************************************************************************************************

/**
 * @class
 * @classdesc If the account associated with a presence subscription is logged in, the PresenceLocations list will have an entry for each login session for that user. Usually each subscribed buddy is logged in from only one location, and the application can use the defaultLocation virtual property to get its presence information.
 * @desc The constructor must not be called directly by the application. The application can get objects of this type from a {@link Vivox5.LoginSession} ({@link Vivox5.LoginSession#onBuddyPresence} callback).
 *
 * @property {Vivox5.Presence} defaultLocation - The online status of the subscribed user
 */
Vivox5.PresenceLocations = function _Vivox5_PresenceLocations() {
  // This is map of location -> Presence
  // With one special property defaultLocation (in prototype)
};

// ===========================================================================================

Object.defineProperty(Vivox5.PresenceLocations.prototype, 'defaultLocation', {
  enumberable: false,
  configurable: false,
  get: function _Vivox5_PresenceLocations_defaultLocation_get() {
    // returns presence location with best rank
    var selectedKey = undefined;
    for (let key in this) {
      if (this.hasOwnProperty(key)) {
        if (!selectedKey) {
          selectedKey = key;
        }
        else {
          if (Vivox5.Presence.STATUS.RANK[this[key].status] > Vivox5.Presence.STATUS.RANK[this[selectedKey].status]) {
            selectedKey = key;
          }
        }
      }
    }

    return selectedKey ? this[selectedKey] : undefined;
  },
});

// -------------------------------------------------------------------------

Vivox5.PresenceLocations.prototype.toString = function _Vivox5_PresenceLocations_toString() {
  return '[object Vivox5.PresenceLocations]';
};

// ===========================================================================================

Object.defineProperty(Vivox5, 'PresenceLocations', { writable: false, configurable: false, enumerable: true });

// *****************************************************************************************************************************

/**
 * @class
 * @classdesc A Text Message
 * @desc The constructor must not be called directly by the application. The application can get objects of this type from a {@link Vivox5.LoginSession} or {@link Vivox5.ChannelSession} (see {@link Vivox5.LoginSession#onDirectedTextMessage} and {@link Vivox5.ChannelSession#onChannelTextMessage} callback functions).
 * @readonly
 *
 * @property {string} message - The message
 * @property {string} [subject] - The subject
 * @property {Vivox5.AccountId} [sender] - The account of the sender
 * @property {Vivox5.Participant} [senderParticipant] - The sender as the Participant object (in ChannelSession messages only, absent in directed user-to-user messages, absent in history messages)
 * @property {string} [senderRoomNickname] - The sender's roomNickname in MUC (only in ChannelSession messages), it is always avaliable in ChannelSession messages, even if sender is unknown (e.g. in history messages)
 * @property {number} [receivedTime] - The time that the message was received (only for history messages; if not specified then the current time is implied); number of milliseconds since January 1, 1970, 00:00:00 UTC
 * @property {string} [language] - The language of the Message
 * @property {string} [applicationStanzaNamespace] - An optional name space for application specific data
 * @property {string} [applicationStanzaBody] - Optional application specific data
 */
Vivox5.TextMessage = function _Vivox5_TextMessage(
  message,
  subject,
  sender,
  senderParticipant = undefined,
  senderRoomNickname = undefined,
  receivedTime = undefined,
  language = undefined,
  applicationStanzaNamespace = undefined,
  applicationStanzaBody = undefined
) {
  var className = 'Vivox5.TextMessage'; // used only for error reporting

  // Check parameters
  if (arguments.length < 3 || arguments.length > 9) throw new Vivox5.Error(Vivox5.Error.CODE.ENUMPARAMS, className, 'CORE');
  if (typeof message !== 'string') throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'message', 'string', className, 'CORE');
  if (typeof subject !== 'string' && subject !== undefined) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'subject', 'string or undefined', className, 'CORE');
  if (!(sender instanceof Vivox5.AccountId)) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'sender', 'Vivox5.AccountId', className, 'CORE');
  if (!(senderParticipant instanceof Vivox5.Participant) && senderParticipant !== undefined) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'senderParticipant', 'Vivox5.Participant or undefined', className, 'CORE');
  if (typeof senderRoomNickname !== 'string' && senderRoomNickname !== undefined) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'senderRoomNickname', 'string or undefined', className, 'CORE');
  if (typeof receivedTime != 'number' && receivedTime !== undefined) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'receivedTime', 'number or undefined', className, 'CORE');
  if (typeof language !== 'string' && language !== undefined) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'language', 'string or undefined', className, 'CORE');
  if (typeof applicationStanzaNamespace !== 'string' && applicationStanzaNamespace !== undefined) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'applicationStanzaNamespace', 'string or undefined', className, 'CORE');
  if (typeof applicationStanzaBody !== 'string' && applicationStanzaBody !== undefined) throw new Vivox5.Error(Vivox5.Error.CODE.EPARAMTYPE, 'applicationStanzaBody', 'string or undefined', className, 'CORE');

  // Fill mandatory object properties
  this.message = message;
  this.subject = subject;
  this.sender = sender;
  this.senderParticipant = senderParticipant;
  this.senderRoomNickname = senderRoomNickname;
  this.receivedTime = receivedTime;
  this.language = language;
  this.applicationStanzaNamespace = applicationStanzaNamespace;
  this.applicationStanzaBody = applicationStanzaBody;

  // Protect object properties from modification and removal
  Object.defineProperties(this, {
    message: { writable: false, configurable: false, enumerable: true },
    subject: { writable: false, configurable: false, enumerable: true },
    sender: { writable: false, configurable: false, enumerable: true },
    senderParticipant: { writable: false, configurable: false, enumerable: true },
    senderRoomNickname: { writable: false, configurable: false, enumerable: true },
    receivedTime: { writable: false, configurable: false, enumerable: true },
    language: { writable: false, configurable: false, enumerable: true },
    applicationStanzaNamespace: { writable: false, configurable: false, enumerable: true },
    applicationStanzaBody: { writable: false, configurable: false, enumerable: true },
  });
};

// ===========================================================================================

Vivox5.TextMessage.prototype.toString = function _Vivox5_TextMessage_toString() {
  var str = '';
  if (this.sender && this.sender.toString && typeof this.sender.toString === 'function') {
    str += 'sender: ' + this.sender.toString();
  }

  if (this.message && typeof this.message === 'string' && this.message.length > 0) {
    if (str) str += '; ';
    str += 'message: ' + this.message;
  }

  if (str) {
    return ('[object Vivox5.TextMessage(' + str + ')]');
  }
  else {
    return ('[object Vivox5.TextMessage]');
  }
};

// ===========================================================================================

Object.defineProperty(Vivox5, 'TextMessage', { writable: false, configurable: false, enumerable: true });

// *****************************************************************************************************************************

export default {
  Vivox5
};



// WEBPACK FOOTER //
// ./src/assets/vivox5webrtc.js